import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  CircularProgress,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const TRIAL_PHASE_OPTIONS = [
  'N/A',
  'Early Phase 1',
  'Phase 1',
  'Phase 1/Phase 2',
  'Phase 2',
  'Phase 2/Phase 3',
  'Phase 3',
  'Phase 4',
];

// Trial Scope options (Reference list 13 - Clinical trial scope per Regulation Article 2)
const TRIAL_SCOPE_OPTIONS = [
  'Treatment',
  'Prevention',
  'Diagnosis',
  'Health Services Research',
  'Basic Science',
  'Other',
];

// Age Range options (Reference list 15 - Subject Age Range)
const AGE_RANGE_OPTIONS = [
  'Adults (18-64 years)',
  'Older Adults (65+ years)',
  'Adolescents (12-17 years)',
  'Children (2-11 years)',
  'Infants (1-23 months)',
  'Neonates (0-27 days)',
  'All ages',
];

// IPD Sharing options
const IPD_SHARING_OPTIONS = ['Yes', 'No', 'Undecided'];

// Medicinal product characteristics options (CT05.02, reference list)
const MEDICINAL_PRODUCT_CHARACTERISTICS_OPTIONS = [
  'Narcotic',
  'Psychotropic',
  'Radiopharmaceutical',
  'Immunological',
  'Plasma Derived',
  'Extractive',
  'Recombinant',
  'Containing GMO(s)',
  'Herbal',
  'Homeopathic',
  'Other',
];

// Allocation method options (lookup: Clinical trial allocation method)
const ALLOCATION_METHOD_OPTIONS = [
  'Randomised',
  'Non-randomised',
  'Single arm',
  'Crossover',
  'Factorial',
  'Other',
];

// Blinding method options (lookup: Blinding method)
const BLINDING_METHOD_OPTIONS = [
  'Open-label',
  'Single-blind',
  'Double-blind',
  'Triple-blind',
  'Quadruple-blind',
  'Other',
];

// Roles blinded options (lookup: Clinical Trial Roles blinded)
const ROLES_BLINDED_OPTIONS = [
  'Participant',
  'Care Provider',
  'Investigator',
  'Outcomes Assessor',
  'Data Analyst',
  'Sponsor',
  'Other',
];

// Helper function to parse eligibility criteria string into separate inclusion/exclusion criteria
const parseEligibilityCriteria = (criteriaText, type) => {
  if (!criteriaText || typeof criteriaText !== 'string') return [];
  
  const text = criteriaText.toLowerCase();
  const criteria = [];
  
  // Try to find inclusion or exclusion section
  let sectionText = '';
  if (type === 'inclusion') {
    // Look for inclusion criteria section
    const inclusionMatch = text.match(/(?:inclusion\s+criteria|eligibility\s+criteria)[\s\S]*?(?=exclusion\s+criteria|$)/i);
    if (inclusionMatch) {
      sectionText = inclusionMatch[0];
    } else {
      // If no clear section, try to extract numbered items that seem like inclusion
      sectionText = text;
    }
  } else {
    // Look for exclusion criteria section
    const exclusionMatch = text.match(/exclusion\s+criteria[\s\S]*$/i);
    if (exclusionMatch) {
      sectionText = exclusionMatch[0];
    }
  }
  
  if (!sectionText) return [];
  
  // Split by common patterns (numbered lists, bullets, line breaks)
  const items = sectionText
    .split(/\n|\r|(?=\d+[\.\)]\s)|(?=[•\-\*]\s)/)
    .map(item => item.trim())
    .filter(item => item.length > 10 && !item.match(/^(inclusion|exclusion|eligibility)\s+criteria/i));
  
  // Create criteria objects with IDs
  return items.slice(0, 20).map((item, idx) => ({
    criteria_id: idx + 1,
    criteria_english: item.substring(0, 4000), // Limit to 4000 chars
    criteria_translation: '',
  }));
};

const THERAPEUTIC_AREA_OPTIONS = [
  'Oncology',
  'Cardiology',
  'Neurology',
  'Endocrinology',
  'Gastroenterology',
  'Hematology',
  'Infectious Diseases',
  'Immunology',
  'Nephrology',
  'Ophthalmology',
  'Orthopedics',
  'Pulmonology',
  'Rheumatology',
  'Dermatology',
  'Psychiatry',
  'Urology',
  'Gynecology',
  'Pediatrics',
  'Geriatrics',
  'Emergency Medicine',
  'Anesthesiology',
  'Radiology',
  'Surgery',
  'Other',
];

const EU_LANGUAGES = [
  { value: 'French', label: 'French (Français)' },
  { value: 'German', label: 'German (Deutsch)' },
  { value: 'Spanish', label: 'Spanish (Español)' },
  { value: 'Italian', label: 'Italian (Italiano)' },
  { value: 'Portuguese', label: 'Portuguese (Português)' },
  { value: 'Polish', label: 'Polish (Polski)' },
  { value: 'Dutch', label: 'Dutch (Nederlands)' },
  { value: 'Greek', label: 'Greek (Ελληνικά)' },
  { value: 'Czech', label: 'Czech (Čeština)' },
  { value: 'Romanian', label: 'Romanian (Română)' },
  { value: 'Hungarian', label: 'Hungarian (Magyar)' },
  { value: 'Swedish', label: 'Swedish (Svenska)' },
  { value: 'Bulgarian', label: 'Bulgarian (Български)' },
  { value: 'Croatian', label: 'Croatian (Hrvatski)' },
  { value: 'Slovak', label: 'Slovak (Slovenčina)' },
  { value: 'Finnish', label: 'Finnish (Suomi)' },
  { value: 'Danish', label: 'Danish (Dansk)' },
  { value: 'Lithuanian', label: 'Lithuanian (Lietuvių)' },
  { value: 'Slovenian', label: 'Slovenian (Slovenščina)' },
  { value: 'Estonian', label: 'Estonian (Eesti)' },
  { value: 'Latvian', label: 'Latvian (Latviešu)' },
  { value: 'Maltese', label: 'Maltese (Malti)' },
  { value: 'Irish', label: 'Irish (Gaeilge)' },
];

const EEA_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Iceland',
  'Ireland',
  'Italy',
  'Latvia',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Norway',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
];

const initialForm = {
  full_title_english: '',
  full_title_translation: '',
  public_title_english: '',
  public_title_translation: '',
  protocol_code: '',
  who_utn: '',
  nct_number: '',
  isrctn_number: '',
  additional_registries: [{ registry_name: '', registry_identifier: '' }],
  section_changes_description: '',
  // Trial Category
  low_intervention_trial: false,
  low_intervention_justification: '',
  low_intervention_justification_attachment: '',
  trial_phase: '',
  // Medical Condition
  medical_conditions: [{ condition_english: '', condition_translation: '' }],
  is_rare_disease: false,
  therapeutic_areas: [],
  // MedDRA Information (CT03.02.02.05) - Read-only, prepopulated
  meddra_info: {
    version: '',
    level: '',
    classification_code: '',
    term_name: '',
    system_organ_class: '',
  },
  // Main Objective (CT03.02.03)
  trial_scope: [],
  main_objective_english: '',
  main_objective_translation: '',
  // Secondary Objectives (CT03.02.04) - Repeatable
  secondary_objectives: [],
  // Eligibility Criteria (CT03.02.05)
  // Principal Inclusion Criteria (CT03.02.05.01) - Repeatable
  inclusion_criteria: [],
  // Principal Exclusion Criteria (CT03.02.05.02) - Repeatable
  exclusion_criteria: [],
  // End Points (CT03.02.06)
  primary_endpoints: [],
  secondary_endpoints: [],
  // Trial Duration (CT03.02.07)
  estimated_recruitment_start_date_eea: '',
  estimated_end_of_trial_date_eea: '',
  estimated_global_end_of_trial_date: '',
  // Source of Monetary Support (CT03.02.08)
  sources_of_monetary_support: [],
  // IPD Sharing Statement (CT03.02.11)
  plan_to_share_ipd: '',
  ipd_sharing_plan_description: '',
  // Population of Trial Subjects (CT03.02.09)
  age_range: [],
  age_range_warning: 'Static warning: impact of including paediatric population',
  age_range_secondary_identifier: [],
  gender_male: false,
  gender_female: false,
  clinical_trial_group: [],
  vulnerable_population: false,
  recruitment_population_group: [],
  subjects_incapable_of_consent: [],
  emergency_situation_description: '',
  other_description: '',
  population_section_changes_description: '',
  // Protocol Information (CT03.03)
  // Clinical trial Protocol documents
  ctis_protocol_documents: [],
  ctis_protocol_synopsis_documents: [],
  ctis_dsmc_charter_documents: [],
  ctis_study_design_documents: [],
  study_design_text: '',
  period_details: [],
  arm_details: [],
  // Scientific Advice and Paediatric Investigation Plan (CT03.04)
  scientific_advice_id: '',
  scientific_advice_competent_authorities: '',
  scientific_advice_summary_documents: [],
  scientific_advice_quality_documents: [],
  pip_ema_investigation_number: '',
  pip_opinion_link: '',
  pip_opinion_documents: [],
  scientific_advice_pip_section_changes_description: '',
  // Associated Clinical Trials (CT03.05)
  associated_clinical_trials: [
    {
      eu_ct_number: '',
      eudract_number: '',
      sponsor: '',
      full_title: '',
      agreement_document: '',
    },
  ],
  associated_trials_section_changes_description: '',
  // References (CT03.06)
  online_references: '',
  reference_publication: '',
  reference_publication_link: '',
  references_section_changes_description: '',
  // Countries outside the EEA (CT03.07)
  countries_outside_eea: [],
  planned_subjects_rest_of_world: '',
  countries_section_changes_description: '',
  // Sponsors (CT04) - 1 or many
  sponsors: [
    {
      name: '',
      organisation_type: '',
      address_street: '',
      town_city: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
    },
  ],
  // Legal Representative (CT04.09)
  has_legal_representative: '',
  legal_representative: {
    name: '',
    organisation_type: '',
    address: '',
    town_city: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
  },
  legal_representative_contact: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  },
  // Scientific and Public contact points (CT04.10)
  scientific_contact_points: [
    {
      organisation_name: '',
      functional_name: '',
      phone: '',
      email: '',
    },
  ],
  scientific_same_as_public: '',
  public_contact_points: [
    {
      organisation_name: '',
      functional_name: '',
      email: '',
      phone: '',
    },
  ],
  // Third Party Organisation(s) associated with the trial (CT04.11)
  third_party_organisations: [],
  thirdPartyOrganisationInput: '',
  // Sponsor's contact point for Union (CT04.12)
  union_contact_points: [],
  unionContactPointInput: '',
  // Responsibilities of the sponsors (CT04.13)
  sponsor_responsible_for_compliance: false,
  sponsor_responsible_contact_point: false,
  sponsor_responsible_article_77: false,
  sponsor_responsibilities_section_changes_description: '',
  // Products (CT05) - 1 or many
  products: [
    {
      // PL: Select substance (mandatory)
      selected_product_id: '', // EU Medicinal Product number or other identifier
      // PL01-PL15: Read-only fields from selected product
      eu_medicinal_product_number: '', // PL01
      marketing_authorisation: '', // PL02
      product_authorisation: '', // PL03
      name: '', // PL04
      pharmaceutical_form: '', // PL05
      strength: '', // PL06
      active_substance_name: '', // PL07
      eu_substance_number: '', // PL08
      atc_code: '', // PL09
      atc_name: '', // PL10
      atc_level: '', // PL11
      sponsor_code: '', // PL12
      active_substance_other_descriptive_name: '', // PL13
      type: '', // PL14
      placebo_name: '', // PL15 (optional, shown if role is placebo)
      // CT05.01: Product details
      medicinal_product_other_name: '', // CT05.01.02
      eu_medicinal_product_unique_id: '', // CT05.01.03
      authorisation_status: '', // CT05.01.04
      is_paediatric_formulation: false, // CT05.01.07 (mandatory)
      medicinal_product_role: [], // CT05.01.08 (mandatory, 1 to many: Test, Comparator, Auxiliary, Placebo)
      sponsor_product_code: '', // CT05.01.09 (conditionally required, max 100 chars)
      // CT05.02: Medicinal product characteristics
      medicinal_product_characteristics: [],
      // CT05.03: Dosage and administration details
      route_of_administration: '',
      max_treatment_duration: '',
      max_treatment_duration_unit: '',
      max_daily_dose_allowed: '',
      daily_dose_unit: '',
      max_total_dose_allowed: '',
      total_dose_unit: '',
      // CT05.04: Information about modification of the Medicinal Product
      product_modified_from_marketing_authorisation: false,
      product_modification_description: '',
      // CT05.06: Product authorisation details
      marketing_authorisation_holder: '',
      marketing_authorisation_country: '',
      marketing_authorisation_number: '',
      registration_procedure_number: '',
      // CT05.07: Orphan designation
      has_orphan_drug_designation: false,
      orphan_drug_designation_number: '',
      // CT05.08: Active substance (1 or many)
      active_substances: [
        {
          classification: '',
          authorisation_status: '',
          name: '',
          synonyms: '',
          other_descriptive_name: '',
          eu_active_substance_code: '',
          sponsor_substance_code: '',
          strength: '',
        },
      ],
      // CT05.09: Advanced Therapy Medicinal Product
      advanced_therapy_classification: '',
      cat_reference_number: '',
      // CT05.09.03.01: Gene therapy product description
      gene_therapy: {
        gene_of_interest: '',
        gene_therapy_type: '',
        gene_transfer_product_type: '',
        gene_therapy_additional_description: '',
      },
      // CT05.09.03.02: Genetically modified cells
      genetically_modified_cells: {
        has_genetically_modified_cells: false,
        genetically_modified_cells_origin: '',
        xenogeneic_species_origin: '',
        genetically_modified_cells_type: '',
      },
      // CT05.09.04: Somatic cell therapy
      somatic_cell_therapy: {
        somatic_cell_origin: '',
        somatic_xenogeneic_species_origin: '',
        somatic_cell_type: '',
        somatic_differentiated_cell_type: '',
        somatic_other_cell_type: '',
      },
      // CT05.09.05: Tissue engineered medicinal product (1 or many)
      tissue_engineered_products: [
        {
          tissue_engineered_tissue_origin: '',
          tissue_engineered_xenogeneic_species_origin: '',
          tissue_engineered_cell_type: '',
          tissue_engineered_cell_specification: '',
        },
      ],
      // CT05.10: Device associated with medicinal product
      device_associated: {
        product_used_with_device: false,
        product_ids: [],
        device_type: '',
        device_has_ce_mark: false,
        device_trade_name: '',
        device_notified_body: '',
        device_description: '',
      },
      // CT05.11: Investigator Brochure for the Medicinal Product
      investigator_brochure_documents: [],
      smpc_documents: [],
      // CT05.12: Compliance with (GMP) for the Medicinal Product
      manufacturing_authorisation_documents: [],
      manufacturing_authorisation_number: '',
      qp_gmp_certification_documents: [],
      // CT05.13: IMPD-Q
      impd_q_documents: [],
      simplified_impd_q_documents: [],
      impd_q_justification: '',
      // CT05.14: IMPD-S and E
      impd_s_e_documents: [],
      simplified_impd_s_e_documents: [],
      impd_s_e_justification: '',
      // CT05.15: Auxiliary Medicinal Product Dossier
      ampd_full_documents: [],
      ampd_justification: '',
      // CT05.16: Placebo
      impd_q_placebo_documents: [],
      // CT05.17: Content labelling
      content_labelling_documents: [],
      content_labelling_linked_products: [],
      // CT05.18: Section changes description (at product level)
      product_section_changes_description: '',
    },
  ],
  // Dialogue boxes
  mpd_unavailable: false, // PL16
  remove_product: false, // PL17
  // Part 2: Trial Sites (CT06.01) - Each field is 1 or many (not the whole section), except investigator fields which are single
  trial_sites: {
    organisation_id: [''],
    organisation_name: [''],
    site_location: [''],
    site_street_address: [''],
    site_city: [''],
    site_post_code: [''],
    site_country: [''],
    investigator: {
      title: '',
      first_name: '',
      last_name: '',
      department: '',
      phone: '',
      email: '',
    },
  },
  // Part 2: Documents (CT06.02)
  documents: {
    recruitment_arrangements_documents: [],
    subject_information_consent_documents: [],
    investigator_cv_documents: [],
    investigator_suitability_documents: [],
    facilities_suitability_documents: [],
    proof_of_insurance_documents: [],
    financial_arrangements_documents: [],
    data_protection_compliance_documents: [],
    biological_samples_compliance_documents: [],
  },
};

const EuropeanUnionRegistry = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('French');
  const [translating, setTranslating] = useState({
    fullTitle: false,
    publicTitle: false,
    medicalCondition: false,
    mainObjective: false,
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentFileName, setAttachmentFileName] = useState('');
  const [sponsorAgreementFileNames, setSponsorAgreementFileNames] = useState({});
  const [uploadingProtocolDocs, setUploadingProtocolDocs] = useState({
    protocol: false,
    synopsis: false,
    dsmc: false,
    studyDesign: false,
    sciSummary: false,
    sciQuality: false,
    pipOpinion: false,
  });
  const [uploadingProductDocs, setUploadingProductDocs] = useState({});
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await protocolRegistrationService.getByProject(projectId);
        if (data?.eu_registry) {
          setFormData({
            full_title_english: data.eu_registry.full_title_english || '',
            full_title_translation: data.eu_registry.full_title_translation || '',
            public_title_english: data.eu_registry.public_title_english || '',
            public_title_translation: data.eu_registry.public_title_translation || '',
            protocol_code: data.eu_registry.protocol_code || '',
            who_utn: data.eu_registry.who_utn || '',
            nct_number: data.eu_registry.nct_number || '',
            isrctn_number: data.eu_registry.isrctn_number || '',
            additional_registries: data.eu_registry.additional_registries?.length
              ? data.eu_registry.additional_registries
              : [{ registry_name: '', registry_identifier: '' }],
            section_changes_description: data.eu_registry.section_changes_description || '',
            // Trial Category
            low_intervention_trial: data.eu_registry.low_intervention_trial || false,
            low_intervention_justification: data.eu_registry.low_intervention_justification || '',
            low_intervention_justification_attachment: data.eu_registry.low_intervention_justification_attachment || '',
            trial_phase: data.eu_registry.trial_phase || '',
            // Medical Condition
            medical_conditions: data.eu_registry.medical_conditions?.length
              ? data.eu_registry.medical_conditions
              : [{ condition_english: '', condition_translation: '' }],
            is_rare_disease: data.eu_registry.is_rare_disease || false,
            therapeutic_areas: data.eu_registry.therapeutic_areas || [],
            // MedDRA Information
            meddra_info: data.eu_registry.meddra_info || {
              version: '',
              level: '',
              classification_code: '',
              term_name: '',
              system_organ_class: '',
            },
            // Main Objective
            trial_scope: data.eu_registry.trial_scope || [],
            main_objective_english: data.eu_registry.main_objective_english || '',
            main_objective_translation: data.eu_registry.main_objective_translation || '',
            // Secondary Objectives
            secondary_objectives: data.eu_registry.secondary_objectives || [],
            // Eligibility Criteria
            inclusion_criteria: data.eu_registry.inclusion_criteria || [],
            exclusion_criteria: data.eu_registry.exclusion_criteria || [],
            // End Points
            primary_endpoints: data.eu_registry.primary_endpoints || [],
            secondary_endpoints: data.eu_registry.secondary_endpoints || [],
            // Trial Duration
            estimated_recruitment_start_date_eea: data.eu_registry.estimated_recruitment_start_date_eea
              ? new Date(data.eu_registry.estimated_recruitment_start_date_eea).toISOString().split('T')[0]
              : '',
            estimated_end_of_trial_date_eea: data.eu_registry.estimated_end_of_trial_date_eea
              ? new Date(data.eu_registry.estimated_end_of_trial_date_eea).toISOString().split('T')[0]
              : '',
            estimated_global_end_of_trial_date: data.eu_registry.estimated_global_end_of_trial_date
              ? new Date(data.eu_registry.estimated_global_end_of_trial_date).toISOString().split('T')[0]
              : '',
            // Source of Monetary Support
            sources_of_monetary_support: data.eu_registry.sources_of_monetary_support || [],
            // IPD Sharing Statement
            plan_to_share_ipd: data.eu_registry.plan_to_share_ipd || '',
            ipd_sharing_plan_description: data.eu_registry.ipd_sharing_plan_description || '',
            // Population of Trial Subjects
            age_range: data.eu_registry.age_range || [],
            age_range_warning: data.eu_registry.age_range_warning || 'Static warning: impact of including paediatric population',
            age_range_secondary_identifier: data.eu_registry.age_range_secondary_identifier || [],
            gender_male: data.eu_registry.gender_male || false,
            gender_female: data.eu_registry.gender_female || false,
            clinical_trial_group: data.eu_registry.clinical_trial_group || [],
            vulnerable_population: data.eu_registry.vulnerable_population || false,
            recruitment_population_group: data.eu_registry.recruitment_population_group || [],
            subjects_incapable_of_consent: data.eu_registry.subjects_incapable_of_consent || [],
            emergency_situation_description: data.eu_registry.emergency_situation_description || '',
            other_description: data.eu_registry.other_description || '',
            population_section_changes_description: data.eu_registry.population_section_changes_description || '',
            period_details: data.eu_registry.period_details || [],
            arm_details: data.eu_registry.arm_details || [],
          // Protocol documents (arrays)
          ctis_protocol_documents: data.eu_registry.ctis_protocol_documents || [],
          ctis_study_design_documents: data.eu_registry.ctis_study_design_documents || [],
          ctis_dsmc_charter_documents: data.eu_registry.ctis_dsmc_charter_documents || [],
          // Protocol Information
          study_design_text: data.eu_registry.study_design_text || '',
          period_details: data.eu_registry.period_details || [],
          arm_details: data.eu_registry.arm_details || [],
          // Scientific Advice and Paediatric Investigation Plan
          scientific_advice_id: data.eu_registry.scientific_advice_id || '',
          scientific_advice_competent_authorities:
            data.eu_registry.scientific_advice_competent_authorities || '',
          scientific_advice_summary_documents: data.eu_registry.scientific_advice_summary_documents || [],
          scientific_advice_quality_documents: data.eu_registry.scientific_advice_quality_documents || [],
          pip_ema_investigation_number: data.eu_registry.pip_ema_paediatric_investigation_number || '',
          pip_opinion_link: data.eu_registry.pip_opinion_link || '',
          pip_opinion_documents: data.eu_registry.pip_opinion_documents || [],
          scientific_advice_pip_section_changes_description:
            data.eu_registry.scientific_advice_pip_section_changes_description || '',
          // Associated Clinical Trials (CT03.05)
          associated_clinical_trials:
            data.eu_registry.associated_clinical_trials?.length > 0
              ? data.eu_registry.associated_clinical_trials
              : [
                  {
                    eu_ct_number: '',
                    eudract_number: '',
                    sponsor: '',
                    full_title: '',
                    agreement_document: '',
                  },
                ],
          associated_trials_section_changes_description:
            data.eu_registry.associated_trials_section_changes_description || '',
          // References (CT03.06)
          online_references: data.eu_registry.online_references || '',
          reference_publication: data.eu_registry.reference_publication || '',
          reference_publication_link: data.eu_registry.reference_publication_link || '',
          references_section_changes_description:
            data.eu_registry.references_section_changes_description || '',
          // Countries outside the EEA (CT03.07)
          countries_outside_eea: data.eu_registry.countries_outside_eea || [],
          planned_subjects_rest_of_world: data.eu_registry.planned_subjects_rest_of_world || '',
          countries_section_changes_description: data.eu_registry.countries_section_changes_description || '',
          // Sponsors (CT04)
          sponsors:
            data.eu_registry.sponsors?.length > 0
              ? data.eu_registry.sponsors
              : [
                  {
                    name: '',
                    organisation_type: '',
                    address_street: '',
                    town_city: '',
                    postcode: '',
                    country: '',
                    phone: '',
                    email: '',
                  },
                ],
  // Third party organisation(s) associated with the trial (CT04.11)
  third_party_organisations: [
    {
      organisation_id: '',
      organisation_name: '',
      country: '',
      address_street: '',
      town_city: '',
      postcode: '',
      phone: '',
      email: '',
      duties: [],
    },
  ],
  // Sponsor's contact point for Union (CT04.12)
  union_contact_points: [
    {
      sponsor_organisation_name: '',
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      town_city: '',
      postcode: '',
      country: '',
      functional_contact_name: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
    },
  ],
  // Responsibilities of the sponsors (CT04.13)
  sponsor_responsible_for_compliance: false,
  sponsor_responsible_contact_point: false,
  sponsor_responsible_article_77: false,
  sponsor_responsibilities_section_changes_description: '',
          // Legal Representative (CT04.09)
          has_legal_representative: data.eu_registry.has_legal_representative || '',
          legal_representative: {
            name: data.eu_registry.legal_representative?.name || '',
            organisation_type: data.eu_registry.legal_representative?.organisation_type || '',
            address: data.eu_registry.legal_representative?.address || '',
            town_city: data.eu_registry.legal_representative?.town_city || '',
            postcode: data.eu_registry.legal_representative?.postcode || '',
            country: data.eu_registry.legal_representative?.country || '',
            phone: data.eu_registry.legal_representative?.phone || '',
            email: data.eu_registry.legal_representative?.email || '',
          },
          legal_representative_contact: {
            first_name: data.eu_registry.legal_representative_contact?.first_name || '',
            last_name: data.eu_registry.legal_representative_contact?.last_name || '',
            email: data.eu_registry.legal_representative_contact?.email || '',
            phone: data.eu_registry.legal_representative_contact?.phone || '',
          },
          // Scientific and Public contact points (CT04.10)
          scientific_contact_points:
            data.eu_registry.scientific_contact_points?.length > 0
              ? data.eu_registry.scientific_contact_points
              : [
                  {
                    organisation_name: '',
                    functional_name: '',
                    phone: '',
                    email: '',
                  },
                ],
          scientific_same_as_public: data.eu_registry.scientific_same_as_public || '',
          public_contact_points:
            data.eu_registry.public_contact_points?.length > 0
              ? data.eu_registry.public_contact_points
              : [
                  {
                    organisation_name: '',
                    functional_name: '',
                    email: '',
                    phone: '',
                  },
                ],
          // Third party organisation(s) associated with the trial (CT04.11)
          third_party_organisations:
            data.eu_registry.third_party_organisations?.length > 0
              ? data.eu_registry.third_party_organisations
              : [
                  {
                    organisation_id: '',
                    organisation_name: '',
                    country: '',
                    address_street: '',
                    town_city: '',
                    postcode: '',
                    phone: '',
                    email: '',
                    duties: [],
                  },
                ],
          // Sponsor's contact point for Union (CT04.12)
          union_contact_points:
            data.eu_registry.union_contact_points?.length > 0
              ? data.eu_registry.union_contact_points
              : [
                  {
                    sponsor_organisation_name: '',
                    address1: '',
                    address2: '',
                    address3: '',
                    address4: '',
                    town_city: '',
                    postcode: '',
                    country: '',
                    functional_contact_name: '',
                    first_name: '',
                    last_name: '',
                    phone: '',
                    email: '',
                  },
                ],
          // Responsibilities of the sponsors (CT04.13)
          sponsor_responsible_for_compliance:
            data.eu_registry.sponsor_responsible_for_compliance || false,
          sponsor_responsible_contact_point:
            data.eu_registry.sponsor_responsible_contact_point || false,
          sponsor_responsible_article_77:
            data.eu_registry.sponsor_responsible_article_77 || false,
          sponsor_responsibilities_section_changes_description:
            data.eu_registry.sponsor_responsibilities_section_changes_description || '',
          // Products (CT05)
          products: data.eu_registry.products?.length
            ? data.eu_registry.products.map((p) => ({
                ...p,
                active_substances:
                  p.active_substances && Array.isArray(p.active_substances) && p.active_substances.length > 0
                    ? p.active_substances
                    : [
                        {
                          classification: '',
                          authorisation_status: '',
                          name: '',
                          synonyms: '',
                          other_descriptive_name: '',
                          eu_active_substance_code: '',
                          sponsor_substance_code: '',
                          strength: '',
                        },
                      ],
                gene_therapy: p.gene_therapy || {
                  gene_of_interest: '',
                  gene_therapy_type: '',
                  gene_transfer_product_type: '',
                  gene_therapy_additional_description: '',
                },
                genetically_modified_cells: p.genetically_modified_cells || {
                  has_genetically_modified_cells: false,
                  genetically_modified_cells_origin: '',
                  xenogeneic_species_origin: '',
                  genetically_modified_cells_type: '',
                },
                somatic_cell_therapy: p.somatic_cell_therapy || {
                  somatic_cell_origin: '',
                  somatic_xenogeneic_species_origin: '',
                  somatic_cell_type: '',
                  somatic_differentiated_cell_type: '',
                  somatic_other_cell_type: '',
                },
                tissue_engineered_products:
                  p.tissue_engineered_products && Array.isArray(p.tissue_engineered_products) && p.tissue_engineered_products.length > 0
                    ? p.tissue_engineered_products
                    : [
                        {
                          tissue_engineered_tissue_origin: '',
                          tissue_engineered_xenogeneic_species_origin: '',
                          tissue_engineered_cell_type: '',
                          tissue_engineered_cell_specification: '',
                        },
                      ],
                device_associated: p.device_associated || {
                  product_used_with_device: false,
                  product_ids: [],
                  device_type: '',
                  device_has_ce_mark: false,
                  device_trade_name: '',
                  device_notified_body: '',
                  device_description: '',
                },
                investigator_brochure_documents: p.investigator_brochure_documents || [],
                smpc_documents: p.smpc_documents || [],
                manufacturing_authorisation_documents: p.manufacturing_authorisation_documents || [],
                manufacturing_authorisation_number: p.manufacturing_authorisation_number || '',
                qp_gmp_certification_documents: p.qp_gmp_certification_documents || [],
              }))
            : [
                {
                  selected_product_id: '',
                  eu_medicinal_product_number: '',
                  marketing_authorisation: '',
                  product_authorisation: '',
                  name: '',
                  pharmaceutical_form: '',
                  strength: '',
                  active_substance_name: '',
                  eu_substance_number: '',
                  atc_code: '',
                  atc_name: '',
                  atc_level: '',
                  sponsor_code: '',
                  active_substance_other_descriptive_name: '',
                  type: '',
                  placebo_name: '',
                  medicinal_product_other_name: '',
                  eu_medicinal_product_unique_id: '',
                  authorisation_status: '',
                  is_paediatric_formulation: false,
                  medicinal_product_role: [],
                  sponsor_product_code: '',
                  medicinal_product_characteristics: [],
                  route_of_administration: '',
                  max_treatment_duration: '',
                  max_treatment_duration_unit: '',
                  max_daily_dose_allowed: '',
                  daily_dose_unit: '',
                  max_total_dose_allowed: '',
                  total_dose_unit: '',
                  product_modified_from_marketing_authorisation: false,
                  product_modification_description: '',
                  marketing_authorisation_holder: '',
                  marketing_authorisation_country: '',
                  marketing_authorisation_number: '',
                  registration_procedure_number: '',
                  has_orphan_drug_designation: false,
                  orphan_drug_designation_number: '',
                  active_substances: [
                    {
                      classification: '',
                      authorisation_status: '',
                      name: '',
                      synonyms: '',
                      other_descriptive_name: '',
                      eu_active_substance_code: '',
                      sponsor_substance_code: '',
                      strength: '',
                    },
                  ],
                  advanced_therapy_classification: '',
                  cat_reference_number: '',
                  gene_therapy: {
                    gene_of_interest: '',
                    gene_therapy_type: '',
                    gene_transfer_product_type: '',
                    gene_therapy_additional_description: '',
                  },
                  genetically_modified_cells: {
                    has_genetically_modified_cells: false,
                    genetically_modified_cells_origin: '',
                    xenogeneic_species_origin: '',
                    genetically_modified_cells_type: '',
                  },
                  somatic_cell_therapy: {
                    somatic_cell_origin: '',
                    somatic_xenogeneic_species_origin: '',
                    somatic_cell_type: '',
                    somatic_differentiated_cell_type: '',
                    somatic_other_cell_type: '',
                  },
                  tissue_engineered_products: [
                    {
                      tissue_engineered_tissue_origin: '',
                      tissue_engineered_xenogeneic_species_origin: '',
                      tissue_engineered_cell_type: '',
                      tissue_engineered_cell_specification: '',
                    },
                  ],
                  device_associated: {
                    product_used_with_device: false,
                    product_ids: [],
                    device_type: '',
                    device_has_ce_mark: false,
                    device_trade_name: '',
                    device_notified_body: '',
                    device_description: '',
                  },
                  investigator_brochure_documents: [],
                  smpc_documents: [],
                  manufacturing_authorisation_documents: [],
                  manufacturing_authorisation_number: '',
                  qp_gmp_certification_documents: [],
                },
              ],
          mpd_unavailable: data.eu_registry.mpd_unavailable || false,
          remove_product: data.eu_registry.remove_product || false,
          trial_sites: data.eu_registry.trial_sites
            ? {
                organisation_id: Array.isArray(data.eu_registry.trial_sites.organisation_id) 
                  ? data.eu_registry.trial_sites.organisation_id 
                  : data.eu_registry.trial_sites.organisation_id ? [data.eu_registry.trial_sites.organisation_id] : [''],
                organisation_name: Array.isArray(data.eu_registry.trial_sites.organisation_name) 
                  ? data.eu_registry.trial_sites.organisation_name 
                  : data.eu_registry.trial_sites.organisation_name ? [data.eu_registry.trial_sites.organisation_name] : [''],
                site_location: Array.isArray(data.eu_registry.trial_sites.site_location) 
                  ? data.eu_registry.trial_sites.site_location 
                  : data.eu_registry.trial_sites.site_location ? [data.eu_registry.trial_sites.site_location] : [''],
                site_street_address: Array.isArray(data.eu_registry.trial_sites.site_street_address) 
                  ? data.eu_registry.trial_sites.site_street_address 
                  : data.eu_registry.trial_sites.site_street_address ? [data.eu_registry.trial_sites.site_street_address] : [''],
                site_city: Array.isArray(data.eu_registry.trial_sites.site_city) 
                  ? data.eu_registry.trial_sites.site_city 
                  : data.eu_registry.trial_sites.site_city ? [data.eu_registry.trial_sites.site_city] : [''],
                site_post_code: Array.isArray(data.eu_registry.trial_sites.site_post_code) 
                  ? data.eu_registry.trial_sites.site_post_code 
                  : data.eu_registry.trial_sites.site_post_code ? [data.eu_registry.trial_sites.site_post_code] : [''],
                site_country: Array.isArray(data.eu_registry.trial_sites.site_country) 
                  ? data.eu_registry.trial_sites.site_country 
                  : data.eu_registry.trial_sites.site_country ? [data.eu_registry.trial_sites.site_country] : [''],
                investigator: {
                  title: data.eu_registry.trial_sites.investigator?.title || '',
                  first_name: data.eu_registry.trial_sites.investigator?.first_name || '',
                  last_name: data.eu_registry.trial_sites.investigator?.last_name || '',
                  department: data.eu_registry.trial_sites.investigator?.department || '',
                  phone: data.eu_registry.trial_sites.investigator?.phone || '',
                  email: data.eu_registry.trial_sites.investigator?.email || '',
                },
              }
            : {
                organisation_id: [''],
                organisation_name: [''],
                site_location: [''],
                site_street_address: [''],
                site_city: [''],
                site_post_code: [''],
                site_country: [''],
                investigator: {
                  title: '',
                  first_name: '',
                  last_name: '',
                  department: '',
                  phone: '',
                  email: '',
                },
              },
          documents: data.eu_registry.documents
            ? {
                recruitment_arrangements_documents: data.eu_registry.documents.recruitment_arrangements_documents || [],
                subject_information_consent_documents: data.eu_registry.documents.subject_information_consent_documents || [],
                investigator_cv_documents: data.eu_registry.documents.investigator_cv_documents || [],
                investigator_suitability_documents: data.eu_registry.documents.investigator_suitability_documents || [],
                facilities_suitability_documents: data.eu_registry.documents.facilities_suitability_documents || [],
                proof_of_insurance_documents: data.eu_registry.documents.proof_of_insurance_documents || [],
                financial_arrangements_documents: data.eu_registry.documents.financial_arrangements_documents || [],
                data_protection_compliance_documents: data.eu_registry.documents.data_protection_compliance_documents || [],
                biological_samples_compliance_documents: data.eu_registry.documents.biological_samples_compliance_documents || [],
              }
            : {
                recruitment_arrangements_documents: [],
                subject_information_consent_documents: [],
                investigator_cv_documents: [],
                investigator_suitability_documents: [],
                facilities_suitability_documents: [],
                proof_of_insurance_documents: [],
                financial_arrangements_documents: [],
                data_protection_compliance_documents: [],
                biological_samples_compliance_documents: [],
              },
          });
          // Reset attachment file name when loading (we don't store it in backend)
          setAttachmentFileName('');
        } else {
          setFormData(initialForm);
          setAttachmentFileName('');
        }
      } catch (err) {
        setError(err?.message || 'Failed to load CTIS data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTranslateField = async (field, sourceText) => {
    if (!sourceText || !sourceText.trim()) {
      setError('Please enter English text to translate.');
      return;
    }

    const translatingKey = field === 'full_title_translation' ? 'fullTitle' : 'publicTitle';
    setTranslating((prev) => ({ ...prev, [translatingKey]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(sourceText, selectedLanguage);
      setFormData((prev) => ({
        ...prev,
        [field]: result.translatedText || '',
      }));
    } catch (err) {
      setError(err?.message || 'Failed to translate text');
    } finally {
      setTranslating((prev) => ({ ...prev, [translatingKey]: false }));
    }
  };

  const handleAdditionalRegistryChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.additional_registries];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        additional_registries: next,
      };
    });
  };

  const handleAddAdditionalRegistry = () => {
    setFormData((prev) => ({
      ...prev,
      additional_registries: [...prev.additional_registries, { registry_name: '', registry_identifier: '' }],
    }));
  };

  const handleRemoveAdditionalRegistry = (index) => () => {
    setFormData((prev) => {
      const next = prev.additional_registries.filter((_, i) => i !== index);
      return {
        ...prev,
        additional_registries: next.length ? next : [{ registry_name: '', registry_identifier: '' }],
      };
    });
  };

  const handleMedicalConditionChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.medical_conditions];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        medical_conditions: next,
      };
    });
  };

  const handleTranslateMedicalCondition = async (index, sourceText) => {
    if (!sourceText || !sourceText.trim()) {
      setError('Please enter English text to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, medicalCondition: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(sourceText, selectedLanguage);
      setFormData((prev) => {
        const next = [...prev.medical_conditions];
        next[index] = {
          ...next[index],
          condition_translation: result.translatedText || '',
        };
        return {
          ...prev,
          medical_conditions: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate text');
    } finally {
      setTranslating((prev) => ({ ...prev, medicalCondition: false }));
    }
  };

  const handleAddMedicalCondition = () => {
    setFormData((prev) => ({
      ...prev,
      medical_conditions: [...prev.medical_conditions, { condition_english: '', condition_translation: '' }],
    }));
  };

  const handleRemoveMedicalCondition = (index) => () => {
    setFormData((prev) => {
      const next = prev.medical_conditions.filter((_, i) => i !== index);
      return {
        ...prev,
        medical_conditions: next.length ? next : [{ condition_english: '', condition_translation: '' }],
      };
    });
  };

  const [therapeuticAreaInput, setTherapeuticAreaInput] = useState('');
  const [monetarySupportInput, setMonetarySupportInput] = useState('');
  const [periodDetailsCount, setPeriodDetailsCount] = useState(0);
  const [countriesOutsideInput, setCountriesOutsideInput] = useState('');

  const handleAddTherapeuticArea = () => {
    if (therapeuticAreaInput && !formData.therapeutic_areas.includes(therapeuticAreaInput)) {
      setFormData((prev) => ({
        ...prev,
        therapeutic_areas: [...prev.therapeutic_areas, therapeuticAreaInput],
      }));
      setTherapeuticAreaInput('');
    }
  };

  const handleRemoveTherapeuticArea = (area) => () => {
    setFormData((prev) => ({
      ...prev,
      therapeutic_areas: prev.therapeutic_areas.filter((a) => a !== area),
    }));
  };

  // Main Objective Translation handler (works like public title)
  const handleTranslateMainObjective = async () => {
    if (!formData.main_objective_english || !formData.main_objective_english.trim()) {
      setError('Please enter main objective in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, mainObjective: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        formData.main_objective_english,
        selectedLanguage
      );
      setFormData((prev) => ({
        ...prev,
        main_objective_translation: result.translatedText || '',
      }));
    } catch (err) {
      setError(err?.message || 'Failed to translate main objective');
    } finally {
      setTranslating((prev) => ({ ...prev, mainObjective: false }));
    }
  };

  // Secondary Objective handlers
  const handleAddSecondaryObjective = () => {
    const nextId = (formData.secondary_objectives || []).length > 0
      ? Math.max(...(formData.secondary_objectives || []).map((o) => o.objective_id || 0)) + 1
      : 1;
    setFormData((prev) => ({
      ...prev,
      secondary_objectives: [
        ...prev.secondary_objectives,
        { objective_id: nextId, objective_english: '', objective_translation: '' },
      ],
    }));
  };

  const handleSecondaryObjectiveChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.secondary_objectives];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        secondary_objectives: next,
      };
    });
  };

  const handleRemoveSecondaryObjective = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      secondary_objectives: prev.secondary_objectives.filter((_, i) => i !== index),
    }));
  };

  // Secondary Objective Translation handler (works like public title)
  const handleTranslateSecondaryObjective = async (objIndex) => {
    const objective = formData.secondary_objectives[objIndex];
    if (!objective?.objective_english || !objective.objective_english.trim()) {
      setError('Please enter secondary objective in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, [`secondaryObjective${objIndex}`]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        objective.objective_english,
        selectedLanguage
      );
      setFormData((prev) => {
        const next = [...prev.secondary_objectives];
        next[objIndex] = {
          ...next[objIndex],
          objective_translation: result.translatedText || '',
        };
        return {
          ...prev,
          secondary_objectives: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate secondary objective');
    } finally {
      setTranslating((prev) => ({ ...prev, [`secondaryObjective${objIndex}`]: false }));
    }
  };

  // Inclusion Criteria handlers
  const handleAddInclusionCriteria = () => {
    const nextId = (formData.inclusion_criteria || []).length > 0
      ? Math.max(...(formData.inclusion_criteria || []).map((c) => c.criteria_id || 0)) + 1
      : 1;
    setFormData((prev) => ({
      ...prev,
      inclusion_criteria: [
        ...prev.inclusion_criteria,
        { criteria_id: nextId, criteria_english: '', criteria_translation: '' },
      ],
    }));
  };

  const handleInclusionCriteriaChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.inclusion_criteria];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        inclusion_criteria: next,
      };
    });
  };

  const handleRemoveInclusionCriteria = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      inclusion_criteria: prev.inclusion_criteria.filter((_, i) => i !== index),
    }));
  };

  const handleTranslateInclusionCriteria = async (index) => {
    const criteria = formData.inclusion_criteria[index];
    if (!criteria?.criteria_english || !criteria.criteria_english.trim()) {
      setError('Please enter inclusion criteria in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, [`inclusionCriteria${index}`]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        criteria.criteria_english,
        selectedLanguage
      );
      setFormData((prev) => {
        const next = [...prev.inclusion_criteria];
        next[index] = {
          ...next[index],
          criteria_translation: result.translatedText || '',
        };
        return {
          ...prev,
          inclusion_criteria: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate inclusion criteria');
    } finally {
      setTranslating((prev) => ({ ...prev, [`inclusionCriteria${index}`]: false }));
    }
  };

  // Exclusion Criteria handlers
  const handleAddExclusionCriteria = () => {
    const nextId = (formData.exclusion_criteria || []).length > 0
      ? Math.max(...(formData.exclusion_criteria || []).map((c) => c.criteria_id || 0)) + 1
      : 1;
    setFormData((prev) => ({
      ...prev,
      exclusion_criteria: [
        ...prev.exclusion_criteria,
        { criteria_id: nextId, criteria_english: '', criteria_translation: '' },
      ],
    }));
  };

  const handleExclusionCriteriaChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.exclusion_criteria];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        exclusion_criteria: next,
      };
    });
  };

  const handleRemoveExclusionCriteria = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      exclusion_criteria: prev.exclusion_criteria.filter((_, i) => i !== index),
    }));
  };

  const handleTranslateExclusionCriteria = async (index) => {
    const criteria = formData.exclusion_criteria[index];
    if (!criteria?.criteria_english || !criteria.criteria_english.trim()) {
      setError('Please enter exclusion criteria in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, [`exclusionCriteria${index}`]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        criteria.criteria_english,
        selectedLanguage
      );
      setFormData((prev) => {
        const next = [...prev.exclusion_criteria];
        next[index] = {
          ...next[index],
          criteria_translation: result.translatedText || '',
        };
        return {
          ...prev,
          exclusion_criteria: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate exclusion criteria');
    } finally {
      setTranslating((prev) => ({ ...prev, [`exclusionCriteria${index}`]: false }));
    }
  };

  // Primary Endpoint handlers
  const handleAddPrimaryEndpoint = () => {
    const nextId = (formData.primary_endpoints || []).length > 0
      ? Math.max(...(formData.primary_endpoints || []).map((e) => e.endpoint_id || 0)) + 1
      : 1;
    setFormData((prev) => ({
      ...prev,
      primary_endpoints: [
        ...prev.primary_endpoints,
        { endpoint_id: nextId, endpoint_english: '', endpoint_translation: '' },
      ],
    }));
  };

  const handlePrimaryEndpointChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.primary_endpoints];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        primary_endpoints: next,
      };
    });
  };

  const handleRemovePrimaryEndpoint = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      primary_endpoints: prev.primary_endpoints.filter((_, i) => i !== index),
    }));
  };

  const handleTranslatePrimaryEndpoint = async (index) => {
    const endpoint = formData.primary_endpoints[index];
    if (!endpoint?.endpoint_english || !endpoint.endpoint_english.trim()) {
      setError('Please enter primary endpoint in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, [`primaryEndpoint${index}`]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        endpoint.endpoint_english,
        selectedLanguage
      );
      setFormData((prev) => {
        const next = [...prev.primary_endpoints];
        next[index] = {
          ...next[index],
          endpoint_translation: result.translatedText || '',
        };
        return {
          ...prev,
          primary_endpoints: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate primary endpoint');
    } finally {
      setTranslating((prev) => ({ ...prev, [`primaryEndpoint${index}`]: false }));
    }
  };

  // Secondary Endpoint handlers
  const handleAddSecondaryEndpoint = () => {
    const nextId = (formData.secondary_endpoints || []).length > 0
      ? Math.max(...(formData.secondary_endpoints || []).map((e) => e.endpoint_id || 0)) + 1
      : 1;
    setFormData((prev) => ({
      ...prev,
      secondary_endpoints: [
        ...prev.secondary_endpoints,
        { endpoint_id: nextId, endpoint_english: '', endpoint_translation: '' },
      ],
    }));
  };

  const handleSecondaryEndpointChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.secondary_endpoints];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        secondary_endpoints: next,
      };
    });
  };

  const handleRemoveSecondaryEndpoint = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      secondary_endpoints: prev.secondary_endpoints.filter((_, i) => i !== index),
    }));
  };

  const handleTranslateSecondaryEndpoint = async (index) => {
    const endpoint = formData.secondary_endpoints[index];
    if (!endpoint?.endpoint_english || !endpoint.endpoint_english.trim()) {
      setError('Please enter secondary endpoint in English to translate.');
      return;
    }

    setTranslating((prev) => ({ ...prev, [`secondaryEndpoint${index}`]: true }));
    setError(null);

    try {
      const result = await protocolRegistrationService.translateToEU(
        endpoint.endpoint_english,
        selectedLanguage
      );
      setFormData((prev) => {
        const next = [...prev.secondary_endpoints];
        next[index] = {
          ...next[index],
          endpoint_translation: result.translatedText || '',
        };
        return {
          ...prev,
          secondary_endpoints: next,
        };
      });
    } catch (err) {
      setError(err?.message || 'Failed to translate secondary endpoint');
    } finally {
      setTranslating((prev) => ({ ...prev, [`secondaryEndpoint${index}`]: false }));
    }
  };

  // Source of Monetary Support handlers
  const handleAddMonetarySupport = () => {
    if (monetarySupportInput && !formData.sources_of_monetary_support.includes(monetarySupportInput)) {
      setFormData((prev) => ({
        ...prev,
        sources_of_monetary_support: [...prev.sources_of_monetary_support, monetarySupportInput],
      }));
      setMonetarySupportInput('');
    }
  };

  const handleRemoveMonetarySupport = (source) => () => {
    setFormData((prev) => ({
      ...prev,
      sources_of_monetary_support: prev.sources_of_monetary_support.filter((s) => s !== source),
    }));
  };

  // Period Details handlers
  const handleAddPeriod = () => {
    const nextNumber =
      (formData.period_details || []).length > 0
        ? Math.max(...(formData.period_details || []).map((p) => p.period_number || 0)) + 1
        : 1;
    setFormData((prev) => ({
      ...prev,
      period_details: [
        ...prev.period_details,
        {
          period_number: nextNumber,
          title: '',
          description: '',
          allocation_method: '',
          blinding_used: '',
          roles_blinded: '',
          blinding_implementation_details: '',
        },
      ],
    }));
    setPeriodDetailsCount((c) => c + 1);
  };

  const handlePeriodChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.period_details];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        period_details: next,
      };
    });
  };

  const handleRemovePeriod = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      period_details: prev.period_details.filter((_, i) => i !== index),
    }));
  };

  // Arm Details handlers
  const handleAddArm = () => {
    setFormData((prev) => ({
      ...prev,
      arm_details: [
        ...prev.arm_details,
        { arm_title: '', arm_description: '', arm_section_changes_description: '' },
      ],
    }));
  };

  const handleArmChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.arm_details];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        arm_details: next,
      };
    });
  };

  const handleRemoveArm = (index) => () => {
    setFormData((prev) => ({
      ...prev,
      arm_details: prev.arm_details.filter((_, i) => i !== index),
    }));
  };

  // Associated Clinical Trials handlers (CT03.05)
  const handleAddAssociatedTrial = () => {
    setFormData((prev) => ({
      ...prev,
      associated_clinical_trials: [
        ...prev.associated_clinical_trials,
        {
          eu_ct_number: '',
          eudract_number: '',
          sponsor: '',
          full_title: '',
          agreement_document: '',
        },
      ],
    }));
  };

  const handleAssociatedTrialChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.associated_clinical_trials];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        associated_clinical_trials: next,
      };
    });
  };

  const handleRemoveAssociatedTrial = (index) => () => {
    setFormData((prev) => {
      const next = prev.associated_clinical_trials.filter((_, i) => i !== index);
      return {
        ...prev,
        associated_clinical_trials:
          next.length > 0
            ? next
            : [
                {
                  eu_ct_number: '',
                  eudract_number: '',
                  sponsor: '',
                  full_title: '',
                  agreement_document: '',
                },
              ],
      };
    });
    setSponsorAgreementFileNames((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleSponsorAgreementUpload = (index) => async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setUploadingAttachment(true);
    setError(null);
    try {
      const result = await protocolRegistrationService.uploadCtisAttachment(file);
      setFormData((prev) => {
        const next = [...prev.associated_clinical_trials];
        const trial =
          next[index] || {
            eu_ct_number: '',
            eudract_number: '',
            sponsor: '',
            full_title: '',
            agreement_document: '',
          };
        next[index] = {
          ...trial,
          agreement_document: result.url || trial.agreement_document,
        };
        return {
          ...prev,
          associated_clinical_trials: next,
        };
      });
      setSponsorAgreementFileNames((prev) => ({
        ...prev,
        [index]: result.originalName || file.name,
      }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to upload sponsor agreement');
    } finally {
      setUploadingAttachment(false);
      event.target.value = '';
    }
  };

  // Countries outside the EEA handlers (CT03.07)
  const handleAddCountryOutside = () => {
    if (countriesOutsideInput && !formData.countries_outside_eea.includes(countriesOutsideInput)) {
      setFormData((prev) => ({
        ...prev,
        countries_outside_eea: [...prev.countries_outside_eea, countriesOutsideInput],
      }));
      setCountriesOutsideInput('');
    }
  };

  const handleRemoveCountryOutside = (country) => () => {
    setFormData((prev) => ({
      ...prev,
      countries_outside_eea: prev.countries_outside_eea.filter((c) => c !== country),
    }));
  };

  // Sponsors (CT04) handlers
  const handleAddSponsor = () => {
    setFormData((prev) => ({
      ...prev,
      sponsors: [
        ...prev.sponsors,
        {
          name: '',
          organisation_type: '',
          address_street: '',
          town_city: '',
          postcode: '',
          country: '',
          phone: '',
          email: '',
        },
      ],
    }));
  };

  const handleSponsorChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...(prev.sponsors || [])];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        sponsors: next,
      };
    });
  };

  const handleRemoveSponsor = (index) => () => {
    setFormData((prev) => {
      const next = (prev.sponsors || []).filter((_, i) => i !== index);
      return {
        ...prev,
        sponsors:
          next.length > 0
            ? next
            : [
                {
                  name: '',
                  organisation_type: '',
                  address_street: '',
                  town_city: '',
                  postcode: '',
                  country: '',
                  phone: '',
                  email: '',
                },
              ],
      };
    });
  };

  // Scientific/Public contact point handlers
  const handleAddScientificContact = () => {
    setFormData((prev) => ({
      ...prev,
      scientific_contact_points: [
        ...prev.scientific_contact_points,
        { organisation_name: '', functional_name: '', phone: '', email: '' },
      ],
    }));
  };

  const handleScientificContactChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.scientific_contact_points];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        scientific_contact_points: next,
      };
    });
  };

  const handleRemoveScientificContact = (index) => () => {
    setFormData((prev) => {
      const next = prev.scientific_contact_points.filter((_, i) => i !== index);
      return {
        ...prev,
        scientific_contact_points:
          next.length > 0 ? next : [{ organisation_name: '', functional_name: '', phone: '', email: '' }],
      };
    });
  };

  const handleAddPublicContact = () => {
    setFormData((prev) => ({
      ...prev,
      public_contact_points: [
        ...prev.public_contact_points,
        { organisation_name: '', functional_name: '', phone: '', email: '' },
      ],
    }));
  };

  const handlePublicContactChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.public_contact_points];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        public_contact_points: next,
      };
    });
  };

  const handleRemovePublicContact = (index) => () => {
    setFormData((prev) => {
      const next = prev.public_contact_points.filter((_, i) => i !== index);
      return {
        ...prev,
        public_contact_points:
          next.length > 0 ? next : [{ organisation_name: '', functional_name: '', phone: '', email: '' }],
      };
    });
  };

  // Third party organisation handlers (CT04.11)
  const handleAddThirdPartyOrg = () => {
    setFormData((prev) => ({
      ...prev,
      third_party_organisations: [
        ...prev.third_party_organisations,
        {
          organisation_id: '',
          organisation_name: '',
          country: '',
          address_street: '',
          town_city: '',
          postcode: '',
          phone: '',
          email: '',
          duties: [],
        },
      ],
    }));
  };

  const handleThirdPartyOrgChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.third_party_organisations];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        third_party_organisations: next,
      };
    });
  };

  const handleThirdPartyOrgDutiesChange = (index) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.third_party_organisations];
      next[index] = {
        ...next[index],
        duties: value,
      };
      return {
        ...prev,
        third_party_organisations: next,
      };
    });
  };

  const handleRemoveThirdPartyOrg = (index) => () => {
    setFormData((prev) => {
      const next = prev.third_party_organisations.filter((_, i) => i !== index);
      return {
        ...prev,
        third_party_organisations:
          next.length > 0
            ? next
            : [
                {
                  organisation_id: '',
                  organisation_name: '',
                  country: '',
                  address_street: '',
                  town_city: '',
                  postcode: '',
                  phone: '',
                  email: '',
                  duties: [],
                },
              ],
      };
    });
  };

  // Union contact point handlers (CT04.12)
  const handleAddUnionContactPoint = () => {
    setFormData((prev) => ({
      ...prev,
      union_contact_points: [
        ...prev.union_contact_points,
        {
          sponsor_organisation_name: '',
          address1: '',
          address2: '',
          address3: '',
          address4: '',
          town_city: '',
          postcode: '',
          country: '',
          functional_contact_name: '',
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
        },
      ],
    }));
  };

  const handleUnionContactPointChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const next = [...prev.union_contact_points];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        union_contact_points: next,
      };
    });
  };

  const handleRemoveUnionContactPoint = (index) => () => {
    setFormData((prev) => {
      const next = prev.union_contact_points.filter((_, i) => i !== index);
      return {
        ...prev,
        union_contact_points:
          next.length > 0
            ? next
            : [
                {
                  sponsor_organisation_name: '',
                  address1: '',
                  address2: '',
                  address3: '',
                  address4: '',
                  town_city: '',
                  postcode: '',
                  country: '',
                  functional_contact_name: '',
                  first_name: '',
                  last_name: '',
                  phone: '',
                  email: '',
                },
              ],
      };
    });
  };

  // Product handlers (CT05)
  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          selected_product_id: '',
          eu_medicinal_product_number: '',
          marketing_authorisation: '',
          product_authorisation: '',
          name: '',
          pharmaceutical_form: '',
          strength: '',
          active_substance_name: '',
          eu_substance_number: '',
          atc_code: '',
          atc_name: '',
          atc_level: '',
          sponsor_code: '',
          active_substance_other_descriptive_name: '',
          type: '',
          placebo_name: '',
          medicinal_product_other_name: '',
          eu_medicinal_product_unique_id: '',
          authorisation_status: '',
          is_paediatric_formulation: false,
          medicinal_product_role: [],
          sponsor_product_code: '',
          medicinal_product_characteristics: [],
          route_of_administration: '',
          max_treatment_duration: '',
          max_treatment_duration_unit: '',
          max_daily_dose_allowed: '',
          daily_dose_unit: '',
          max_total_dose_allowed: '',
          total_dose_unit: '',
          product_modified_from_marketing_authorisation: false,
          product_modification_description: '',
          marketing_authorisation_holder: '',
          marketing_authorisation_country: '',
          marketing_authorisation_number: '',
          registration_procedure_number: '',
          has_orphan_drug_designation: false,
          orphan_drug_designation_number: '',
          active_substances: [
            {
              classification: '',
              authorisation_status: '',
              name: '',
              synonyms: '',
              other_descriptive_name: '',
              eu_active_substance_code: '',
              sponsor_substance_code: '',
              strength: '',
            },
          ],
          advanced_therapy_classification: '',
          cat_reference_number: '',
          gene_therapy: {
            gene_of_interest: '',
            gene_therapy_type: '',
            gene_transfer_product_type: '',
            gene_therapy_additional_description: '',
          },
          genetically_modified_cells: {
            has_genetically_modified_cells: false,
            genetically_modified_cells_origin: '',
            xenogeneic_species_origin: '',
            genetically_modified_cells_type: '',
          },
          somatic_cell_therapy: {
            somatic_cell_origin: '',
            somatic_xenogeneic_species_origin: '',
            somatic_cell_type: '',
            somatic_differentiated_cell_type: '',
            somatic_other_cell_type: '',
          },
          tissue_engineered_products: [
            {
              tissue_engineered_tissue_origin: '',
              tissue_engineered_xenogeneic_species_origin: '',
              tissue_engineered_cell_type: '',
              tissue_engineered_cell_specification: '',
            },
          ],
          device_associated: {
            product_used_with_device: false,
            product_ids: [],
            device_type: '',
            device_has_ce_mark: false,
            device_trade_name: '',
            device_notified_body: '',
            device_description: '',
          },
          investigator_brochure_documents: [],
          smpc_documents: [],
          manufacturing_authorisation_documents: [],
          manufacturing_authorisation_number: '',
          qp_gmp_certification_documents: [],
          impd_q_documents: [],
          simplified_impd_q_documents: [],
          impd_q_justification: '',
          impd_s_e_documents: [],
          simplified_impd_s_e_documents: [],
          impd_s_e_justification: '',
          ampd_full_documents: [],
          ampd_justification: '',
          impd_q_placebo_documents: [],
          content_labelling_documents: [],
          content_labelling_linked_products: [],
          product_section_changes_description: '',
        },
      ],
    }));
  };

  const handleProductChange = (index, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => {
      const next = [...prev.products];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        products: next,
      };
    });
  };

  // Active substance handlers (CT05.08)
  const handleAddActiveSubstance = (productIndex) => () => {
    setFormData((prev) => {
      const products = [...prev.products];
      const current = products[productIndex] || {};
      const currentList = Array.isArray(current.active_substances)
        ? current.active_substances
        : [];
      const nextList = [
        ...currentList,
        {
          classification: '',
          authorisation_status: '',
          name: '',
          synonyms: '',
          other_descriptive_name: '',
          eu_active_substance_code: '',
          sponsor_substance_code: '',
          strength: '',
        },
      ];
      products[productIndex] = {
        ...current,
        active_substances: nextList,
      };
      return {
        ...prev,
        products,
      };
    });
  };

  const handleActiveSubstanceChange = (productIndex, subIndex, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const products = [...prev.products];
      const product = products[productIndex] || {};
      const list = Array.isArray(product.active_substances)
        ? [...product.active_substances]
        : [];
      const item = list[subIndex] || {
        classification: '',
        authorisation_status: '',
        name: '',
        synonyms: '',
        other_descriptive_name: '',
        eu_active_substance_code: '',
        sponsor_substance_code: '',
        strength: '',
      };
      list[subIndex] = {
        ...item,
        [field]: value,
      };
      products[productIndex] = {
        ...product,
        active_substances: list,
      };
      return {
        ...prev,
        products,
      };
    });
  };

  const handleRemoveActiveSubstance = (productIndex, subIndex) => () => {
    setFormData((prev) => {
      const products = [...prev.products];
      const product = products[productIndex] || {};
      const list = Array.isArray(product.active_substances)
        ? product.active_substances.filter((_, i) => i !== subIndex)
        : [];
      products[productIndex] = {
      ...product,
        active_substances:
          list.length > 0
            ? list
            : [
                {
                  classification: '',
                  authorisation_status: '',
                  name: '',
                  synonyms: '',
                  other_descriptive_name: '',
                  eu_active_substance_code: '',
                  sponsor_substance_code: '',
                  strength: '',
                },
              ],
      };
      return {
        ...prev,
        products,
      };
    });
  };

  const handleProductArrayChange = (index, field) => (event) => {
    // MUI multiple Select passes the full array in event.target.value
    let { value } = event.target;

    // Normalise to array of strings
    if (!Array.isArray(value)) {
      if (typeof value === 'string') {
        value = value.split(',').map((v) => v.trim()).filter(Boolean);
      } else if (value == null) {
        value = [];
      } else {
        value = [value];
      }
    }

    setFormData((prev) => {
      const next = [...prev.products];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return {
        ...prev,
        products: next,
      };
    });
  };

  const handleRemoveProduct = (index) => () => {
    setFormData((prev) => {
      const next = prev.products.filter((_, i) => i !== index);
      return {
        ...prev,
        products:
          next.length > 0
            ? next
            : [
                {
                  selected_product_id: '',
                  eu_medicinal_product_number: '',
                  marketing_authorisation: '',
                  product_authorisation: '',
                  name: '',
                  pharmaceutical_form: [],
                  strength: [],
                  active_substance_name: [],
                  eu_substance_number: '',
                  atc_code: [],
                  atc_name: '',
                  atc_level: '',
                  sponsor_code: [],
                  active_substance_other_descriptive_name: [],
                  type: '',
                  placebo_name: '',
                  medicinal_product_other_name: [],
                  eu_medicinal_product_unique_id: '',
                  authorisation_status: '',
                  is_paediatric_formulation: false,
                  medicinal_product_role: [],
                  sponsor_product_code: '',
                },
              ],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!projectId) {
      setError('Please select a project before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Front-end validation for conditionally required fields
      const validationErrors = [];

      // Age range secondary identifier required when age range is provided
      if (
        (formData.age_range && formData.age_range.length > 0) &&
        (!formData.age_range_secondary_identifier || formData.age_range_secondary_identifier.length === 0)
      ) {
        validationErrors.push(
          'Subject age range secondary identifier is required when age range is specified.',
        );
      }

      // Vulnerable population related fields
      if (formData.vulnerable_population) {
        if (!formData.recruitment_population_group || formData.recruitment_population_group.length === 0) {
          validationErrors.push(
            'Recruitment population group is required when vulnerable population is selected.',
          );
        }
        if (!formData.subjects_incapable_of_consent || formData.subjects_incapable_of_consent.length === 0) {
          validationErrors.push(
            'Subjects incapable of giving consent personally must be specified when vulnerable population is selected.',
          );
        }
        if (!formData.emergency_situation_description?.trim()) {
          validationErrors.push(
            'Emergency situation description is required when vulnerable population is selected.',
          );
        }
        if (!formData.other_description?.trim()) {
          validationErrors.push(
            'Other description is required when vulnerable population is selected.',
          );
        }
      }

      // Associated clinical trials: if any trial has data, sponsor must be provided
      const associatedTrials = formData.associated_clinical_trials || [];
      const meaningfulAssociatedTrials = associatedTrials.filter((trial) =>
        (trial.eu_ct_number && trial.eu_ct_number.trim()) ||
        (trial.eudract_number && trial.eudract_number.trim()) ||
        (trial.sponsor && trial.sponsor.trim()) ||
        (trial.full_title && trial.full_title.trim()) ||
        (trial.agreement_document && trial.agreement_document.trim()),
      );

      meaningfulAssociatedTrials.forEach((trial, idx) => {
        if (!trial.sponsor || !trial.sponsor.trim()) {
          validationErrors.push(
            `Sponsor for associated clinical trial #${idx + 1} is required when other associated trial details are provided.`,
          );
        }
      });

      if (
        meaningfulAssociatedTrials.length > 0 &&
        !formData.associated_trials_section_changes_description?.trim()
      ) {
        validationErrors.push(
          'Section changes description for Associated clinical trials is required when any associated clinical trial is provided.',
        );
      }

      // Countries outside EEA: if any country is listed, planned subjects and global end of trial date are required
      const hasCountriesOutside = formData.countries_outside_eea && formData.countries_outside_eea.length > 0;
      if (hasCountriesOutside) {
        if (
          formData.planned_subjects_rest_of_world === '' ||
          formData.planned_subjects_rest_of_world === null ||
          formData.planned_subjects_rest_of_world === undefined
        ) {
          validationErrors.push(
            'Planned number of subjects to be enrolled in the Rest of the world is required when countries outside the EEA are specified.',
          );
        }
        if (!formData.estimated_global_end_of_trial_date) {
          validationErrors.push(
            'Estimated global end of trial date is required when countries outside the EEA are specified.',
          );
        }
      }

      // Sponsors (CT04): at least one sponsor with name; for any sponsor with other data, name is required
      const sponsors = formData.sponsors || [];
      const meaningfulSponsors = sponsors.filter((s) =>
        (s.name && s.name.trim()) ||
        (s.organisation_type && s.organisation_type.trim()) ||
        (s.address_street && s.address_street.trim()) ||
        (s.town_city && s.town_city.trim()) ||
        (s.postcode && s.postcode.trim()) ||
        (s.country && s.country.trim()) ||
        (s.phone && s.phone.trim()) ||
        (s.email && s.email.trim()),
      );

      if (meaningfulSponsors.length === 0) {
        validationErrors.push('At least one sponsor with Name is required in the Sponsors section.');
      }

      meaningfulSponsors.forEach((s, idx) => {
        if (!s.name || !s.name.trim()) {
          validationErrors.push(`Sponsor Name is required for sponsor #${idx + 1}.`);
        }
      });

      // Legal Representative (CT04.09): if "Yes", require organisation name and contact first/last
      if (formData.has_legal_representative === 'Yes') {
        if (!formData.legal_representative?.name?.trim()) {
          validationErrors.push('Legal representative organisational name is required when Legal Representative = Yes.');
        }
        if (!formData.legal_representative_contact?.first_name?.trim()) {
          validationErrors.push('Legal representative contact first name is required when Legal Representative = Yes.');
        }
        if (!formData.legal_representative_contact?.last_name?.trim()) {
          validationErrors.push('Legal representative contact last name is required when Legal Representative = Yes.');
        }
      }

      // Scientific contact point: at least one with organisation and functional name
      const sciContacts = formData.scientific_contact_points || [];
      const sciWithNames = sciContacts.filter(
        (c) => c.organisation_name?.trim() || c.functional_name?.trim(),
      );
      if (sciWithNames.length === 0) {
        validationErrors.push('At least one Scientific contact point with organisation and functional name is required.');
      } else {
        sciWithNames.forEach((c, idx) => {
          if (!c.organisation_name?.trim()) {
            validationErrors.push(`Scientific contact point #${idx + 1}: organisation name is required.`);
          }
          if (!c.functional_name?.trim()) {
            validationErrors.push(`Scientific contact point #${idx + 1}: functional contact point name is required.`);
          }
        });
      }

      // Public contact point: at least one with organisation and functional name (if not same as scientific)
      if (formData.scientific_same_as_public !== 'Yes') {
        const pubContacts = formData.public_contact_points || [];
        const pubWithNames = pubContacts.filter(
          (c) => c.organisation_name?.trim() || c.functional_name?.trim(),
        );
        if (pubWithNames.length === 0) {
          validationErrors.push('At least one Public contact point with organisation and functional name is required.');
        } else {
          pubWithNames.forEach((c, idx) => {
            if (!c.organisation_name?.trim()) {
              validationErrors.push(`Public contact point #${idx + 1}: organisation name is required.`);
            }
            if (!c.functional_name?.trim()) {
              validationErrors.push(`Public contact point #${idx + 1}: functional contact point name is required.`);
            }
          });
        }
      }

      // Third party organisations: if any row has data, require organisation name and at least one duty
      const tpoList = formData.third_party_organisations || [];
      const tpoWithData = tpoList.filter(
        (org) =>
          org.organisation_name?.trim() ||
          org.country?.trim() ||
          org.address_street?.trim() ||
          org.town_city?.trim() ||
          org.postcode?.trim() ||
          org.phone?.trim() ||
          org.email?.trim() ||
          (org.duties && org.duties.length > 0),
      );
      tpoWithData.forEach((org, idx) => {
        if (!org.organisation_name?.trim()) {
          validationErrors.push(`Third party organisation #${idx + 1}: Organisation name is required when other data is provided.`);
        }
        if (!org.duties || org.duties.length === 0) {
          validationErrors.push(`Third party organisation #${idx + 1}: At least one duty must be selected.`);
        }
      });

      // Union contact points: if any row has data, require sponsor org name and first/last name
      const unionContacts = formData.union_contact_points || [];
      const unionWithData = unionContacts.filter(
        (cp) =>
          cp.sponsor_organisation_name?.trim() ||
          cp.address1?.trim() ||
          cp.address2?.trim() ||
          cp.address3?.trim() ||
          cp.address4?.trim() ||
          cp.town_city?.trim() ||
          cp.postcode?.trim() ||
          cp.country?.trim() ||
          cp.functional_contact_name?.trim() ||
          cp.first_name?.trim() ||
          cp.last_name?.trim() ||
          cp.phone?.trim() ||
          cp.email?.trim(),
      );
      unionWithData.forEach((cp, idx) => {
        if (!cp.sponsor_organisation_name?.trim()) {
          validationErrors.push(`Union contact point #${idx + 1}: Name of sponsor organisation is required when other data is provided.`);
        }
        if (!cp.first_name?.trim()) {
          validationErrors.push(`Union contact point #${idx + 1}: First name is required when other data is provided.`);
        }
        if (!cp.last_name?.trim()) {
          validationErrors.push(`Union contact point #${idx + 1}: Last name is required when other data is provided.`);
        }
      });

      // Sponsor responsibilities: Section changes description is mandatory
      if (!formData.sponsor_responsibilities_section_changes_description?.trim()) {
        validationErrors.push('Sponsor responsibilities section changes description is required.');
      }

      // Products validation (CT05)
      if (formData.products && formData.products.length > 0) {
        formData.products.forEach((product, idx) => {
          // PL: Select substance is mandatory
          if (!product.selected_product_id?.trim()) {
            validationErrors.push(`Product #${idx + 1}: Select substance is required.`);
          }
          // CT05.01.07: Is this a specific paediatric formulation? (mandatory)
          if (product.is_paediatric_formulation === undefined || product.is_paediatric_formulation === null) {
            validationErrors.push(`Product #${idx + 1}: Is this a specific paediatric formulation? is required.`);
          }
          // CT05.01.08: Medicinal product role in trial (mandatory, 1 to many)
          if (!product.medicinal_product_role || product.medicinal_product_role.length === 0) {
            validationErrors.push(`Product #${idx + 1}: Medicinal product role in trial is required.`);
          }
          // CT05.01.09: Sponsor product code (conditionally required - required if product is selected)
          if (product.selected_product_id?.trim() && !product.sponsor_product_code?.trim()) {
            validationErrors.push(`Product #${idx + 1}: Sponsor product code is required when a product is selected.`);
          }
        });
      }

      if (validationErrors.length > 0) {
        setError(validationErrors.join(' '));
        setSaving(false);
        return;
      }

      let existingData = {};
      try {
        existingData = (await protocolRegistrationService.getByProject(projectId)) || {};
      } catch {
        // OK if no existing record
      }

      const payload = {
        ...existingData,
        eu_registry: {
          full_title_english: formData.full_title_english || undefined,
          full_title_translation: formData.full_title_translation || undefined,
          public_title_english: formData.public_title_english || undefined,
          public_title_translation: formData.public_title_translation || undefined,
          protocol_code: formData.protocol_code || undefined,
          who_utn: formData.who_utn || undefined,
          nct_number: formData.nct_number || undefined,
          isrctn_number: formData.isrctn_number || undefined,
          additional_registries: (formData.additional_registries || [])
            .filter((reg) => reg.registry_name?.trim() || reg.registry_identifier?.trim())
            .map((reg) => ({
              registry_name: reg.registry_name || undefined,
              registry_identifier: reg.registry_identifier || undefined,
            })),
          section_changes_description: formData.section_changes_description || undefined,
          // Trial Category
          low_intervention_trial: formData.low_intervention_trial || undefined,
          low_intervention_justification: formData.low_intervention_justification || undefined,
          low_intervention_justification_attachment: formData.low_intervention_justification_attachment || undefined,
          trial_phase: formData.trial_phase || undefined,
          // Medical Condition
          medical_conditions: (formData.medical_conditions || [])
            .filter((cond) => cond.condition_english?.trim() || cond.condition_translation?.trim())
            .map((cond) => ({
              condition_english: cond.condition_english || undefined,
              condition_translation: cond.condition_translation || undefined,
            })),
          is_rare_disease: formData.is_rare_disease || undefined,
          therapeutic_areas: formData.therapeutic_areas?.length > 0 ? formData.therapeutic_areas : undefined,
          // MedDRA Information
          meddra_info: formData.meddra_info?.version || formData.meddra_info?.term_name
            ? {
                version: formData.meddra_info.version || undefined,
                level: formData.meddra_info.level || undefined,
                classification_code: formData.meddra_info.classification_code || undefined,
                term_name: formData.meddra_info.term_name || undefined,
                system_organ_class: formData.meddra_info.system_organ_class || undefined,
              }
            : undefined,
          // Main Objective
          trial_scope: formData.trial_scope?.length > 0 ? formData.trial_scope : undefined,
          main_objective_english: formData.main_objective_english || undefined,
          main_objective_translation: formData.main_objective_translation || undefined,
          // Secondary Objectives
          secondary_objectives: (formData.secondary_objectives || [])
            .filter((obj) => obj.objective_english?.trim() || obj.objective_translation?.trim())
            .map((obj) => ({
              objective_id: obj.objective_id || undefined,
              objective_english: obj.objective_english || undefined,
              objective_translation: obj.objective_translation || undefined,
            })),
          // Eligibility Criteria
          inclusion_criteria: (formData.inclusion_criteria || [])
            .filter((criteria) => criteria.criteria_english?.trim() || criteria.criteria_translation?.trim())
            .map((criteria) => ({
              criteria_id: criteria.criteria_id || undefined,
              criteria_english: criteria.criteria_english || undefined,
              criteria_translation: criteria.criteria_translation || undefined,
            })),
          exclusion_criteria: (formData.exclusion_criteria || [])
            .filter((criteria) => criteria.criteria_english?.trim() || criteria.criteria_translation?.trim())
            .map((criteria) => ({
              criteria_id: criteria.criteria_id || undefined,
              criteria_english: criteria.criteria_english || undefined,
              criteria_translation: criteria.criteria_translation || undefined,
            })),
          // End Points
          primary_endpoints: (formData.primary_endpoints || [])
            .filter((ep) => ep.endpoint_english?.trim() || ep.endpoint_translation?.trim())
            .map((ep) => ({
              endpoint_id: ep.endpoint_id || undefined,
              endpoint_english: ep.endpoint_english || undefined,
              endpoint_translation: ep.endpoint_translation || undefined,
            })),
          secondary_endpoints: (formData.secondary_endpoints || [])
            .filter((ep) => ep.endpoint_english?.trim() || ep.endpoint_translation?.trim())
            .map((ep) => ({
              endpoint_id: ep.endpoint_id || undefined,
              endpoint_english: ep.endpoint_english || undefined,
              endpoint_translation: ep.endpoint_translation || undefined,
            })),
          // Trial Duration
          estimated_recruitment_start_date_eea: formData.estimated_recruitment_start_date_eea
            ? new Date(formData.estimated_recruitment_start_date_eea)
            : undefined,
          estimated_end_of_trial_date_eea: formData.estimated_end_of_trial_date_eea
            ? new Date(formData.estimated_end_of_trial_date_eea)
            : undefined,
          estimated_global_end_of_trial_date: formData.estimated_global_end_of_trial_date
            ? new Date(formData.estimated_global_end_of_trial_date)
            : undefined,
          // Source of Monetary Support
          sources_of_monetary_support: formData.sources_of_monetary_support?.length > 0
            ? formData.sources_of_monetary_support
            : undefined,
          // IPD Sharing Statement
          plan_to_share_ipd: formData.plan_to_share_ipd || undefined,
          ipd_sharing_plan_description: formData.ipd_sharing_plan_description || undefined,
          // Population of Trial Subjects
          age_range: formData.age_range?.length > 0 ? formData.age_range : undefined,
          age_range_warning: formData.age_range_warning || undefined,
          age_range_secondary_identifier: formData.age_range_secondary_identifier?.length > 0
            ? formData.age_range_secondary_identifier
            : undefined,
          gender_male: formData.gender_male || undefined,
          gender_female: formData.gender_female || undefined,
          clinical_trial_group: formData.clinical_trial_group?.length > 0
            ? formData.clinical_trial_group
            : undefined,
          vulnerable_population: formData.vulnerable_population || undefined,
          recruitment_population_group: formData.recruitment_population_group?.length > 0
            ? formData.recruitment_population_group
            : undefined,
          subjects_incapable_of_consent: formData.subjects_incapable_of_consent?.length > 0
            ? formData.subjects_incapable_of_consent
            : undefined,
          emergency_situation_description: formData.emergency_situation_description || undefined,
          other_description: formData.other_description || undefined,
          population_section_changes_description: formData.population_section_changes_description || undefined,
          // Protocol documents
          ctis_protocol_documents:
            formData.ctis_protocol_documents?.length > 0 ? formData.ctis_protocol_documents : undefined,
          ctis_protocol_synopsis_documents:
            formData.ctis_protocol_synopsis_documents?.length > 0
              ? formData.ctis_protocol_synopsis_documents
              : undefined,
          ctis_dsmc_charter_documents:
            formData.ctis_dsmc_charter_documents?.length > 0
              ? formData.ctis_dsmc_charter_documents
              : undefined,
          ctis_study_design_documents:
            formData.ctis_study_design_documents?.length > 0
              ? formData.ctis_study_design_documents
              : undefined,
          // Protocol Information
          study_design_text: formData.study_design_text || undefined,
          // Scientific Advice and Paediatric Investigation Plan (CT03.04)
          scientific_advice_id: formData.scientific_advice_id || undefined,
          scientific_advice_competent_authorities:
            formData.scientific_advice_competent_authorities || undefined,
          scientific_advice_summary_documents:
            formData.scientific_advice_summary_documents?.length > 0
              ? formData.scientific_advice_summary_documents
              : undefined,
          scientific_advice_quality_documents:
            formData.scientific_advice_quality_documents?.length > 0
              ? formData.scientific_advice_quality_documents
              : undefined,
          pip_ema_investigation_number: formData.pip_ema_investigation_number || undefined,
          pip_opinion_link: formData.pip_opinion_link || undefined,
          pip_opinion_documents:
            formData.pip_opinion_documents?.length > 0 ? formData.pip_opinion_documents : undefined,
          scientific_advice_pip_section_changes_description:
            formData.scientific_advice_pip_section_changes_description || undefined,
          // Associated Clinical Trials (CT03.05)
          associated_clinical_trials: (formData.associated_clinical_trials || [])
            .filter(
              (trial) =>
                trial.eu_ct_number?.trim() ||
                trial.eudract_number?.trim() ||
                trial.sponsor?.trim() ||
                trial.full_title?.trim() ||
                trial.agreement_document?.trim(),
            )
            .map((trial) => ({
              eu_ct_number: trial.eu_ct_number || undefined,
              eudract_number: trial.eudract_number || undefined,
              sponsor: trial.sponsor || undefined,
              full_title: trial.full_title || undefined,
              agreement_document: trial.agreement_document || undefined,
            })),
          associated_trials_section_changes_description:
            formData.associated_trials_section_changes_description || undefined,
          // References (CT03.06)
          online_references: formData.online_references || undefined,
          reference_publication: formData.reference_publication || undefined,
          reference_publication_link: formData.reference_publication_link || undefined,
          references_section_changes_description:
            formData.references_section_changes_description || undefined,
          // Countries outside the EEA (CT03.07)
          countries_outside_eea:
            formData.countries_outside_eea?.length > 0 ? formData.countries_outside_eea : undefined,
          planned_subjects_rest_of_world:
            formData.planned_subjects_rest_of_world || undefined,
          countries_section_changes_description:
            formData.countries_section_changes_description || undefined,
          // Sponsors (CT04)
          sponsors: (formData.sponsors || [])
            .filter((s) =>
              s.name?.trim() ||
              s.organisation_type?.trim() ||
              s.address_street?.trim() ||
              s.town_city?.trim() ||
              s.postcode?.trim() ||
              s.country?.trim() ||
              s.phone?.trim() ||
              s.email?.trim(),
            )
            .map((s) => ({
              name: s.name || undefined,
              organisation_type: s.organisation_type || undefined,
              address_street: s.address_street || undefined,
              town_city: s.town_city || undefined,
              postcode: s.postcode || undefined,
              country: s.country || undefined,
              phone: s.phone || undefined,
              email: s.email || undefined,
            })),
          // Legal Representative (CT04.09)
          has_legal_representative: formData.has_legal_representative || undefined,
          legal_representative:
            formData.has_legal_representative === 'Yes'
              ? {
                  name: formData.legal_representative?.name || undefined,
                  organisation_type: formData.legal_representative?.organisation_type || undefined,
                  address: formData.legal_representative?.address || undefined,
                  town_city: formData.legal_representative?.town_city || undefined,
                  postcode: formData.legal_representative?.postcode || undefined,
                  country: formData.legal_representative?.country || undefined,
                  phone: formData.legal_representative?.phone || undefined,
                  email: formData.legal_representative?.email || undefined,
                }
              : undefined,
          legal_representative_contact:
            formData.has_legal_representative === 'Yes'
              ? {
                  first_name: formData.legal_representative_contact?.first_name || undefined,
                  last_name: formData.legal_representative_contact?.last_name || undefined,
                  email: formData.legal_representative_contact?.email || undefined,
                  phone: formData.legal_representative_contact?.phone || undefined,
                }
              : undefined,
          // Scientific and Public contact points (CT04.10)
          scientific_contact_points: (formData.scientific_contact_points || [])
            .filter(
              (c) =>
                c.organisation_name?.trim() ||
                c.functional_name?.trim() ||
                c.phone?.trim() ||
                c.email?.trim(),
            )
            .map((c) => ({
              organisation_name: c.organisation_name || undefined,
              functional_name: c.functional_name || undefined,
              phone: c.phone || undefined,
              email: c.email || undefined,
            })),
          scientific_same_as_public: formData.scientific_same_as_public || undefined,
          public_contact_points:
            formData.scientific_same_as_public === 'Yes'
              ? undefined
              : (formData.public_contact_points || [])
                  .filter(
                    (c) =>
                      c.organisation_name?.trim() ||
                      c.functional_name?.trim() ||
                      c.phone?.trim() ||
                      c.email?.trim(),
                  )
                  .map((c) => ({
                    organisation_name: c.organisation_name || undefined,
                    functional_name: c.functional_name || undefined,
                    email: c.email || undefined,
                    phone: c.phone || undefined,
                  })),
          // Third party organisation(s) associated with the trial (CT04.11)
          third_party_organisations: (formData.third_party_organisations || [])
            .filter((org) =>
              org.organisation_name?.trim() ||
              org.country?.trim() ||
              org.address_street?.trim() ||
              org.town_city?.trim() ||
              org.postcode?.trim() ||
              org.phone?.trim() ||
              org.email?.trim() ||
              (org.duties && org.duties.length > 0),
            )
            .map((org) => ({
              organisation_id: org.organisation_id || undefined,
              organisation_name: org.organisation_name || undefined,
              country: org.country || undefined,
              address_street: org.address_street || undefined,
              town_city: org.town_city || undefined,
              postcode: org.postcode || undefined,
              phone: org.phone || undefined,
              email: org.email || undefined,
              duties: org.duties?.length ? org.duties : undefined,
            })),
          // Sponsor's contact point for Union (CT04.12)
          union_contact_points: (formData.union_contact_points || [])
            .filter((cp) =>
              cp.sponsor_organisation_name?.trim() ||
              cp.address1?.trim() ||
              cp.address2?.trim() ||
              cp.address3?.trim() ||
              cp.address4?.trim() ||
              cp.town_city?.trim() ||
              cp.postcode?.trim() ||
              cp.country?.trim() ||
              cp.functional_contact_name?.trim() ||
              cp.first_name?.trim() ||
              cp.last_name?.trim() ||
              cp.phone?.trim() ||
              cp.email?.trim(),
            )
            .map((cp) => ({
              sponsor_organisation_name: cp.sponsor_organisation_name || undefined,
              address1: cp.address1 || undefined,
              address2: cp.address2 || undefined,
              address3: cp.address3 || undefined,
              address4: cp.address4 || undefined,
              town_city: cp.town_city || undefined,
              postcode: cp.postcode || undefined,
              country: cp.country || undefined,
              functional_contact_name: cp.functional_contact_name || undefined,
              first_name: cp.first_name || undefined,
              last_name: cp.last_name || undefined,
              phone: cp.phone || undefined,
              email: cp.email || undefined,
            })),
          // Responsibilities of the sponsors (CT04.13)
          sponsor_responsible_for_compliance:
            formData.sponsor_responsible_for_compliance || undefined,
          sponsor_responsible_contact_point:
            formData.sponsor_responsible_contact_point || undefined,
          sponsor_responsible_article_77:
            formData.sponsor_responsible_article_77 || undefined,
          sponsor_responsibilities_section_changes_description:
            formData.sponsor_responsibilities_section_changes_description || undefined,
          // Products (CT05)
          products: (formData.products || [])
            .filter((p) => p.selected_product_id?.trim())
            .map((p) => ({
              selected_product_id: p.selected_product_id || undefined,
              eu_medicinal_product_number: p.eu_medicinal_product_number || undefined,
              marketing_authorisation: p.marketing_authorisation || undefined,
              product_authorisation: p.product_authorisation || undefined,
              name: p.name || undefined,
              pharmaceutical_form: p.pharmaceutical_form || undefined,
              strength: p.strength || undefined,
              active_substance_name: p.active_substance_name || undefined,
              eu_substance_number: p.eu_substance_number || undefined,
              atc_code: p.atc_code || undefined,
              atc_name: p.atc_name || undefined,
              atc_level: p.atc_level || undefined,
              sponsor_code: p.sponsor_code || undefined,
              active_substance_other_descriptive_name: p.active_substance_other_descriptive_name || undefined,
              type: p.type || undefined,
              placebo_name: p.placebo_name || undefined,
              medicinal_product_other_name: p.medicinal_product_other_name || undefined,
              eu_medicinal_product_unique_id: p.eu_medicinal_product_unique_id || undefined,
              authorisation_status: p.authorisation_status || undefined,
              is_paediatric_formulation: p.is_paediatric_formulation || undefined,
              medicinal_product_role: p.medicinal_product_role?.length > 0 ? p.medicinal_product_role : undefined,
              sponsor_product_code: p.sponsor_product_code || undefined,
              medicinal_product_characteristics:
                p.medicinal_product_characteristics?.length > 0
                  ? p.medicinal_product_characteristics
                  : undefined,
              route_of_administration: p.route_of_administration || undefined,
              max_treatment_duration: p.max_treatment_duration || undefined,
              max_treatment_duration_unit: p.max_treatment_duration_unit || undefined,
              max_daily_dose_allowed: p.max_daily_dose_allowed || undefined,
              daily_dose_unit: p.daily_dose_unit || undefined,
              max_total_dose_unit_allowed: p.max_total_dose_allowed || undefined,
              total_dose_unit: p.total_dose_unit || undefined,
              product_modified_from_marketing_authorisation:
                typeof p.product_modified_from_marketing_authorisation === 'boolean'
                  ? p.product_modified_from_marketing_authorisation
                  : undefined,
              product_modification_description: p.product_modification_description || undefined,
              marketing_authorisation_holder: p.marketing_authorisation_holder || undefined,
              marketing_authorisation_country: p.marketing_authorisation_country || undefined,
              marketing_authorisation_number: p.marketing_authorisation_number || undefined,
              registration_procedure_number: p.registration_procedure_number || undefined,
              has_orphan_drug_designation:
                typeof p.has_orphan_drug_designation === 'boolean'
                  ? p.has_orphan_drug_designation
                  : undefined,
              orphan_drug_designation_number: p.orphan_drug_designation_number || undefined,
              active_substances:
                Array.isArray(p.active_substances) && p.active_substances.length > 0
                  ? p.active_substances
                      .map((s) => ({
                        classification: s.classification?.trim() || undefined,
                        authorisation_status: s.authorisation_status?.trim() || undefined,
                        name: s.name?.trim() || undefined,
                        synonyms: s.synonyms?.trim() || undefined,
                        other_descriptive_name: s.other_descriptive_name?.trim() || undefined,
                        eu_active_substance_code: s.eu_active_substance_code?.trim() || undefined,
                        sponsor_substance_code: s.sponsor_substance_code?.trim() || undefined,
                        strength: s.strength?.trim() || undefined,
                      }))
                      .filter(
                        (s) =>
                          s.classification ||
                          s.authorisation_status ||
                          s.name ||
                          s.synonyms ||
                          s.other_descriptive_name ||
                          s.eu_active_substance_code ||
                          s.sponsor_substance_code ||
                          s.strength,
                      )
                  : undefined,
              gene_therapy: p.gene_therapy
                ? {
                    gene_of_interest: p.gene_therapy.gene_of_interest?.trim() || undefined,
                    gene_therapy_type: p.gene_therapy.gene_therapy_type?.trim() || undefined,
                    gene_transfer_product_type:
                      p.gene_therapy.gene_transfer_product_type?.trim() || undefined,
                    gene_therapy_additional_description:
                      p.gene_therapy.gene_therapy_additional_description?.trim() || undefined,
                  }
                : undefined,
              genetically_modified_cells: p.genetically_modified_cells
                ? {
                    has_genetically_modified_cells:
                      typeof p.genetically_modified_cells.has_genetically_modified_cells ===
                      'boolean'
                        ? p.genetically_modified_cells.has_genetically_modified_cells
                        : undefined,
                    genetically_modified_cells_origin:
                      p.genetically_modified_cells.genetically_modified_cells_origin?.trim() ||
                      undefined,
                    xenogeneic_species_origin:
                      p.genetically_modified_cells.xenogeneic_species_origin?.trim() || undefined,
                    genetically_modified_cells_type:
                      p.genetically_modified_cells.genetically_modified_cells_type?.trim() ||
                      undefined,
                  }
                : undefined,
              somatic_cell_therapy: p.somatic_cell_therapy
                ? {
                    somatic_cell_origin: p.somatic_cell_therapy.somatic_cell_origin?.trim() || undefined,
                    somatic_xenogeneic_species_origin:
                      p.somatic_cell_therapy.somatic_xenogeneic_species_origin?.trim() || undefined,
                    somatic_cell_type: p.somatic_cell_therapy.somatic_cell_type?.trim() || undefined,
                    somatic_differentiated_cell_type:
                      p.somatic_cell_therapy.somatic_differentiated_cell_type?.trim() || undefined,
                    somatic_other_cell_type:
                      p.somatic_cell_therapy.somatic_other_cell_type?.trim() || undefined,
                  }
                : undefined,
              tissue_engineered_products:
                Array.isArray(p.tissue_engineered_products) && p.tissue_engineered_products.length > 0
                  ? p.tissue_engineered_products
                      .map((tep) => ({
                        tissue_engineered_tissue_origin:
                          tep.tissue_engineered_tissue_origin?.trim() || undefined,
                        tissue_engineered_xenogeneic_species_origin:
                          tep.tissue_engineered_xenogeneic_species_origin?.trim() || undefined,
                        tissue_engineered_cell_type: tep.tissue_engineered_cell_type?.trim() || undefined,
                        tissue_engineered_cell_specification:
                          tep.tissue_engineered_cell_specification?.trim() || undefined,
                      }))
                      .filter(
                        (tep) =>
                          tep.tissue_engineered_tissue_origin ||
                          tep.tissue_engineered_xenogeneic_species_origin ||
                          tep.tissue_engineered_cell_type ||
                          tep.tissue_engineered_cell_specification,
                      )
                  : undefined,
              device_associated: p.device_associated
                ? {
                    product_used_with_device:
                      typeof p.device_associated.product_used_with_device === 'boolean'
                        ? p.device_associated.product_used_with_device
                        : undefined,
                    product_ids:
                      Array.isArray(p.device_associated.product_ids) && p.device_associated.product_ids.length > 0
                        ? p.device_associated.product_ids.filter((id) => id?.trim()).map((id) => id.trim())
                        : undefined,
                    device_type: p.device_associated.device_type?.trim() || undefined,
                    device_has_ce_mark:
                      typeof p.device_associated.device_has_ce_mark === 'boolean'
                        ? p.device_associated.device_has_ce_mark
                        : undefined,
                    device_trade_name: p.device_associated.device_trade_name?.trim() || undefined,
                    device_notified_body: p.device_associated.device_notified_body?.trim() || undefined,
                    device_description: p.device_associated.device_description?.trim() || undefined,
                  }
                : undefined,
              investigator_brochure_documents:
                Array.isArray(p.investigator_brochure_documents) && p.investigator_brochure_documents.length > 0
                  ? p.investigator_brochure_documents
                  : undefined,
              smpc_documents:
                Array.isArray(p.smpc_documents) && p.smpc_documents.length > 0 ? p.smpc_documents : undefined,
              manufacturing_authorisation_documents:
                Array.isArray(p.manufacturing_authorisation_documents) &&
                p.manufacturing_authorisation_documents.length > 0
                  ? p.manufacturing_authorisation_documents
                  : undefined,
              manufacturing_authorisation_number:
                p.manufacturing_authorisation_number?.trim() || undefined,
              qp_gmp_certification_documents:
                Array.isArray(p.qp_gmp_certification_documents) && p.qp_gmp_certification_documents.length > 0
                  ? p.qp_gmp_certification_documents
                  : undefined,
            })),
          mpd_unavailable: formData.mpd_unavailable || undefined,
          remove_product: formData.remove_product || undefined,
          trial_sites: formData.trial_sites
            ? {
                organisation_id: (formData.trial_sites.organisation_id || []).filter((v) => v !== '').map((v) => (typeof v === 'number' ? v : Number(v))).filter((v) => !isNaN(v)),
                organisation_name: (formData.trial_sites.organisation_name || []).filter((v) => v !== ''),
                site_location: (formData.trial_sites.site_location || []).filter((v) => v !== ''),
                site_street_address: (formData.trial_sites.site_street_address || []).filter((v) => v !== ''),
                site_city: (formData.trial_sites.site_city || []).filter((v) => v !== ''),
                site_post_code: (formData.trial_sites.site_post_code || []).filter((v) => v !== ''),
                site_country: (formData.trial_sites.site_country || []).filter((v) => v !== ''),
                investigator: {
                  title: formData.trial_sites.investigator?.title || undefined,
                  first_name: formData.trial_sites.investigator?.first_name || undefined,
                  last_name: formData.trial_sites.investigator?.last_name || undefined,
                  department: formData.trial_sites.investigator?.department || undefined,
                  phone: formData.trial_sites.investigator?.phone || undefined,
                  email: formData.trial_sites.investigator?.email || undefined,
                },
              }
            : undefined,
          documents: formData.documents
            ? {
                recruitment_arrangements_documents: formData.documents.recruitment_arrangements_documents || [],
                subject_information_consent_documents: formData.documents.subject_information_consent_documents || [],
                investigator_cv_documents: formData.documents.investigator_cv_documents || [],
                investigator_suitability_documents: formData.documents.investigator_suitability_documents || [],
                facilities_suitability_documents: formData.documents.facilities_suitability_documents || [],
                proof_of_insurance_documents: formData.documents.proof_of_insurance_documents || [],
                financial_arrangements_documents: formData.documents.financial_arrangements_documents || [],
                data_protection_compliance_documents: formData.documents.data_protection_compliance_documents || [],
                biological_samples_compliance_documents: formData.documents.biological_samples_compliance_documents || [],
              }
            : undefined,
        },
      };

      await protocolRegistrationService.upsertByProject(projectId, payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save CTIS data');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoPopulateFromCoreDatasheet = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }

    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateRegistry(
        projectId,
        'eu'
      );

      if (response.success && response.data) {
        // Merge auto-populated data with existing form data
        const mergedData = { ...formData };
        
        // Deep merge function for nested objects
        const deepMerge = (target, source) => {
          Object.keys(source).forEach(key => {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
              if (!target[key]) target[key] = {};
              deepMerge(target[key], source[key]);
            } else if (source[key] !== null && source[key] !== undefined && source[key] !== '') {
              if (Array.isArray(source[key]) && source[key].length > 0) {
                target[key] = source[key];
              } else if (!Array.isArray(source[key])) {
                target[key] = source[key];
              }
            }
          });
        };

        deepMerge(mergedData, response.data);
        setFormData(mergedData);
        
        // Show warnings if any
        if (response.metadata?.warnings && response.metadata.warnings.length > 0) {
          showSnackbar(
            `Form auto-populated. Note: ${response.metadata.warnings.length} field(s) require manual entry. See tooltips for guidance.`,
            'warning'
          );
        } else {
          showSnackbar('Form auto-populated from Core Datasheet. Review and save when ready.', 'success');
        }
      } else {
        showSnackbar(response.message || 'Failed to auto-populate from Core Datasheet', 'error');
      }
    } catch (error) {
      console.error('Error auto-populating from Core Datasheet:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to auto-populate from Core Datasheet',
        'error'
      );
    } finally {
      setAutoPopulating(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAutoFill = async () => {
    if (!projectId) {
      setError('Please select a project before auto-fill.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await protocolRegistrationService.autoFill(projectId);

      // Extract English titles
      const fullTitleEnglish = data?.study_identification?.official_title || '';
      const publicTitleEnglish = data?.study_identification?.brief_title || '';

      // Try to derive secondary IDs from extracted data
      let whoUtn = '';
      let nctNumber = '';
      let isrctnNumber = '';

      const secId = data?.study_identification?.secondary_id;
      if (secId?.id_type && secId?.description) {
        const idType = secId.id_type.toLowerCase();
        if (idType.includes('utn') || idType.includes('who universal trial')) {
          whoUtn = secId.description;
        } else if (idType.includes('nct') || idType.includes('clinicaltrials.gov')) {
          nctNumber = secId.description;
        } else if (idType.includes('isrctn')) {
          isrctnNumber = secId.description;
        }
      }

      // Update form with English titles and identifiers first
      setFormData((prev) => ({
        ...prev,
        full_title_english: fullTitleEnglish || prev.full_title_english,
        public_title_english: publicTitleEnglish || prev.public_title_english,
        protocol_code:
          data?.study_identification?.unique_protocol_id || prev.protocol_code,
        who_utn: whoUtn || prev.who_utn,
        nct_number: nctNumber || prev.nct_number,
        isrctn_number: isrctnNumber || prev.isrctn_number,
        // Trial Category
        trial_phase: data?.study_design?.interventional?.study_phase || prev.trial_phase,
        // Medical Condition
        medical_conditions: data?.condition_keywords?.primary_condition
          ? [{ condition_english: data.condition_keywords.primary_condition, condition_translation: '' }]
          : prev.medical_conditions,
        therapeutic_areas: data?.condition_keywords?.keywords?.length
          ? data.condition_keywords.keywords
          : prev.therapeutic_areas,
        // Main Objective
        main_objective_english: data?.outcomes?.primary?.[0]?.description ||
          data?.study_overview?.primary_objective ||
          prev.main_objective_english,
        // Secondary Objectives
        secondary_objectives: data?.outcomes?.secondary?.length
          ? data.outcomes.secondary.map((obj, idx) => ({
              objective_id: idx + 1,
              objective_english: obj.description || obj.name || '',
              objective_translation: '',
            }))
          : prev.secondary_objectives,
        // Eligibility Criteria - Parse from eligibility_criteria string
        inclusion_criteria: data?.eligibility?.eligibility_criteria
          ? parseEligibilityCriteria(data.eligibility.eligibility_criteria, 'inclusion')
          : prev.inclusion_criteria,
        exclusion_criteria: data?.eligibility?.eligibility_criteria
          ? parseEligibilityCriteria(data.eligibility.eligibility_criteria, 'exclusion')
          : prev.exclusion_criteria,
      }));

      // Automatically translate medical conditions to selected EU language (right after setting)
      const primaryCondition = data?.condition_keywords?.primary_condition;
      if (primaryCondition && primaryCondition.trim()) {
        try {
          const conditionTranslation = await protocolRegistrationService.translateToEU(
            primaryCondition,
            selectedLanguage
          );
          setFormData((prev) => ({
            ...prev,
            medical_conditions: prev.medical_conditions.map((cond, idx) => 
              idx === 0 && cond.condition_english === primaryCondition
                ? { ...cond, condition_translation: conditionTranslation?.translatedText || '' }
                : cond
            ),
          }));
        } catch (translationError) {
          console.warn('Medical condition translation failed, but auto-fill succeeded:', translationError);
          // Don't fail the whole operation if translation fails
        }
      }

      // Automatically translate inclusion and exclusion criteria to selected EU language
      const eligibilityCriteriaText = data?.eligibility?.eligibility_criteria;
      if (eligibilityCriteriaText) {
        try {
          // Parse inclusion criteria and translate
          const inclusionCriteria = parseEligibilityCriteria(eligibilityCriteriaText, 'inclusion');
          if (inclusionCriteria.length > 0) {
            const inclusionTranslationPromises = inclusionCriteria.map((criteria) => {
              if (criteria.criteria_english?.trim()) {
                return protocolRegistrationService.translateToEU(criteria.criteria_english, selectedLanguage);
              }
              return Promise.resolve({ translatedText: '' });
            });

            const inclusionTranslations = await Promise.all(inclusionTranslationPromises);
            setFormData((prev) => ({
              ...prev,
              inclusion_criteria: inclusionCriteria.map((criteria, idx) => ({
                ...criteria,
                criteria_translation: inclusionTranslations[idx]?.translatedText || '',
              })),
            }));
          }

          // Parse exclusion criteria and translate
          const exclusionCriteria = parseEligibilityCriteria(eligibilityCriteriaText, 'exclusion');
          if (exclusionCriteria.length > 0) {
            const exclusionTranslationPromises = exclusionCriteria.map((criteria) => {
              if (criteria.criteria_english?.trim()) {
                return protocolRegistrationService.translateToEU(criteria.criteria_english, selectedLanguage);
              }
              return Promise.resolve({ translatedText: '' });
            });

            const exclusionTranslations = await Promise.all(exclusionTranslationPromises);
            setFormData((prev) => ({
              ...prev,
              exclusion_criteria: exclusionCriteria.map((criteria, idx) => ({
                ...criteria,
                criteria_translation: exclusionTranslations[idx]?.translatedText || '',
              })),
            }));
          }
        } catch (translationError) {
          console.warn('Eligibility criteria translation failed, but auto-fill succeeded:', translationError);
          // Don't fail the whole operation if translation fails
        }
      }

      // Automatically translate to selected EU language if English titles exist
      if (fullTitleEnglish || publicTitleEnglish) {
        try {
          const translations = await Promise.all([
            fullTitleEnglish
              ? protocolRegistrationService.translateToEU(fullTitleEnglish, selectedLanguage)
              : Promise.resolve({ translatedText: '' }),
            publicTitleEnglish
              ? protocolRegistrationService.translateToEU(publicTitleEnglish, selectedLanguage)
              : Promise.resolve({ translatedText: '' }),
          ]);

          // Update form with translations
          setFormData((prev) => ({
            ...prev,
            full_title_translation: translations[0]?.translatedText || prev.full_title_translation,
            public_title_translation: translations[1]?.translatedText || prev.public_title_translation,
          }));
        } catch (translationError) {
          console.warn('Translation failed, but auto-fill succeeded:', translationError);
          // Don't fail the whole operation if translation fails
        }
      }

      // Automatically translate main objective to selected EU language
      const mainObjectiveEnglish = data?.outcomes?.primary?.[0]?.description ||
        data?.study_overview?.primary_objective;
      if (mainObjectiveEnglish && mainObjectiveEnglish.trim()) {
        try {
          const objectiveTranslation = await protocolRegistrationService.translateToEU(
            mainObjectiveEnglish,
            selectedLanguage
          );
          setFormData((prev) => ({
            ...prev,
            main_objective_translation: objectiveTranslation?.translatedText || '',
          }));
        } catch (translationError) {
          console.warn('Main objective translation failed, but auto-fill succeeded:', translationError);
        }
      }

      // Automatically translate secondary objectives to selected EU language
      const secondaryObjectives = data?.outcomes?.secondary;
      if (secondaryObjectives && secondaryObjectives.length > 0) {
        try {
          // Translate all secondary objectives in parallel
          const translationPromises = secondaryObjectives.map((obj) => {
            const englishText = obj.description || obj.name || '';
            if (englishText.trim()) {
              return protocolRegistrationService.translateToEU(englishText, selectedLanguage);
            }
            return Promise.resolve({ translatedText: '' });
          });

          const translations = await Promise.all(translationPromises);

          setFormData((prev) => {
            const updatedObjectives = secondaryObjectives.map((obj, idx) => ({
              objective_id: idx + 1,
              objective_english: obj.description || obj.name || '',
              objective_translation: translations[idx]?.translatedText || '',
            }));
            return {
              ...prev,
              secondary_objectives: updatedObjectives,
            };
          });
        } catch (translationError) {
          console.warn('Secondary objectives translation failed, but auto-fill succeeded:', translationError);
        }
      }

      // Auto-fill End Points from outcomes
      const primaryOutcomes = data?.outcomes?.primary;
      const secondaryOutcomes = data?.outcomes?.secondary;
      if (primaryOutcomes && primaryOutcomes.length > 0) {
        setFormData((prev) => ({
          ...prev,
          primary_endpoints: primaryOutcomes.map((outcome, idx) => ({
            endpoint_id: idx + 1,
            endpoint_english: outcome.description || outcome.title || '',
            endpoint_translation: '',
          })),
        }));
        // Auto-translate primary endpoints
        try {
          const primaryEndpointTranslations = await Promise.all(
            primaryOutcomes.map((outcome) => {
              const text = outcome.description || outcome.title || '';
              return text.trim()
                ? protocolRegistrationService.translateToEU(text, selectedLanguage)
                : Promise.resolve({ translatedText: '' });
            })
          );
          setFormData((prev) => ({
            ...prev,
            primary_endpoints: primaryOutcomes.map((outcome, idx) => ({
              endpoint_id: idx + 1,
              endpoint_english: outcome.description || outcome.title || '',
              endpoint_translation: primaryEndpointTranslations[idx]?.translatedText || '',
            })),
          }));
        } catch (translationError) {
          console.warn('Primary endpoints translation failed, but auto-fill succeeded:', translationError);
        }
      }
      if (secondaryOutcomes && secondaryOutcomes.length > 0) {
        setFormData((prev) => ({
          ...prev,
          secondary_endpoints: secondaryOutcomes.map((outcome, idx) => ({
            endpoint_id: idx + 1,
            endpoint_english: outcome.description || outcome.title || '',
            endpoint_translation: '',
          })),
        }));
        // Auto-translate secondary endpoints
        try {
          const secondaryEndpointTranslations = await Promise.all(
            secondaryOutcomes.map((outcome) => {
              const text = outcome.description || outcome.title || '';
              return text.trim()
                ? protocolRegistrationService.translateToEU(text, selectedLanguage)
                : Promise.resolve({ translatedText: '' });
            })
          );
          setFormData((prev) => ({
            ...prev,
            secondary_endpoints: secondaryOutcomes.map((outcome, idx) => ({
              endpoint_id: idx + 1,
              endpoint_english: outcome.description || outcome.title || '',
              endpoint_translation: secondaryEndpointTranslations[idx]?.translatedText || '',
            })),
          }));
        } catch (translationError) {
          console.warn('Secondary endpoints translation failed, but auto-fill succeeded:', translationError);
        }
      }

      // Auto-fill Trial Duration dates
      if (data?.study_status?.study_start_date) {
        setFormData((prev) => ({
          ...prev,
          estimated_recruitment_start_date_eea: new Date(data.study_status.study_start_date).toISOString().split('T')[0],
        }));
      }
      if (data?.study_status?.study_completion_date) {
        setFormData((prev) => ({
          ...prev,
          estimated_end_of_trial_date_eea: new Date(data.study_status.study_completion_date).toISOString().split('T')[0],
          estimated_global_end_of_trial_date: new Date(data.study_status.study_completion_date).toISOString().split('T')[0],
        }));
      }

      // Auto-fill Products (CT05) from extracted data
      if (data?.products && Array.isArray(data.products) && data.products.length > 0) {
        setFormData((prev) => ({
          ...prev,
          products: data.products.map((prod, index) => {
            const existing = prev.products && prev.products[index] ? prev.products[index] : {};
            return {
              ...existing,
              selected_product_id: prod.name || existing.selected_product_id || '',
              eu_medicinal_product_number: existing.eu_medicinal_product_number || '',
              marketing_authorisation: existing.marketing_authorisation || '',
              product_authorisation: existing.product_authorisation || '',
              name: prod.name || existing.name || '',
              pharmaceutical_form: Array.isArray(prod.pharmaceutical_form)
                ? prod.pharmaceutical_form.join(', ')
                : prod.pharmaceutical_form || existing.pharmaceutical_form || '',
              strength: Array.isArray(prod.strength)
                ? prod.strength.join(', ')
                : prod.strength || existing.strength || '',
              active_substance_name: Array.isArray(prod.active_substance_name)
                ? prod.active_substance_name.join(', ')
                : prod.active_substance_name || existing.active_substance_name || '',
              eu_substance_number: existing.eu_substance_number || '',
              atc_code: Array.isArray(prod.atc_code)
                ? prod.atc_code.join(', ')
                : prod.atc_code || existing.atc_code || '',
              atc_name: existing.atc_name || '',
              atc_level: existing.atc_level || '',
              sponsor_code: Array.isArray(prod.sponsor_code)
                ? prod.sponsor_code.join(', ')
                : prod.sponsor_code || existing.sponsor_code || '',
              active_substance_other_descriptive_name: Array.isArray(
                prod.active_substance_other_descriptive_name,
              )
                ? prod.active_substance_other_descriptive_name.join(', ')
                : prod.active_substance_other_descriptive_name ||
                  existing.active_substance_other_descriptive_name ||
                  '',
              type: existing.type || '',
              placebo_name: prod.placebo_description || existing.placebo_name || '',
              medicinal_product_other_name: Array.isArray(prod.medicinal_product_other_name)
                ? prod.medicinal_product_other_name.join(', ')
                : prod.medicinal_product_other_name || existing.medicinal_product_other_name || '',
              eu_medicinal_product_unique_id:
                prod.eu_medicinal_product_unique_id || existing.eu_medicinal_product_unique_id || '',
              authorisation_status: prod.authorisation_status || existing.authorisation_status || '',
              medicinal_product_characteristics: Array.isArray(prod.medicinal_product_characteristics)
                ? prod.medicinal_product_characteristics
                : existing.medicinal_product_characteristics || [],
              route_of_administration: Array.isArray(prod.route_of_administration)
                ? prod.route_of_administration.join(', ')
                : prod.route_of_administration || existing.route_of_administration || '',
              max_treatment_duration:
                typeof prod.maximum_treatment_duration === 'number'
                  ? String(prod.maximum_treatment_duration)
                  : prod.maximum_treatment_duration || existing.max_treatment_duration || '',
              max_treatment_duration_unit:
                prod.maximum_treatment_duration_unit || existing.max_treatment_duration_unit || '',
              max_daily_dose_allowed:
                prod.maximum_daily_dose_allowed || existing.max_daily_dose_allowed || '',
              daily_dose_unit: prod.maximum_daily_dose_unit || existing.daily_dose_unit || '',
              max_total_dose_allowed:
                prod.maximum_total_dose_allowed || existing.max_total_dose_allowed || '',
              total_dose_unit: prod.maximum_total_dose_unit || existing.total_dose_unit || '',
              product_modified_from_marketing_authorisation:
                typeof prod.modified_from_marketing_authorisation === 'boolean'
                  ? prod.modified_from_marketing_authorisation
                  : existing.product_modified_from_marketing_authorisation || false,
              product_modification_description:
                prod.modification_description || existing.product_modification_description || '',
              advanced_therapy_classification:
                prod.advanced_therapy_classification || existing.advanced_therapy_classification || '',
              cat_reference_number: prod.cat_reference_number || existing.cat_reference_number || '',
              is_paediatric_formulation:
                prod.is_paediatric_formulation === true ||
                prod.is_paediatric_formulation === 'true' ||
                existing.is_paediatric_formulation ||
                false,
              medicinal_product_role:
                Array.isArray(prod.role_in_trial) && prod.role_in_trial.length > 0
                  ? prod.role_in_trial
                  : existing.medicinal_product_role || [],
              sponsor_product_code: prod.sponsor_product_code || existing.sponsor_product_code || '',
              // Active substances (CT05.08) - 1 to many
              active_substances:
                existing.active_substances && Array.isArray(existing.active_substances)
                  ? existing.active_substances
                  : [
                      {
                        classification: prod.active_substance_classification || '',
                        authorisation_status: prod.active_substance_authorisation_status || '',
                        name: Array.isArray(prod.active_substance_name)
                          ? prod.active_substance_name.join(', ')
                          : prod.active_substance_name || '',
                        synonyms: prod.active_substance_name_synonyms || '',
                        other_descriptive_name: prod.active_substance_other_descriptive_name || '',
                        eu_active_substance_code: prod.eu_active_substance_code || '',
                        sponsor_substance_code: prod.sponsor_substance_code || '',
                        strength: prod.active_substance_strength || '',
                      },
                    ],
              gene_therapy: existing.gene_therapy || {
                gene_of_interest: prod.gene_therapy?.gene_of_interest || '',
                gene_therapy_type: prod.gene_therapy?.gene_therapy_type || '',
                gene_transfer_product_type: prod.gene_therapy?.gene_transfer_product_type || '',
                gene_therapy_additional_description:
                  prod.gene_therapy?.gene_therapy_additional_description || '',
              },
              genetically_modified_cells: existing.genetically_modified_cells || {
                has_genetically_modified_cells:
                  typeof prod.genetically_modified_cells?.has_genetically_modified_cells ===
                  'boolean'
                    ? prod.genetically_modified_cells.has_genetically_modified_cells
                    : false,
                genetically_modified_cells_origin:
                  prod.genetically_modified_cells?.genetically_modified_cells_origin || '',
                xenogeneic_species_origin:
                  prod.genetically_modified_cells?.xenogeneic_species_origin || '',
                genetically_modified_cells_type:
                  prod.genetically_modified_cells?.genetically_modified_cells_type || '',
              },
              somatic_cell_therapy: existing.somatic_cell_therapy || {
                somatic_cell_origin: prod.somatic_cell_therapy?.somatic_cell_origin || '',
                somatic_xenogeneic_species_origin:
                  prod.somatic_cell_therapy?.somatic_xenogeneic_species_origin || '',
                somatic_cell_type: prod.somatic_cell_therapy?.somatic_cell_type || '',
                somatic_differentiated_cell_type:
                  prod.somatic_cell_therapy?.somatic_differentiated_cell_type || '',
                somatic_other_cell_type: prod.somatic_cell_therapy?.somatic_other_cell_type || '',
              },
              tissue_engineered_products:
                existing.tissue_engineered_products && Array.isArray(existing.tissue_engineered_products)
                  ? existing.tissue_engineered_products
                  : prod.tissue_engineered_products && Array.isArray(prod.tissue_engineered_products)
                    ? prod.tissue_engineered_products
                    : prod.tissue_engineered_product
                      ? [
                          {
                            tissue_engineered_tissue_origin:
                              prod.tissue_engineered_product.tissue_engineered_tissue_origin || '',
                            tissue_engineered_xenogeneic_species_origin:
                              prod.tissue_engineered_product.tissue_engineered_xenogeneic_species_origin || '',
                            tissue_engineered_cell_type:
                              prod.tissue_engineered_product.tissue_engineered_cell_type || '',
                            tissue_engineered_cell_specification:
                              prod.tissue_engineered_product.tissue_engineered_cell_specification || '',
                          },
                        ]
                      : [
                          {
                            tissue_engineered_tissue_origin: '',
                            tissue_engineered_xenogeneic_species_origin: '',
                            tissue_engineered_cell_type: '',
                            tissue_engineered_cell_specification: '',
                          },
                        ],
              device_associated: existing.device_associated || {
                product_used_with_device: prod.device_associated?.product_used_with_device || false,
                product_ids: Array.isArray(prod.device_associated?.product_ids)
                  ? prod.device_associated.product_ids
                  : [],
                device_type: prod.device_associated?.device_type || '',
                device_has_ce_mark: prod.device_associated?.device_has_ce_mark || false,
                device_trade_name: prod.device_associated?.device_trade_name || '',
                device_notified_body: prod.device_associated?.device_notified_body || '',
                device_description: prod.device_associated?.device_description || '',
              },
              investigator_brochure_documents: existing.investigator_brochure_documents || [],
              smpc_documents: existing.smpc_documents || [],
              manufacturing_authorisation_documents: existing.manufacturing_authorisation_documents || [],
              manufacturing_authorisation_number:
                existing.manufacturing_authorisation_number || prod.manufacturing_authorisation_number || '',
              qp_gmp_certification_documents: existing.qp_gmp_certification_documents || [],
              // CT05.13: IMPD-Q
              impd_q_documents: existing.impd_q_documents || prod.impd_q_documents || [],
              simplified_impd_q_documents: existing.simplified_impd_q_documents || prod.simplified_impd_q_documents || [],
              impd_q_justification: existing.impd_q_justification || prod.impd_q_justification || '',
              // CT05.14: IMPD-S and E
              impd_s_e_documents: existing.impd_s_e_documents || prod.impd_s_e_documents || [],
              simplified_impd_s_e_documents: existing.simplified_impd_s_e_documents || prod.simplified_impd_s_e_documents || [],
              impd_s_e_justification: existing.impd_s_e_justification || prod.impd_s_e_justification || '',
              // CT05.15: Auxiliary Medicinal Product Dossier
              ampd_full_documents: existing.ampd_full_documents || prod.ampd_full_documents || [],
              ampd_justification: existing.ampd_justification || prod.ampd_justification || '',
              // CT05.16: Placebo
              impd_q_placebo_documents: existing.impd_q_placebo_documents || prod.impd_q_placebo_documents || [],
              // CT05.17: Content labelling
              content_labelling_documents: existing.content_labelling_documents || prod.content_labelling_documents || [],
              content_labelling_linked_products: existing.content_labelling_linked_products || prod.content_labelling_linked_products || [],
              // CT05.18: Section changes description
              product_section_changes_description: existing.product_section_changes_description || prod.product_section_changes_description || '',
            };
          }),
        }));
      }

      // Auto-fill Trial Sites (CT06.01) from extracted data - Each field is 1 or many
      if (data?.contacts_locations_investigators?.facilities && Array.isArray(data.contacts_locations_investigators.facilities) && data.contacts_locations_investigators.facilities.length > 0) {
        const facilities = data.contacts_locations_investigators.facilities;
        setFormData((prev) => ({
          ...prev,
          trial_sites: {
            organisation_id: facilities.map((f, i) => f.organisation_id || i + 1),
            organisation_name: facilities.map((f) => f.organisation_name || f.name || ''),
            site_location: facilities.map((f) => f.site_location || f.location || ''),
            site_street_address: facilities.map((f) => f.site_street_address || f.address || f.street || ''),
            site_city: facilities.map((f) => f.city || ''),
            site_post_code: facilities.map((f) => f.postal_code || ''),
            site_country: facilities.map((f) => f.country || ''),
            investigator: {
              title: (() => {
                const inv = facilities[0]?.investigators?.[0] || facilities[0]?.contact;
                return inv?.title || '';
              })(),
              first_name: (() => {
                const inv = facilities[0]?.investigators?.[0] || facilities[0]?.contact;
                return inv?.first_name || '';
              })(),
              last_name: (() => {
                const inv = facilities[0]?.investigators?.[0] || facilities[0]?.contact;
                return inv?.last_name || '';
              })(),
              department: facilities[0]?.department || facilities[0]?.investigators?.[0]?.department || facilities[0]?.contact?.department || '',
              phone: (() => {
                const inv = facilities[0]?.investigators?.[0] || facilities[0]?.contact;
                return inv?.phone || '';
              })(),
              email: (() => {
                const inv = facilities[0]?.investigators?.[0] || facilities[0]?.contact;
                return inv?.email || '';
              })(),
            },
          },
        }));
      }

      // Auto-fill IPD Sharing from WHO data if available
      // This would need to be mapped from WHO registry if it exists

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to auto-fill from protocol');
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setUploadingAttachment(true);
    setError(null);
    try {
      const result = await protocolRegistrationService.uploadCtisAttachment(file);
      setFormData((prev) => ({
        ...prev,
        low_intervention_justification_attachment:
          result.url || prev.low_intervention_justification_attachment,
      }));
      // Store the file name for display
      setAttachmentFileName(result.originalName || file.name);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      // Clear the input so selecting the same file again will trigger onChange
      event.target.value = '';
    }
  };

  if (!projectId) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Select or create a project to manage CTIS registration details.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ py: 3, px: 2 }}>
    <Typography variant="h5" gutterBottom>
      European Union Clinical Trials Information System (CTIS)
    </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Capture EU CTIS trial identifiers and titles for this study.
    </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Saved successfully.</Alert>}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Translation Language</InputLabel>
            <Select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              label="Translation Language"
              disabled={loading || saving}
            >
              {EU_LANGUAGES.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={handleAutoFill}
            disabled={loading || saving}
          >
            Auto-fill from Protocol
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleAutoPopulateFromCoreDatasheet}
            disabled={autoPopulating || loading || saving}
            title="Auto-populate from Core Datasheet"
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate from Core Datasheet'}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
  </Box>
      </Stack>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Section: Trial Identifiers
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Full Title (English)"
            value={formData.full_title_english}
            onChange={handleChange('full_title_english')}
            fullWidth
            disabled={loading || saving}
            placeholder="Scientific title of the study as in protocol (English)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={`Full Title (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
            value={formData.full_title_translation}
            onChange={handleChange('full_title_translation')}
            fullWidth
            disabled={loading || saving || translating.fullTitle}
            placeholder={`Full title in ${selectedLanguage} (optional)`}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => handleTranslateField('full_title_translation', formData.full_title_english)}
                  disabled={loading || saving || translating.fullTitle || !formData.full_title_english.trim()}
                  size="small"
                  title={`Translate to ${selectedLanguage}`}
                >
                  <TranslateIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Public Title (English)"
            value={formData.public_title_english}
            onChange={handleChange('public_title_english')}
            fullWidth
            disabled={loading || saving}
            placeholder="Title for lay public in English"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={`Public Title (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
            value={formData.public_title_translation}
            onChange={handleChange('public_title_translation')}
            fullWidth
            disabled={loading || saving || translating.publicTitle}
            placeholder={`Public title in ${selectedLanguage} (optional)`}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => handleTranslateField('public_title_translation', formData.public_title_english)}
                  disabled={loading || saving || translating.publicTitle || !formData.public_title_english.trim()}
                  size="small"
                  title={`Translate to ${selectedLanguage}`}
                >
                  <TranslateIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Protocol Code"
            value={formData.protocol_code}
            onChange={handleChange('protocol_code')}
            fullWidth
            disabled={loading || saving}
            placeholder="Abbreviated title / code / acronym"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Subsection: Secondary Identifying Numbers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Secondary identifiers that the sponsor may have registered the clinical trial with. If there is a Secondary ID assigned to the clinical trial, then this ID must be provided here.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="WHO Universal Trial Number (UTN)"
            value={formData.who_utn}
            onChange={handleChange('who_utn')}
            fullWidth
            disabled={loading || saving}
            placeholder="UXXXX-XXXX-XXXX"
            helperText="Format: UXXXX-XXXX-XXXX"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="ClinicalTrials.gov Identifier (NCT Number)"
            value={formData.nct_number}
            onChange={handleChange('nct_number')}
            fullWidth
            disabled={loading || saving}
            placeholder="NCTXXXXXXXX"
            helperText="Format: NCTXXXXXXXX"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="ISRCTN Number"
            value={formData.isrctn_number}
            onChange={handleChange('isrctn_number')}
            fullWidth
            disabled={loading || saving}
            placeholder="ISRCTNXXXXXXXX"
            helperText="Format: ISRCTNXXXXXXXX"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Additional Registries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Additional registries where the trial is registered. You may add multiple registries.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.additional_registries || []).map((registry, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Additional Registry #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Registry Name"
                  value={registry.registry_name}
                  onChange={handleAdditionalRegistryChange(index, 'registry_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Name of WHO ICTRP partner or Partner registry"
                  helperText="Max 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Registry Identifier"
                  value={registry.registry_identifier}
                  onChange={handleAdditionalRegistryChange(index, 'registry_identifier')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Registry identifier"
                  helperText="Max 20 characters"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  color="error"
                  size="small"
                  onClick={handleRemoveAdditionalRegistry(index)}
                  disabled={loading || saving || formData.additional_registries.length === 1}
                  fullWidth
                  startIcon={<CloseIcon />}
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddAdditionalRegistry}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Another Registry
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Section Changes Description
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The description of the changes made in this section.
    </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Section Changes Description"
            value={formData.section_changes_description}
            onChange={handleChange('section_changes_description')}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder="Describe any changes made in this section"
            helperText="Max 4000 characters"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Trial Information
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Trial Category
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.low_intervention_trial}
                onChange={(e) => setFormData((prev) => ({ ...prev, low_intervention_trial: e.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Low intervention trial"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Confirm if the clinical trial meets the conditions for a low intervention clinical trial as set out in the Regulation.
          </Typography>
        </Grid>
        {formData.low_intervention_trial && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Justification of the Low Interventional Clinical Trial"
                value={formData.low_intervention_justification}
                onChange={handleChange('low_intervention_justification')}
                fullWidth
                multiline
                rows={4}
                disabled={loading || saving}
                placeholder="Detailed description of the justification"
                helperText="Max 4000 characters"
                inputProps={{ maxLength: 4000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={loading || saving || uploadingAttachment}
                    size="medium"
                    startIcon={uploadingAttachment ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  >
                    {uploadingAttachment ? 'Uploading...' : 'Upload Attachment'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleAttachmentUpload}
                    />
                  </Button>
                  {attachmentFileName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {attachmentFileName}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={loading || saving || uploadingAttachment}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            low_intervention_justification_attachment: '',
                          }));
                          setAttachmentFileName('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {uploadingAttachment && (
                  <Typography variant="body2" color="text.secondary">
                    Uploading attachment...
                  </Typography>
                )}
                {!uploadingAttachment && !attachmentFileName && (
                  <Typography variant="body2" color="text.secondary">
                    Accepted file types: .PDF/A, .Doc, .Docx
                  </Typography>
                )}
              </Box>
            </Grid>
          </>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Trial Phase"
            value={formData.trial_phase}
            onChange={handleChange('trial_phase')}
            fullWidth
            disabled={loading || saving}
            required
          >
            {TRIAL_PHASE_OPTIONS.map((phase) => (
              <MenuItem key={phase} value={phase}>
                {phase}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Medical Condition
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Summary of attributes that describe the medical condition that is being investigated within the clinical trial.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.medical_conditions || []).map((condition, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Medical Condition #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Medical Condition (English)"
                  value={condition.condition_english}
                  onChange={handleMedicalConditionChange(index, 'condition_english')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder="Medical condition(s) studied. For healthy volunteer trials, provide intended indication."
                  helperText="Max 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label={`Medical Condition (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={condition.condition_translation}
                  onChange={handleMedicalConditionChange(index, 'condition_translation')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder={`Medical condition in ${selectedLanguage} (optional)`}
                  helperText="Max 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslateMedicalCondition(index, condition.condition_english)}
                        disabled={loading || saving || translating.medicalCondition || !condition.condition_english.trim()}
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  color="error"
                  size="small"
                  onClick={handleRemoveMedicalCondition(index)}
                  disabled={loading || saving || formData.medical_conditions.length === 1}
                  fullWidth
                  startIcon={<CloseIcon />}
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddMedicalCondition}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Another Medical Condition
      </Button>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_rare_disease}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_rare_disease: e.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Is the medical condition considered to be a rare disease?"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            A rare disease is one that affects fewer than 5 in 10,000 people in the general population and shows evidence of gravity (life-threatening, invalidating, or serious and chronic).
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            select
            label="Therapeutic Area"
            value={therapeuticAreaInput}
            onChange={(e) => setTherapeuticAreaInput(e.target.value)}
            fullWidth
            disabled={loading || saving}
            placeholder="Select therapeutic area"
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleAddTherapeuticArea} disabled={loading || saving || !therapeuticAreaInput}>
                  <AddIcon />
                </IconButton>
              ),
            }}
          >
            {THERAPEUTIC_AREA_OPTIONS.map((area) => (
              <MenuItem key={area} value={area}>
                {area}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.therapeutic_areas || []).map((area) => (
              <Chip
                key={area}
                label={area}
                onDelete={handleRemoveTherapeuticArea(area)}
                deleteIcon={<CloseIcon />}
                disabled={loading || saving}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* MedDRA Information Section (CT03.02.02.05) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Subsection: Medical condition(s) MedDRA information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        This sub-section will present the prepopulated fields from the Search Medical condition MedDRA information.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Version"
            value={formData.meddra_info?.version || ''}
            fullWidth
            disabled
            helperText="MedDRA version (read-only)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Level"
            value={formData.meddra_info?.level || ''}
            fullWidth
            disabled
            helperText="MedDRA hierarchy level (read-only)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Classification Code"
            value={formData.meddra_info?.classification_code || ''}
            fullWidth
            disabled
            helperText="MedDRA code (read-only)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Term Name"
            value={formData.meddra_info?.term_name || ''}
            fullWidth
            disabled
            helperText="MedDRA term (read-only)"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="System Organ Class"
            value={formData.meddra_info?.system_organ_class || ''}
            fullWidth
            disabled
            helperText="Primary system organ class (read-only)"
          />
        </Grid>
      </Grid>

      {/* Main Objective Section (CT03.02.03) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Main objective
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Trial Scope</InputLabel>
            <Select
              multiple
              value={formData.trial_scope || []}
              onChange={(e) => setFormData((prev) => ({ ...prev, trial_scope: e.target.value }))}
              disabled={loading || saving}
              label="Trial Scope"
              renderValue={(selected) => selected.join(', ')}
            >
              {TRIAL_SCOPE_OPTIONS.map((scope) => (
                <MenuItem key={scope} value={scope}>
                  {scope}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Clinical trial scope as per Regulation Article 2 (1-2). Reference list number 13. (Mandatory, 1 or many)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Main Objective (English)"
            value={formData.main_objective_english}
            onChange={handleChange('main_objective_english')}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder="Main objective of the trial"
            helperText="Max 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
            required
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleTranslateMainObjective}
                  disabled={loading || saving || translating.mainObjective || !formData.main_objective_english.trim()}
                  size="small"
                  title={`Translate to ${selectedLanguage}`}
                  sx={{ mr: 1 }}
                >
                  <TranslateIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={`Main Objective (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
            value={formData.main_objective_translation}
            onChange={handleChange('main_objective_translation')}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder={`Main objective in ${selectedLanguage} (optional)`}
            helperText="Max 4000 characters"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Secondary Objectives Section (CT03.02.04) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Secondary Objective Number
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.secondary_objectives || []).map((objective, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Secondary Objective #{objective.objective_id || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveSecondaryObjective(index)}
                disabled={loading || saving || formData.secondary_objectives.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Secondary Objective Number (ID)"
                  value={objective.objective_id || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  label="Secondary Objective (English)"
                  value={objective.objective_english || ''}
                  onChange={handleSecondaryObjectiveChange(index, 'objective_english')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Secondary objective of the trial"
                  helperText="Max 4000 characters (Optional)"
                  inputProps={{ maxLength: 4000 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslateSecondaryObjective(index)}
                        disabled={
                          loading ||
                          saving ||
                          translating[`secondaryObjective${index}`] ||
                          !objective.objective_english?.trim()
                        }
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                        sx={{ mr: 1 }}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`Secondary Objective (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={objective.objective_translation || ''}
                  onChange={handleSecondaryObjectiveChange(index, 'objective_translation')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder={`Secondary objective in ${selectedLanguage} (optional)`}
                  helperText="Max 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddSecondaryObjective}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Secondary Objective
      </Button>

      {/* Eligibility Criteria Section (CT03.02.05) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Eligibility criteria
      </Typography>

      {/* Principal Inclusion Criteria Subsection (CT03.02.05.01) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Principal inclusion criteria
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.inclusion_criteria || []).map((criteria, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Inclusion Criteria #{criteria.criteria_id || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveInclusionCriteria(index)}
                disabled={loading || saving || formData.inclusion_criteria.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Inclusion Criteria Number (ID)"
                  value={criteria.criteria_id || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  label="Principal inclusion criteria (English)"
                  value={criteria.criteria_english || ''}
                  onChange={handleInclusionCriteriaChange(index, 'criteria_english')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Principal inclusion criteria including age and sex"
                  helperText="Max 4000 characters (Mandatory)"
                  inputProps={{ maxLength: 4000 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslateInclusionCriteria(index)}
                        disabled={
                          loading ||
                          saving ||
                          translating[`inclusionCriteria${index}`] ||
                          !criteria.criteria_english?.trim()
                        }
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                        sx={{ mr: 1 }}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`Principal inclusion criteria (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={criteria.criteria_translation || ''}
                  onChange={handleInclusionCriteriaChange(index, 'criteria_translation')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder={`Principal inclusion criteria in ${selectedLanguage} (optional)`}
                  helperText="Max 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddInclusionCriteria}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Inclusion Criteria
      </Button>

      {/* Principal Exclusion Criteria Subsection (CT03.02.05.02) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Subsection: Principal exclusion criteria
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.exclusion_criteria || []).map((criteria, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Exclusion Criteria #{criteria.criteria_id || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveExclusionCriteria(index)}
                disabled={loading || saving || formData.exclusion_criteria.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Exclusion Criteria Number (ID)"
                  value={criteria.criteria_id || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  label="Principal exclusion criteria (English)"
                  value={criteria.criteria_english || ''}
                  onChange={handleExclusionCriteriaChange(index, 'criteria_english')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Principal exclusion criteria including age and sex"
                  helperText="Max 4000 characters (Mandatory)"
                  inputProps={{ maxLength: 4000 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslateExclusionCriteria(index)}
                        disabled={
                          loading ||
                          saving ||
                          translating[`exclusionCriteria${index}`] ||
                          !criteria.criteria_english?.trim()
                        }
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                        sx={{ mr: 1 }}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`Principal exclusion criteria (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={criteria.criteria_translation || ''}
                  onChange={handleExclusionCriteriaChange(index, 'criteria_translation')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder={`Principal exclusion criteria in ${selectedLanguage} (optional)`}
                  helperText="Max 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddExclusionCriteria}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Exclusion Criteria
      </Button>

      {/* End Points Section (CT03.02.06) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: End Points
      </Typography>

      {/* Primary end points Subsection (CT03.02.06.01) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Primary end points
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.primary_endpoints || []).map((endpoint, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Primary End Point #{endpoint.endpoint_id || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemovePrimaryEndpoint(index)}
                disabled={loading || saving || formData.primary_endpoints.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Primary end point Number (ID)"
                  value={endpoint.endpoint_id || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  label="Primary end point (English)"
                  value={endpoint.endpoint_english || ''}
                  onChange={handlePrimaryEndpointChange(index, 'endpoint_english')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder="Add primary endpoint in English"
                  helperText="Max 500 characters (Mandatory)"
                  inputProps={{ maxLength: 500 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslatePrimaryEndpoint(index)}
                        disabled={
                          loading ||
                          saving ||
                          translating[`primaryEndpoint${index}`] ||
                          !endpoint.endpoint_english?.trim()
                        }
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                        sx={{ mr: 1 }}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`Primary end point (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={endpoint.endpoint_translation || ''}
                  onChange={handlePrimaryEndpointChange(index, 'endpoint_translation')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder={`Primary end point in ${selectedLanguage} (optional)`}
                  helperText="Max 500 characters"
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddPrimaryEndpoint}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Primary End Point
      </Button>

      {/* Secondary end points Subsection (CT03.02.06.02) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Secondary end points
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.secondary_endpoints || []).map((endpoint, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Secondary End Point #{endpoint.endpoint_id || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveSecondaryEndpoint(index)}
                disabled={loading || saving || formData.secondary_endpoints.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Secondary End Point Number (ID)"
                  value={endpoint.endpoint_id || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <TextField
                  label="Secondary end point (English)"
                  value={endpoint.endpoint_english || ''}
                  onChange={handleSecondaryEndpointChange(index, 'endpoint_english')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder="Add secondary endpoint in English"
                  helperText="Max 500 characters (Optional)"
                  inputProps={{ maxLength: 500 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => handleTranslateSecondaryEndpoint(index)}
                        disabled={
                          loading ||
                          saving ||
                          translating[`secondaryEndpoint${index}`] ||
                          !endpoint.endpoint_english?.trim()
                        }
                        size="small"
                        title={`Translate to ${selectedLanguage}`}
                        sx={{ mr: 1 }}
                      >
                        <TranslateIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`Secondary end point (${EU_LANGUAGES.find(l => l.value === selectedLanguage)?.label.split('(')[0].trim() || selectedLanguage})`}
                  value={endpoint.endpoint_translation || ''}
                  onChange={handleSecondaryEndpointChange(index, 'endpoint_translation')}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || saving}
                  placeholder={`Secondary end point in ${selectedLanguage} (optional)`}
                  helperText="Max 500 characters"
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddSecondaryEndpoint}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Secondary End Point
      </Button>

      {/* Trial Duration Section (CT03.02.07) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Trial duration
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Estimated recruitment start date in EEA"
            type="date"
            value={formData.estimated_recruitment_start_date_eea}
            onChange={handleChange('estimated_recruitment_start_date_eea')}
            fullWidth
            disabled={loading || saving}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Estimated end of trial date in EEA"
            type="date"
            value={formData.estimated_end_of_trial_date_eea}
            onChange={handleChange('estimated_end_of_trial_date_eea')}
            fullWidth
            disabled={loading || saving}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Estimated global end of trial date"
            type="date"
            value={formData.estimated_global_end_of_trial_date}
            onChange={handleChange('estimated_global_end_of_trial_date')}
            fullWidth
            disabled={loading || saving}
            InputLabelProps={{ shrink: true }}
            helperText="Conditionally required"
          />
        </Grid>
      </Grid>

      {/* Source of Monetary Support Section (CT03.02.08) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Source of monetary support or material support
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Source of Monetary or Material Support: Organisation name"
            value={monetarySupportInput}
            onChange={(e) => setMonetarySupportInput(e.target.value)}
            fullWidth
            disabled={loading || saving}
            placeholder="Organisation providing monetary support"
            helperText="Max 100 characters (Optional)"
            inputProps={{ maxLength: 100 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleAddMonetarySupport}
                  disabled={loading || saving || !monetarySupportInput.trim()}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(formData.sources_of_monetary_support || []).map((source) => (
              <Chip
                key={source}
                label={source}
                onDelete={handleRemoveMonetarySupport(source)}
                deleteIcon={<CloseIcon />}
                disabled={loading || saving}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* IPD Sharing Statement Section (CT03.02.11) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Individual Participant Data (IPD) Sharing Statement
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Plan to share IPD</InputLabel>
            <Select
              value={formData.plan_to_share_ipd}
              onChange={handleChange('plan_to_share_ipd')}
              disabled={loading || saving}
              label="Plan to share IPD"
            >
              {IPD_SHARING_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Should indicate whether or not IPD will be shared. WHO field 24-IPD sharing statement. (Mandatory)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Plan description"
            value={formData.ipd_sharing_plan_description}
            onChange={handleChange('ipd_sharing_plan_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="If 'plan to share IPD' is No or Undecided, an explanation may be provided"
            helperText="Max 1000 characters (Optional)"
            inputProps={{ maxLength: 1000 }}
          />
        </Grid>
      </Grid>

      {/* Population of Trial Subjects Section (CT03.02.09) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Population of trial subjects
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Age range warning"
            value={formData.age_range_warning}
            fullWidth
            disabled
            helperText="Static warning: impact of including paediatric population (Read-only)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Age range</InputLabel>
            <Select
              multiple
              value={formData.age_range || []}
              onChange={(e) => setFormData((prev) => ({ ...prev, age_range: e.target.value }))}
              disabled={loading || saving}
              label="Age range"
              renderValue={(selected) => selected.join(', ')}
            >
              {AGE_RANGE_OPTIONS.map((range) => (
                <MenuItem key={range} value={range}>
                  {range}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The age range of the population of the trial subjects. Reference list number 15. (Mandatory, 1 or many)
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Age range secondary identifier</InputLabel>
            <Select
              multiple
              value={formData.age_range_secondary_identifier || []}
              onChange={(e) => setFormData((prev) => ({ ...prev, age_range_secondary_identifier: e.target.value }))}
              disabled={loading || saving}
              label="Age range secondary identifier"
              renderValue={(selected) => selected.join(', ')}
            >
              {AGE_RANGE_OPTIONS.map((range) => (
                <MenuItem key={range} value={range}>
                  {range}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Subject age range secondary identifier. Reference list number 16. (Conditionally Required, 1 or many)
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.gender_male}
                onChange={(e) => setFormData((prev) => ({ ...prev, gender_male: e.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Gender (Are subjects male?)"
            required
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Select if male participants will be included in the clinical trial. (Mandatory)
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.gender_female}
                onChange={(e) => setFormData((prev) => ({ ...prev, gender_female: e.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Gender (Are subjects female?)"
            required
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Select if female participants will be included in the clinical trial. (Mandatory)
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Clinical trial group</InputLabel>
            <Select
              multiple
              value={formData.clinical_trial_group || []}
              onChange={(e) => setFormData((prev) => ({ ...prev, clinical_trial_group: e.target.value }))}
              disabled={loading || saving}
              label="Clinical trial group"
              renderValue={(selected) => selected.join(', ')}
            >
              {THERAPEUTIC_AREA_OPTIONS.map((group) => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The description of the majority of the subjects making up the clinical trial population. Reference list number 17a. (Mandatory, 1 or many)
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.vulnerable_population}
                onChange={(e) => setFormData((prev) => ({ ...prev, vulnerable_population: e.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Vulnerable population"
            required
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Select if vulnerable population will be included in the clinical trial. (Mandatory)
          </Typography>
        </Grid>
        {formData.vulnerable_population && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Recruitment population group</InputLabel>
                <Select
                  multiple
                  value={formData.recruitment_population_group || []}
                  onChange={(e) => setFormData((prev) => ({ ...prev, recruitment_population_group: e.target.value }))}
                  disabled={loading || saving}
                  label="Recruitment population group"
                  renderValue={(selected) => selected.join(', ')}
                >
                  {THERAPEUTIC_AREA_OPTIONS.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Description of the vulnerable population for the subjects making up the clinical trial population. Reference list number 17b. (Conditionally required, 1 or many)
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subjects incapable of giving consent personally</InputLabel>
                <Select
                  multiple
                  value={formData.subjects_incapable_of_consent || []}
                  onChange={(e) => setFormData((prev) => ({ ...prev, subjects_incapable_of_consent: e.target.value }))}
                  disabled={loading || saving}
                  label="Subjects incapable of giving consent personally"
                  renderValue={(selected) => selected.join(', ')}
                >
                  {THERAPEUTIC_AREA_OPTIONS.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Description of subjects incapable of giving consent personally. Reference list 17c. (Conditionally required, 1 or many)
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Emergency situation description"
                value={formData.emergency_situation_description}
                onChange={handleChange('emergency_situation_description')}
                fullWidth
                multiline
                rows={3}
                disabled={loading || saving}
                placeholder="Description of the emergency situation that the subjects may encounter during the clinical trial"
                helperText="Max 4000 characters (Conditionally required)"
                inputProps={{ maxLength: 4000 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Other description"
                value={formData.other_description}
                onChange={handleChange('other_description')}
                fullWidth
                multiline
                rows={3}
                disabled={loading || saving}
                placeholder="Description of the other situation that the subjects may encounter during the clinical trial"
                helperText="Max 4000 characters (Conditionally required)"
                inputProps={{ maxLength: 4000 }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Section changes description for Population of Trial Subjects (CT03.02.10) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Field: Section changes description
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Section changes description"
            value={formData.population_section_changes_description}
            onChange={handleChange('population_section_changes_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="The description of the changes made in this section"
            helperText="Limitation of 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Protocol Information Section (CT03.03) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Protocol information
      </Typography>

      {/* Subsection: Clinical trial Protocol (documents) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Clinical trial Protocol
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload the Clinical trial Protocol documents required for CTIS. Files are also stored in the
        central Documents repository.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {['protocol', 'synopsis', 'dsmc'].map((key) => {
          const labelMap = {
            protocol: 'Document: Protocol',
            synopsis: 'Document: Synopsis of the Protocol',
            dsmc: 'Document: Data Safety Monitoring Committee Charter',
          };
          const fieldMap = {
            protocol: 'ctis_protocol_documents',
            synopsis: 'ctis_protocol_synopsis_documents',
            dsmc: 'ctis_dsmc_charter_documents',
          };
          const field = fieldMap[key];
          const docs = formData[field] || [];
          return (
            <Grid item xs={12} key={key}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={loading || saving || uploadingProtocolDocs[key] || !projectId}
                    size="medium"
                    startIcon={
                      uploadingProtocolDocs[key] ? <CircularProgress size={16} /> : <UploadFileIcon />
                    }
                  >
                    {uploadingProtocolDocs[key] ? 'Uploading...' : labelMap[key]}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={async (event) => {
                        const files = event.target.files;
                        if (!files || files.length === 0 || !projectId) return;

                        setUploadingProtocolDocs((prev) => ({ ...prev, [key]: true }));
                        setError(null);
                        try {
                          const documentType =
                            key === 'protocol'
                              ? 'ctis_protocol'
                              : key === 'synopsis'
                              ? 'ctis_protocol_synopsis'
                              : 'ctis_dsmc_charter';

                          const uploadedDocs = [];
                          for (const file of Array.from(files)) {
                            const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                            // result has shape { url, filename, originalName, ... }
                            uploadedDocs.push({
                              document_type: documentType,
                              title: result.originalName || file.name,
                              file_url: result.url,
                            });
                          }

                          setFormData((prev) => ({
                            ...prev,
                            [field]: [...(prev[field] || []), ...uploadedDocs],
                          }));
                          setSuccess(true);
                          setTimeout(() => setSuccess(false), 3000);
                        } catch (err) {
                          setError(err?.message || 'Failed to upload document(s)');
                        } finally {
                          setUploadingProtocolDocs((prev) => ({ ...prev, [key]: false }));
                          event.target.value = '';
                        }
                      }}
                    />
                  </Button>
                  {docs.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {docs.map((doc, idx) => (
                        <Chip
                          key={`${field}-${idx}`}
                          icon={<CheckCircleIcon color="success" fontSize="small" />}
                          label={doc.title || doc.file_url}
                          disabled={loading || saving}
                          clickable
                          onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                          onDelete={() =>
                            setFormData((prev) => ({
                              ...prev,
                              [field]: (prev[field] || []).filter((_, i) => i !== idx),
                            }))
                          }
                          deleteIcon={<CloseIcon />}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Subsection: Study design */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Study design
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Study design"
            value={formData.study_design_text}
            onChange={handleChange('study_design_text')}
            fullWidth
            multiline
            rows={4}
            disabled={loading || saving}
            placeholder="Study design description"
            helperText="Max 4000 characters (Optional)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Document: Study design
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={loading || saving || uploadingProtocolDocs.studyDesign || !projectId}
            size="medium"
            startIcon={
              uploadingProtocolDocs.studyDesign ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
          >
            {uploadingProtocolDocs.studyDesign ? 'Uploading...' : 'Upload Study Design Document'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (event) => {
                const files = event.target.files;
                if (!files || files.length === 0 || !projectId) return;

                setUploadingProtocolDocs((prev) => ({ ...prev, studyDesign: true }));
                setError(null);
                try {
                  const documentType = 'ctis_study_design';
                  const uploadedDocs = [];
                  for (const file of Array.from(files)) {
                    const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                    uploadedDocs.push({
                      document_type: documentType,
                      title: result.originalName || file.name,
                      file_url: result.url,
                    });
                  }

                  setFormData((prev) => ({
                    ...prev,
                    ctis_study_design_documents: [...(prev.ctis_study_design_documents || []), ...uploadedDocs],
                  }));
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 3000);
                } catch (err) {
                  setError(err?.message || 'Failed to upload Study design document(s)');
                } finally {
                  setUploadingProtocolDocs((prev) => ({ ...prev, studyDesign: false }));
                  event.target.value = '';
                }
              }}
            />
          </Button>
          {formData.ctis_study_design_documents?.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.ctis_study_design_documents || []).map((doc, idx) => (
                <Chip
                  key={`study-design-${idx}`}
                  icon={<CheckCircleIcon color="success" fontSize="small" />}
                  label={doc.title || doc.file_url}
                  disabled={loading || saving}
                  clickable
                  onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                  onDelete={() =>
                    setFormData((prev) => ({
                      ...prev,
                      ctis_study_design_documents: (prev.ctis_study_design_documents || []).filter(
                        (_, i) => i !== idx
                      ),
                    }))
                  }
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Subsection: Period details */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Period details
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.period_details || []).map((period, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Period #{period.period_number || index + 1}
              </Typography>
              <IconButton
                onClick={handleRemovePeriod(index)}
                disabled={loading || saving || formData.period_details.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Period Number"
                  value={period.period_number || ''}
                  fullWidth
                  disabled
                  helperText="Read-only"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Title"
                  value={period.title || ''}
                  onChange={handlePeriodChange(index, 'title')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Title of this period"
                  helperText="Limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  value={period.description || ''}
                  onChange={handlePeriodChange(index, 'description')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Description of this period"
                  helperText="Limitation of 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Allocation method</InputLabel>
                  <Select
                    value={period.allocation_method || ''}
                    onChange={handlePeriodChange(index, 'allocation_method')}
                    disabled={loading || saving}
                    label="Allocation method"
                  >
                    {ALLOCATION_METHOD_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Method of assigning subjects to treatment group in this period (Lookup: Clinical trial allocation method).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Blinding used</InputLabel>
                  <Select
                    value={period.blinding_used || ''}
                    onChange={handlePeriodChange(index, 'blinding_used')}
                    disabled={loading || saving}
                    label="Blinding used"
                  >
                    {BLINDING_METHOD_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Blinding method used in this period (Lookup: Blinding method).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Roles blinded</InputLabel>
                  <Select
                    multiple
                    value={period.roles_blinded || []}
                    onChange={(e) =>
                      handlePeriodChange(index, 'roles_blinded')({
                        target: { value: e.target.value },
                      })
                    }
                    disabled={loading || saving}
                    label="Roles blinded"
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    {ROLES_BLINDED_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Roles that were blinded in this period (Reference list number 55).
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Blinding implementation details"
                  value={period.blinding_implementation_details || ''}
                  onChange={handlePeriodChange(index, 'blinding_implementation_details')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Details of blinding implementation (e.g. double dummy techniques)"
                  helperText="Limitation of 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddPeriod}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Period
      </Button>

      {/* Subsection: Arm details */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Arm details
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.arm_details || []).map((arm, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Arm #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveArm(index)}
                disabled={loading || saving || formData.arm_details.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Arm title"
                  value={arm.arm_title || ''}
                  onChange={handleArmChange(index, 'arm_title')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Title of this arm"
                  helperText="Limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Arm description"
                  value={arm.arm_description || ''}
                  onChange={handleArmChange(index, 'arm_description')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Description of this arm"
                  helperText="Limitation of 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Section changes description"
                  value={arm.arm_section_changes_description || ''}
                  onChange={handleArmChange(index, 'arm_section_changes_description')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading || saving}
                  placeholder="Description of the changes made in this section"
                  helperText="Limitation of 4000 characters (Mandatory)"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddArm}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Arm
      </Button>

      {/* Section: Scientific Advice and Paediatric Investigation Plan (CT03.04) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Scientific Advice and Paediatric Investigation Plan (PIP)
      </Typography>

      {/* Subsection: Scientific Advice (CT03.04.01) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Scientific Advice
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="ID"
            value={formData.scientific_advice_id}
            onChange={handleChange('scientific_advice_id')}
            fullWidth
            disabled={loading || saving}
            helperText="The field will show the ID in CTIS of the Competent Authority(ies) that provided scientific advice (read-only in CTIS)"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Competent authorities that have provided scientific advice"
            value={formData.scientific_advice_competent_authorities}
            onChange={handleChange('scientific_advice_competent_authorities')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="Name of Competent Authority(ies) that have provided scientific advice"
            helperText="Optional"
          />
        </Grid>
        {/* Document: Summary of scientific advice */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Document: Summary of scientific advice
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={loading || saving || uploadingProtocolDocs.sciSummary || !projectId}
            size="medium"
            startIcon={
              uploadingProtocolDocs.sciSummary ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
          >
            {uploadingProtocolDocs.sciSummary ? 'Uploading...' : 'Upload Summary Document'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (event) => {
                const files = event.target.files;
                if (!files || files.length === 0 || !projectId) return;

                setUploadingProtocolDocs((prev) => ({ ...prev, sciSummary: true }));
                setError(null);
                try {
                  const documentType = 'ctis_scientific_advice_summary';
                  const uploadedDocs = [];
                  for (const file of Array.from(files)) {
                    const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                    uploadedDocs.push({
                      document_type: documentType,
                      title: result.originalName || file.name,
                      file_url: result.url,
                    });
                  }
                  setFormData((prev) => ({
                    ...prev,
                    scientific_advice_summary_documents: [
                      ...(prev.scientific_advice_summary_documents || []),
                      ...uploadedDocs,
                    ],
                  }));
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 3000);
                } catch (err) {
                  setError(err?.message || 'Failed to upload Summary of scientific advice document(s)');
                } finally {
                  setUploadingProtocolDocs((prev) => ({ ...prev, sciSummary: false }));
                  event.target.value = '';
                }
              }}
            />
          </Button>
          {formData.scientific_advice_summary_documents?.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.scientific_advice_summary_documents || []).map((doc, idx) => (
                <Chip
                  key={`sci-summary-${idx}`}
                  icon={<CheckCircleIcon color="success" fontSize="small" />}
                  label={doc.title || doc.file_url}
                  clickable
                  disabled={loading || saving}
                  onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                  onDelete={() =>
                    setFormData((prev) => ({
                      ...prev,
                      scientific_advice_summary_documents: (
                        prev.scientific_advice_summary_documents || []
                      ).filter((_, i) => i !== idx),
                    }))
                  }
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Grid>

        {/* Document: Scientific advice – Quality */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Document: Scientific advice – Quality
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={loading || saving || uploadingProtocolDocs.sciQuality || !projectId}
            size="medium"
            startIcon={
              uploadingProtocolDocs.sciQuality ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
          >
            {uploadingProtocolDocs.sciQuality ? 'Uploading...' : 'Upload Scientific Advice – Quality'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (event) => {
                const files = event.target.files;
                if (!files || files.length === 0 || !projectId) return;

                setUploadingProtocolDocs((prev) => ({ ...prev, sciQuality: true }));
                setError(null);
                try {
                  const documentType = 'ctis_scientific_advice_quality';
                  const uploadedDocs = [];
                  for (const file of Array.from(files)) {
                    const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                    uploadedDocs.push({
                      document_type: documentType,
                      title: result.originalName || file.name,
                      file_url: result.url,
                    });
                  }
                  setFormData((prev) => ({
                    ...prev,
                    scientific_advice_quality_documents: [
                      ...(prev.scientific_advice_quality_documents || []),
                      ...uploadedDocs,
                    ],
                  }));
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 3000);
                } catch (err) {
                  setError(err?.message || 'Failed to upload Scientific advice – Quality document(s)');
                } finally {
                  setUploadingProtocolDocs((prev) => ({ ...prev, sciQuality: false }));
                  event.target.value = '';
                }
              }}
            />
          </Button>
          {formData.scientific_advice_quality_documents?.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.scientific_advice_quality_documents || []).map((doc, idx) => (
                <Chip
                  key={`sci-quality-${idx}`}
                  icon={<CheckCircleIcon color="success" fontSize="small" />}
                  label={doc.title || doc.file_url}
                  clickable
                  disabled={loading || saving}
                  onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                  onDelete={() =>
                    setFormData((prev) => ({
                      ...prev,
                      scientific_advice_quality_documents: (
                        prev.scientific_advice_quality_documents || []
                      ).filter((_, i) => i !== idx),
                    }))
                  }
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Subsection: Paediatric Investigation Plan (CT03.04.02) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Subsection: Paediatric Investigation Plan
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="EMA paediatric investigation number"
            value={formData.pip_ema_investigation_number}
            onChange={handleChange('pip_ema_investigation_number')}
            fullWidth
            disabled={loading || saving}
            placeholder="EMEA-XXXXXX-PIPXX-XX"
            helperText="Mandatory (format: EMEA-XXXXXX-PIPXX-XX)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="PIP opinion link"
            value={formData.pip_opinion_link}
            onChange={handleChange('pip_opinion_link')}
            fullWidth
            disabled={loading || saving}
            helperText="Automated link to enable the provision to provide several PIP numbers (read-only in CTIS)"
          />
        </Grid>

        {/* Document: PIP opinion */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Document: PIP opinion
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={loading || saving || uploadingProtocolDocs.pipOpinion || !projectId}
            size="medium"
            startIcon={
              uploadingProtocolDocs.pipOpinion ? <CircularProgress size={16} /> : <UploadFileIcon />
            }
          >
            {uploadingProtocolDocs.pipOpinion ? 'Uploading...' : 'Upload PIP Opinion Document'}
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (event) => {
                const files = event.target.files;
                if (!files || files.length === 0 || !projectId) return;

                setUploadingProtocolDocs((prev) => ({ ...prev, pipOpinion: true }));
                setError(null);
                try {
                  const documentType = 'ctis_pip_opinion';
                  const uploadedDocs = [];
                  for (const file of Array.from(files)) {
                    const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                    uploadedDocs.push({
                      document_type: documentType,
                      title: result.originalName || file.name,
                      file_url: result.url,
                    });
                  }
                  setFormData((prev) => ({
                    ...prev,
                    pip_opinion_documents: [...(prev.pip_opinion_documents || []), ...uploadedDocs],
                  }));
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 3000);
                } catch (err) {
                  setError(err?.message || 'Failed to upload PIP opinion document(s)');
                } finally {
                  setUploadingProtocolDocs((prev) => ({ ...prev, pipOpinion: false }));
                  event.target.value = '';
                }
              }}
            />
          </Button>
          {formData.pip_opinion_documents?.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.pip_opinion_documents || []).map((doc, idx) => (
                <Chip
                  key={`pip-opinion-${idx}`}
                  icon={<CheckCircleIcon color="success" fontSize="small" />}
                  label={doc.title || doc.file_url}
                  clickable
                  disabled={loading || saving}
                  onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                  onDelete={() =>
                    setFormData((prev) => ({
                      ...prev,
                      pip_opinion_documents: (prev.pip_opinion_documents || []).filter(
                        (_, i) => i !== idx,
                      ),
                    }))
                  }
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Grid>

        {/* Section changes description for CT03.04.03 */}
        <Grid item xs={12}>
          <TextField
            label="Section changes description"
            value={formData.scientific_advice_pip_section_changes_description}
            onChange={handleChange('scientific_advice_pip_section_changes_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="The description of the changes made in this section"
            helperText="Limitation of 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Section: Associated Clinical Trials (CT03.05) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
        Section: Associated clinical trials
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Record details of any associated EU clinical trials and agreements from other sponsors.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.associated_clinical_trials || []).map((trial, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Associated clinical trial #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveAssociatedTrial(index)}
                disabled={loading || saving || formData.associated_clinical_trials.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Associated EU CT number"
                  value={trial.eu_ct_number || ''}
                  onChange={handleAssociatedTrialChange(index, 'eu_ct_number')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="EU CT number"
                  helperText="Format of the EU CT number (optional)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="EudraCT number"
                  value={trial.eudract_number || ''}
                  onChange={handleAssociatedTrialChange(index, 'eudract_number')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="EudraCT number"
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Sponsor for associated clinical trial"
                  value={trial.sponsor || ''}
                  onChange={handleAssociatedTrialChange(index, 'sponsor')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Sponsor name"
                  helperText="Conditionally required"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Full title for associated clinical trial"
                  value={trial.full_title || ''}
                  onChange={handleAssociatedTrialChange(index, 'full_title')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  rows={2}
                  placeholder="Full title of the associated clinical trial"
                  helperText="Limitation of 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Document: Agreement from another sponsor
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={loading || saving || uploadingAttachment}
                      size="medium"
                      startIcon={uploadingAttachment ? <CircularProgress size={16} /> : <UploadFileIcon />}
                    >
                      {uploadingAttachment ? 'Uploading...' : 'Upload Agreement'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleSponsorAgreementUpload(index)}
                      />
                    </Button>
                    {sponsorAgreementFileNames[index] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {sponsorAgreementFileNames[index]}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={loading || saving || uploadingAttachment}
                          onClick={() => {
                            setFormData((prev) => {
                              const next = [...prev.associated_clinical_trials];
                              if (next[index]) {
                                next[index] = { ...next[index], agreement_document: '' };
                              }
                              return { ...prev, associated_clinical_trials: next };
                            });
                            setSponsorAgreementFileNames((prev) => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {uploadingAttachment && (
                    <Typography variant="body2" color="text.secondary">
                      Uploading agreement...
                    </Typography>
                  )}
                  {!uploadingAttachment && !sponsorAgreementFileNames[index] && (
                    <Typography variant="body2" color="text.secondary">
                      Accepted file types: .PDF/A, .Doc, .Docx
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddAssociatedTrial}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Associated Clinical Trial
      </Button>

      <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 1 }}>
        Section changes description (Associated clinical trials)
      </Typography>
      <TextField
        label="Section changes description"
        value={formData.associated_trials_section_changes_description}
        onChange={handleChange('associated_trials_section_changes_description')}
        fullWidth
        multiline
        rows={3}
        disabled={loading || saving}
        placeholder="The description of the changes made in this section"
        helperText="Limitation of 4000 characters (Mandatory)"
        inputProps={{ maxLength: 4000 }}
        sx={{ mb: 4 }}
      />

      {/* Section: References (CT03.06) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Section: References
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            label="Online references"
            value={formData.online_references}
            onChange={handleChange('online_references')}
            fullWidth
            multiline
            rows={2}
            disabled={loading || saving}
            placeholder="Provide identifiers to retrieve publications of interest in regards to this clinical trial"
            helperText="Optional"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Reference to publication"
            value={formData.reference_publication}
            onChange={handleChange('reference_publication')}
            fullWidth
            disabled={loading || saving}
            placeholder="e.g. PubMed ID or other reference identifier"
            helperText="Optional"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Reference link to publication"
            value={formData.reference_publication_link}
            onChange={handleChange('reference_publication_link')}
            fullWidth
            disabled={loading || saving}
            placeholder="URL to the publication"
            helperText="Optional"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Section changes description (References)"
            value={formData.references_section_changes_description}
            onChange={handleChange('references_section_changes_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="The description of the changes made in this section"
            helperText="Limitation of 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Section: Countries outside the European Economic Area (CT03.07) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Countries outside the European Economic Area
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Country to add"
            value={countriesOutsideInput}
            onChange={(e) => setCountriesOutsideInput(e.target.value)}
            fullWidth
            disabled={loading || saving}
            placeholder="Enter a country and click the + button"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleAddCountryOutside}
                  disabled={loading || saving || !countriesOutsideInput.trim()}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(formData.countries_outside_eea || []).map((country) => (
              <Chip
                key={country}
                label={country}
                onDelete={handleRemoveCountryOutside(country)}
                deleteIcon={<CloseIcon />}
                disabled={loading || saving}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Planned number of subjects to be enrolled in the Rest of the world"
            type="number"
            value={formData.planned_subjects_rest_of_world}
            onChange={handleChange('planned_subjects_rest_of_world')}
            fullWidth
            disabled={loading || saving}
            helperText="Conditionally required"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Section changes description (Countries outside the EEA)"
            value={formData.countries_section_changes_description}
            onChange={handleChange('countries_section_changes_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="The description of the changes made in this section"
            helperText="Limitation of 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Section: Sponsors (CT04) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
        Section: Sponsors
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.sponsors || []).map((sponsor, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Sponsor #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveSponsor(index)}
                disabled={loading || saving || (formData.sponsors || []).length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sponsor name"
                  value={sponsor.name || ''}
                  onChange={handleSponsorChange(index, 'name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Sponsor organisational name"
                  helperText="Mandatory"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Organisation type</InputLabel>
                  <Select
                    value={sponsor.organisation_type || ''}
                    label="Organisation type"
                    onChange={handleSponsorChange(index, 'organisation_type')}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Non-commercial">Non-commercial</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Field stating whether the sponsor is a commercial or non-commercial sponsor (Reference list 125).
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address: Street name"
                  value={sponsor.address_street || ''}
                  onChange={handleSponsorChange(index, 'address_street')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Address of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Town / City"
                  value={sponsor.town_city || ''}
                  onChange={handleSponsorChange(index, 'town_city')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Town / City of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Post code"
                  value={sponsor.postcode || ''}
                  onChange={handleSponsorChange(index, 'postcode')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Post code of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Country"
                  value={sponsor.country || ''}
                  onChange={handleSponsorChange(index, 'country')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Country of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={sponsor.phone || ''}
                  onChange={handleSponsorChange(index, 'phone')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Phone number of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={sponsor.email || ''}
                  onChange={handleSponsorChange(index, 'email')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Email address of the sponsor's organisation"
                  helperText="Read only in CTIS"
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddSponsor}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Sponsor
      </Button>

      {/* Subsection: Legal Representative (CT04.09) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Subsection: Legal Representative
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={loading || saving}>
            <InputLabel>Legal Representative</InputLabel>
            <Select
              value={formData.has_legal_representative || ''}
              label="Legal Representative"
              onChange={handleChange('has_legal_representative')}
            >
              <MenuItem value="">
                <em>Select...</em>
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This section is shown only if a Legal Representative for the sponsor is defined.
            </Typography>
          </FormControl>
        </Grid>
      </Grid>

      {formData.has_legal_representative === 'Yes' && (
        <>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, mb: 1 }}>
            Legal Representative Organisation
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={formData.legal_representative.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, name: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Organisational name"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading || saving}>
                <InputLabel>Organisation type</InputLabel>
                <Select
                  value={formData.legal_representative.organisation_type || ''}
                  label="Organisation type"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      legal_representative: {
                        ...prev.legal_representative,
                        organisation_type: e.target.value,
                      },
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Non-commercial">Non-commercial</MenuItem>
                </Select>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Status of the organisation: commercial or non-commercial (Reference list 125).
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={formData.legal_representative.address}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, address: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Address of Legal representative of the Sponsor"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Town / City"
                value={formData.legal_representative.town_city}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, town_city: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Town/City of the legal representative for the sponsor"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Post code"
                value={formData.legal_representative.postcode}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, postcode: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Post code of the legal representative for the sponsor"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Country"
                value={formData.legal_representative.country}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, country: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Country of the legal representative for the sponsor"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={formData.legal_representative.phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, phone: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Phone number of the legal representative"
                helperText="Read only in CTIS"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email address"
                value={formData.legal_representative.email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative: { ...prev.legal_representative, email: e.target.value },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Email address of the legal representative"
                helperText="Read only in CTIS"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
            Legal Representative Contact Person
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Contact person – First name"
                value={formData.legal_representative_contact.first_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative_contact: {
                      ...prev.legal_representative_contact,
                      first_name: e.target.value,
                    },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Name of contact person at legal representative organisation"
                helperText="Mandatory, max 100 characters"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Contact person – Last name"
                value={formData.legal_representative_contact.last_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative_contact: {
                      ...prev.legal_representative_contact,
                      last_name: e.target.value,
                    },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Contact person at legal representative organisation"
                helperText="Mandatory, max 100 characters"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={formData.legal_representative_contact.email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative_contact: {
                      ...prev.legal_representative_contact,
                      email: e.target.value,
                    },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Email address for contact person at legal representative organisation"
                helperText="Conditionally required, max 100 characters"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={formData.legal_representative_contact.phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    legal_representative_contact: {
                      ...prev.legal_representative_contact,
                      phone: e.target.value,
                    },
                  }))
                }
                fullWidth
                disabled={loading || saving}
                placeholder="Full telephone number at legal representative organisation"
                helperText="Conditionally required, limitation of 15 characters"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Section: Scientific/Public contact point (CT04.10) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
        Section: Scientific / Public contact point
      </Typography>

      {/* Subsection: Scientific contact point (1 or many) */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, mb: 1 }}>
        Subsection: Scientific contact point
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.scientific_contact_points || []).map((contact, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Scientific contact point #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveScientificContact(index)}
                disabled={loading || saving || formData.scientific_contact_points.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Organisation name"
                  value={contact.organisation_name || ''}
                  onChange={handleScientificContactChange(index, 'organisation_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Scientific contact organisation name"
                  helperText="The field shows the information selected in Org. Search functionality for the Sponsor (Read-only in CTIS)."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Functional contact point name"
                  value={contact.functional_name || ''}
                  onChange={handleScientificContactChange(index, 'functional_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. Clinical trial information desk"
                  helperText="Mandatory, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={contact.phone || ''}
                  onChange={handleScientificContactChange(index, 'phone')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Full telephone number of the Scientific contact point"
                  helperText="Conditionally required, limitation of 15 characters, only numbers and '+' symbol are allowed"
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={contact.email || ''}
                  onChange={handleScientificContactChange(index, 'email')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Scientific contact point email address"
                  helperText="Conditionally required, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            onClick={handleAddScientificContact}
            disabled={loading || saving}
            startIcon={<AddIcon />}
          >
            Add Scientific contact point
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth disabled={loading || saving}>
            <InputLabel>Is scientific contact same as public contact?</InputLabel>
            <Select
              value={formData.scientific_same_as_public || ''}
              label="Is scientific contact same as public contact?"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  scientific_same_as_public: e.target.value,
                }))
              }
            >
              <MenuItem value="">
                <em>Select...</em>
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select "Yes" if the sponsor&apos;s public contact point is the same as the scientific contact point.
            </Typography>
          </FormControl>
        </Grid>
      </Grid>

      {/* Subsection: Public contact point (only if not same as scientific) */}
      {formData.scientific_same_as_public !== 'Yes' && (
        <>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, mb: 1 }}>
            Subsection: Public contact point
          </Typography>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {(formData.public_contact_points || []).map((contact, index) => (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Public contact point #{index + 1}
                  </Typography>
                  <IconButton
                    onClick={handleRemovePublicContact(index)}
                    disabled={loading || saving || formData.public_contact_points.length === 1}
                    color="error"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name of organisation"
                      value={contact.organisation_name || ''}
                      onChange={handlePublicContactChange(index, 'organisation_name')}
                      fullWidth
                      disabled={loading || saving}
                      placeholder="Public contact organisation name"
                      helperText="The field shows the information selected in Org. Search functionality for the Sponsor (Read-only in CTIS)."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Functional contact point name"
                      value={contact.functional_name || ''}
                      onChange={handlePublicContactChange(index, 'functional_name')}
                      fullWidth
                      disabled={loading || saving}
                      placeholder="e.g. Clinical trial information desk"
                      helperText="Mandatory, limitation of 100 characters"
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email address"
                      value={contact.email || ''}
                      onChange={handlePublicContactChange(index, 'email')}
                      fullWidth
                      disabled={loading || saving}
                      placeholder="Public contact point email address"
                      helperText="Conditionally required, limitation of 100 characters"
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Telephone number"
                      value={contact.phone || ''}
                      onChange={handlePublicContactChange(index, 'phone')}
                      fullWidth
                      disabled={loading || saving}
                      placeholder="Full telephone number of the Public contact point"
                      helperText="Conditionally required, limitation of 15 characters, only numbers and '+' symbol are allowed"
                      inputProps={{ maxLength: 15 }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
          <Button
            variant="outlined"
            onClick={handleAddPublicContact}
            disabled={loading || saving}
            startIcon={<AddIcon />}
            sx={{ mb: 3 }}
          >
            Add Public contact point
          </Button>
        </>
      )}

      {/* Section: Third Party Organisation(s) associated with the trial (CT04.11) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
        Section: Third Party Organisation(s) associated with the trial
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.third_party_organisations || []).map((org, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Third party organisation #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveThirdPartyOrg(index)}
                disabled={loading || saving || formData.third_party_organisations.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Organisation ID"
                  value={org.organisation_id || ''}
                  onChange={handleThirdPartyOrgChange(index, 'organisation_id')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Organisational number (read-only in CTIS)"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Name of organisation"
                  value={org.organisation_name || ''}
                  onChange={handleThirdPartyOrgChange(index, 'organisation_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Third party organisation name"
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Address: Street name"
                  value={org.address_street || ''}
                  onChange={handleThirdPartyOrgChange(index, 'address_street')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Street address"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Town / City"
                  value={org.town_city || ''}
                  onChange={handleThirdPartyOrgChange(index, 'town_city')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Town/City"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Post code"
                  value={org.postcode || ''}
                  onChange={handleThirdPartyOrgChange(index, 'postcode')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Post code"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Country"
                  value={org.country || ''}
                  onChange={handleThirdPartyOrgChange(index, 'country')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Country"
                  helperText="Read only in CTIS"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Telephone number"
                  value={org.phone || ''}
                  onChange={handleThirdPartyOrgChange(index, 'phone')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Phone number (only numbers and '+' allowed)"
                  helperText="Conditionally required, limitation of 15 characters"
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email address"
                  value={org.email || ''}
                  onChange={handleThirdPartyOrgChange(index, 'email')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Email of contact person in third party organisation"
                  helperText="Conditionally required, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Duties</InputLabel>
                  <Select
                    multiple
                    value={org.duties || []}
                    label="Duties"
                    onChange={handleThirdPartyOrgDutiesChange(index)}
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    {/* Simple free-text options; in real app, use lookup list 68 */}
                    <MenuItem value="Funding">Funding</MenuItem>
                    <MenuItem value="Data Management">Data Management</MenuItem>
                    <MenuItem value="Monitoring">Monitoring</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Duties of the third party organisation linked to the trial (Reference list 68, 1 or many, mandatory when organisation is provided).
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddThirdPartyOrg}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Third party organisation
      </Button>

      {/* Section: Sponsor's contact point for Union (CT04.12) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Sponsor&apos;s contact point for Union
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.union_contact_points || []).map((cp, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Union contact point #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveUnionContactPoint(index)}
                disabled={loading || saving || formData.union_contact_points.length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name of sponsor organisation"
                  value={cp.sponsor_organisation_name || ''}
                  onChange={handleUnionContactPointChange(index, 'sponsor_organisation_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Organisational name"
                  helperText="Mandatory"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Address 1"
                  value={cp.address1 || ''}
                  onChange={handleUnionContactPointChange(index, 'address1')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Limitation of 100 characters (Mandatory)"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Address 2"
                  value={cp.address2 || ''}
                  onChange={handleUnionContactPointChange(index, 'address2')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Optional, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Address 3"
                  value={cp.address3 || ''}
                  onChange={handleUnionContactPointChange(index, 'address3')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Optional, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Address 4"
                  value={cp.address4 || ''}
                  onChange={handleUnionContactPointChange(index, 'address4')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Optional, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Town / City"
                  value={cp.town_city || ''}
                  onChange={handleUnionContactPointChange(index, 'town_city')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Mandatory, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Post code"
                  value={cp.postcode || ''}
                  onChange={handleUnionContactPointChange(index, 'postcode')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={cp.country || ''}
                    label="Country"
                    onChange={handleUnionContactPointChange(index, 'country')}
                  >
                    {EEA_COUNTRIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Mandatory (EEA Member State)
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Functional name of contact point"
                  value={cp.functional_contact_name || ''}
                  onChange={handleUnionContactPointChange(index, 'functional_contact_name')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. Clinical trial information desk"
                  helperText="Optional, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First name"
                  value={cp.first_name || ''}
                  onChange={handleUnionContactPointChange(index, 'first_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Mandatory, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last name"
                  value={cp.last_name || ''}
                  onChange={handleUnionContactPointChange(index, 'last_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Mandatory, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={cp.phone || ''}
                  onChange={handleUnionContactPointChange(index, 'phone')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Conditionally required, limitation of 15 characters, only numbers and '+' symbol are allowed"
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={cp.email || ''}
                  onChange={handleUnionContactPointChange(index, 'email')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Conditionally required, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddUnionContactPoint}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Union contact point
      </Button>

      {/* Section: Responsibilities of the sponsors (CT04.13) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Responsibilities of the sponsors
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sponsor_responsible_for_compliance}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sponsor_responsible_for_compliance: e.target.checked,
                  }))
                }
                disabled={loading || saving}
              />
            }
            label="Sponsor responsible for compliance with the obligations of a sponsor in the authorisation procedures set out in Chapters II and III"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sponsor_responsible_contact_point}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sponsor_responsible_contact_point: e.target.checked,
                  }))
                }
                disabled={loading || saving}
              />
            }
            label="Sponsor responsible for being a contact point receiving all questions from subjects, investigators or any Member State concerned regarding the clinical trial and providing answers to them"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sponsor_responsible_article_77}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sponsor_responsible_article_77: e.target.checked,
                  }))
                }
                disabled={loading || saving}
              />
            }
            label="Sponsor responsible for implementing the measures taken in accordance with Article 77"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Section changes description"
            value={formData.sponsor_responsibilities_section_changes_description}
            onChange={handleChange('sponsor_responsibilities_section_changes_description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading || saving}
            placeholder="The description of the changes made in this section"
            helperText="Limitation of 4000 characters (Mandatory)"
            inputProps={{ maxLength: 4000 }}
          />
        </Grid>
      </Grid>

      {/* Section: Products (CT05) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 1 }}>
        Section: Products
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select and configure medicinal products (Authorised, Unauthorised Medicinal Product, Active Substance and ATC Code) for this clinical trial.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Product #{index + 1}
              </Typography>
              <IconButton
                onClick={handleRemoveProduct(index)}
                disabled={loading || saving || (formData.products || []).length === 1}
                color="error"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* PL: Select substance (mandatory) */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Select substance</InputLabel>
                  <Select
                    value={product.selected_product_id || ''}
                    label="Select substance"
                    onChange={handleProductChange(index, 'selected_product_id')}
                    required
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="Authorised">Authorised</MenuItem>
                    <MenuItem value="Unauthorised Medicinal Products">Unauthorised Medicinal Products</MenuItem>
                    <MenuItem value="Active Substance">Active Substance</MenuItem>
                    <MenuItem value="ATC Code">ATC Code</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Mandatory – options based on Reference list 142: Authorised, Unauthorised Medicinal Products, Active Substance and ATC Code.
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>

            {/* PL01-PL15: Read-only fields from selected product */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Product Information (Read-only, populated from selected product)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="EU Medicinal Product number (PL01)"
                  value={product.eu_medicinal_product_number || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'eu_medicinal_product_number')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Marketing Authorisation (PL02)"
                  value={product.marketing_authorisation || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'marketing_authorisation')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Product Authorisation (PL03)"
                  value={product.product_authorisation || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'product_authorisation')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name (PL04)"
                  value={product.name || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'name')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pharmaceutical Form (PL05)"
                  value={product.pharmaceutical_form || ''}
                  onChange={handleProductChange(index, 'pharmaceutical_form')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Strength (PL06)"
                  value={product.strength || ''}
                  onChange={handleProductChange(index, 'strength')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Active Substance Name (PL07)"
                  value={product.active_substance_name || ''}
                  onChange={handleProductChange(index, 'active_substance_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="EU Substance Number (PL08)"
                  value={product.eu_substance_number || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'eu_substance_number')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ATC Code (PL09)"
                  value={product.atc_code || ''}
                  onChange={handleProductChange(index, 'atc_code')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ATC Name (PL10)"
                  value={product.atc_name || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'atc_name')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ATC Level (PL11)"
                  value={product.atc_level || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'atc_level')}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sponsor code (PL12)"
                  value={product.sponsor_code || ''}
                  onChange={handleProductChange(index, 'sponsor_code')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Active Substance other descriptive name (PL13)"
                  value={product.active_substance_other_descriptive_name || ''}
                  onChange={handleProductChange(index, 'active_substance_other_descriptive_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Type (PL14)"
                  value={product.type || ''}
                  fullWidth
                  disabled={loading || saving}
                  onChange={handleProductChange(index, 'type')}
                  helperText="Text field (editable)"
                />
              </Grid>
              {product.medicinal_product_role?.includes('Placebo') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Name (Description of the Placebo) (PL15)"
                    value={product.placebo_name || ''}
                    onChange={handleProductChange(index, 'placebo_name')}
                    fullWidth
                    disabled={loading || saving}
                    placeholder="Description of the placebo"
                    helperText="Optional - shown when role is Placebo"
                  />
                </Grid>
              )}
            </Grid>

          </Box>
        ))}
      </Stack>
      <Button
        variant="outlined"
        onClick={handleAddProduct}
        disabled={loading || saving}
        startIcon={<AddIcon />}
        sx={{ mb: 3 }}
      >
        Add Product
      </Button>

      {/* Dialogue Boxes */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Dialogue Boxes
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.mpd_unavailable || false}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    mpd_unavailable: e.target.checked,
                  }))
                }
                disabled={loading || saving}
              />
            }
            label="MPD Unavailable (PL16)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Dialogue Box is displayed when MPD is unavailable (Mandatory)
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.remove_product || false}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    remove_product: e.target.checked,
                  }))
                }
                disabled={loading || saving}
              />
            }
            label="Remove Product (PL17)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Dialogue Box is displayed when a product previously associated with the clinical trial application is removed (Mandatory)
          </Typography>
        </Grid>
      </Grid>

      {/* Section: Product details (CT05.01) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Product details (CT05.01)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide detailed information about each medicinal product included in the clinical trial.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-details-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Product details for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name (CT05.01.01)"
                  value={product.name || ''}
                  onChange={handleProductChange(index, 'name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Product name as per selected substance (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Medicinal product other name (CT05.01.02)"
                  value={product.medicinal_product_other_name || ''}
                  onChange={handleProductChange(index, 'medicinal_product_other_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (optional)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="EU Medicinal product number/medicinal product unique ID (CT05.01.03)"
                  value={product.eu_medicinal_product_unique_id || ''}
                  onChange={handleProductChange(index, 'eu_medicinal_product_unique_id')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Unique identifier assigned to the medicinal product (EV code, editable here)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Authorisation Status (CT05.01.04)"
                  value={product.authorisation_status || ''}
                  onChange={handleProductChange(index, 'authorisation_status')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Status of authorisation/registration (e.g. Authorised/Development, editable)"
                />
              </Grid>
              {/* Pharmaceutical form and Strength for CT05.01.05 and CT05.01.06 reuse PL05/PL06 fields, already editable above */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pharmaceutical Form (CT05.01.05)"
                  value={product.pharmaceutical_form || ''}
                  onChange={handleProductChange(index, 'pharmaceutical_form')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Strength (CT05.01.06)"
                  value={product.strength || ''}
                  onChange={handleProductChange(index, 'strength')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Text field (editable)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={product.is_paediatric_formulation || false}
                      onChange={handleProductChange(index, 'is_paediatric_formulation')}
                      disabled={loading || saving}
                    />
                  }
                  label="Is this a specific paediatric formulation? (CT05.01.07)"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Mandatory
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Medicinal product role in trial (CT05.01.08)</InputLabel>
                  <Select
                    multiple
                    value={product.medicinal_product_role || []}
                    label="Medicinal product role in trial (CT05.01.08)"
                    onChange={handleProductArrayChange(index, 'medicinal_product_role')}
                    renderValue={(selected) => (selected || []).join(', ')}
                  >
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Comparator">Comparator</MenuItem>
                    <MenuItem value="Auxiliary">Auxiliary</MenuItem>
                    <MenuItem value="Placebo">Placebo</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Mandatory, 1 to many - Role of the medicinal product in the trial (Reference list 77)
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sponsor product code (CT05.01.09)"
                  value={product.sponsor_product_code || ''}
                  onChange={handleProductChange(index, 'sponsor_product_code')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Product code as assigned by the sponsor"
                  helperText="Conditionally required, limitation of 100 characters"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Medicinal product characteristics (CT05.02) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Medicinal product characteristics (CT05.02)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe the characteristics of each medicinal product as per the protocol (e.g. immediate-release,
        modified-release, strength, formulation).
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-characteristics-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Medicinal product characteristics for Product #{index + 1}
            </Typography>
            <FormControl fullWidth disabled={loading || saving}>
              <InputLabel>Medicinal product characteristics (CT05.02.01)</InputLabel>
              <Select
                multiple
                value={product.medicinal_product_characteristics || []}
                label="Medicinal product characteristics (CT05.02.01)"
                onChange={handleProductArrayChange(index, 'medicinal_product_characteristics')}
                renderValue={(selected) => (selected || []).join(', ')}
              >
                {MEDICINAL_PRODUCT_CHARACTERISTICS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select one or more characteristics (Reference list: Narcotic, Psychotropic,
                Radiopharmaceutical, Immunological, Plasma Derived, Extractive, Recombinant,
                Containing GMO(s), Herbal, Homeopathic, Other).
              </Typography>
            </FormControl>
          </Box>
        ))}
      </Stack>

      {/* Section: Dosage and administration details (CT05.03) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Dosage and administration details (CT05.03)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Capture information on route of administration and dosage for each medicinal product as defined in the protocol.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-dosage-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Dosage and administration for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                  <TextField
                    label="Route of administration (CT05.03.01)"
                    value={product.route_of_administration || ''}
                    onChange={handleProductChange(index, 'route_of_administration')}
                    fullWidth
                    disabled={loading || saving}
                    placeholder="e.g. Oral, Intravenous"
                    helperText="Route of administration (lookup list - Route of administration, mandatory)"
                  />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Maximum duration of treatment (CT05.03.02)"
                  value={product.max_treatment_duration || ''}
                  onChange={handleProductChange(index, 'max_treatment_duration')}
                  type="number"
                  fullWidth
                  disabled={loading || saving}
                  helperText="Maximum duration of treatment of a subject according to the protocol (up to 2 decimals)"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Time unit (CT05.03.03)"
                  value={product.max_treatment_duration_unit || ''}
                  onChange={handleProductChange(index, 'max_treatment_duration_unit')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. Days, Weeks, Months"
                  helperText="Time unit for maximum duration of treatment (lookup list: Time unit)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Maximum daily dose allowed (CT05.03.04)"
                  value={product.max_daily_dose_allowed || ''}
                  onChange={handleProductChange(index, 'max_daily_dose_allowed')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Specify maximum daily dose that can be administered as per the protocol (up to 100 characters, up to 2 decimals)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Dose unit for maximum daily dose (CT05.03.05)"
                  value={product.daily_dose_unit || ''}
                  onChange={handleProductChange(index, 'daily_dose_unit')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. mg/kg/day"
                  helperText="Dose unit for maximum daily dose (lookup list)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Maximum total dose allowed (CT05.03.06)"
                  value={product.max_total_dose_allowed || ''}
                  onChange={handleProductChange(index, 'max_total_dose_allowed')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Specify maximum total dose (up to 100 characters; numbers with up to 2 decimals)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Dose unit for maximum total dose (CT05.03.07)"
                  value={product.total_dose_unit || ''}
                  onChange={handleProductChange(index, 'total_dose_unit')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. mg, mg/kg"
                  helperText="Dose unit for maximum total dose (lookup list)"
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Information about the modification of the Medicinal Product (CT05.04) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Information about the modification of the Medicinal Product (CT05.04)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide information on whether the medicinal product has been modified in relation to its Marketing
        Authorisation and describe the modification where applicable.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-modification-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Modification information for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Has the medicinal product been modified? (CT05.04.01)</InputLabel>
                  <Select
                    value={
                      product.product_modified_from_marketing_authorisation === true
                        ? 'Yes'
                        : product.product_modified_from_marketing_authorisation === false
                        ? 'No'
                        : ''
                    }
                    label="Has the medicinal product been modified? (CT05.04.01)"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const next = [...prev.products];
                        next[index] = {
                          ...next[index],
                          product_modified_from_marketing_authorisation:
                            value === '' ? false : value === 'Yes',
                        };
                        // If set to No, clear description
                        if (value === 'No') {
                          next[index].product_modification_description = '';
                        }
                        return {
                          ...prev,
                          products: next,
                        };
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Indicates whether the medicinal product has been modified in relation to its Marketing
                    Authorisation (Boolean, mandatory).
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description of the modification (CT05.04.02)"
                  value={product.product_modification_description || ''}
                  onChange={handleProductChange(index, 'product_modification_description')}
                  fullWidth
                  disabled={loading || saving || !product.product_modified_from_marketing_authorisation}
                  multiline
                  rows={3}
                  placeholder="Provide a description of the modification, including the scope and rationale"
                  helperText="Conditionally required; limitation of 4000 characters"
                  inputProps={{ maxLength: 4000 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Product classification (CT05.05) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Product classification (CT05.05)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The classification of the medicinal product according to Anatomical Therapeutic Chemical (ATC) code.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-classification-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Product classification for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Anatomical Therapeutic Chemical (ATC) Code (CT05.05.01)"
                  value={product.atc_code || ''}
                  onChange={handleProductChange(index, 'atc_code')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="ATC classification code for the medicinal product (0, 1 or many)."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="ATC name (CT05.05.02)"
                  value={product.atc_name || ''}
                  onChange={handleProductChange(index, 'atc_name')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="ATC name corresponding to the code."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="ATC level (CT05.05.03)"
                  value={product.atc_level || ''}
                  onChange={handleProductChange(index, 'atc_level')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="ATC level for the classification."
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Product authorisation details (CT05.06) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Product authorisation details (CT05.06)
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-authorisation-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Product authorisation details for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Marketing authorisation holder (CT05.06.01)"
                  value={product.marketing_authorisation_holder || ''}
                  onChange={handleProductChange(index, 'marketing_authorisation_holder')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Organisation with the marketing authorisation of the product."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Marketing authorisation country (CT05.06.02)"
                  value={product.marketing_authorisation_country || ''}
                  onChange={handleProductChange(index, 'marketing_authorisation_country')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Country where the product has marketing authorisation."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Marketing authorisation number (CT05.06.03)"
                  value={product.marketing_authorisation_number || ''}
                  onChange={handleProductChange(index, 'marketing_authorisation_number')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Marketing authorisation number."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Centralised procedure/MRP/DCP/Registration procedure number (CT05.06.04)"
                  value={product.registration_procedure_number || ''}
                  onChange={handleProductChange(index, 'registration_procedure_number')}
                  fullWidth
                  disabled={loading || saving}
                  helperText="Registration procedure number (e.g. Centralised, MRP, DCP)."
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Orphan designation (CT05.07) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Orphan designation (CT05.07)
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-orphan-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Orphan designation for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loading || saving}>
                  <InputLabel>Does this product have an orphan drug designation? (CT05.07.01)</InputLabel>
                  <Select
                    value={
                      product.has_orphan_drug_designation === true
                        ? 'Yes'
                        : product.has_orphan_drug_designation === false
                        ? 'No'
                        : ''
                    }
                    label="Does this product have an orphan drug designation? (CT05.07.01)"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const next = [...prev.products];
                        next[index] = {
                          ...next[index],
                          has_orphan_drug_designation:
                            value === '' ? false : value === 'Yes',
                        };
                        if (value === 'No') {
                          next[index].orphan_drug_designation_number = '';
                        }
                        return {
                          ...prev,
                          products: next,
                        };
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Specifies whether the medicinal product has an orphan drug designation.
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Designation number for orphan drug (CT05.07.02)"
                  value={product.orphan_drug_designation_number || ''}
                  onChange={handleProductChange(index, 'orphan_drug_designation_number')}
                  fullWidth
                  disabled={loading || saving || !product.has_orphan_drug_designation}
                  helperText="Designation number for the orphan drug (limitation of 20 characters)."
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Section: Active substance (CT05.08) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Active substance (CT05.08)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide information about the active substance(s) related to each medicinal product.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-active-substance-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Active substance information for Product #{index + 1}
            </Typography>
            <Stack spacing={2}>
              {(product.active_substances && product.active_substances.length > 0
                ? product.active_substances
                : [
                    {
                      classification: '',
                      authorisation_status: '',
                      name: '',
                      synonyms: '',
                      other_descriptive_name: '',
                      eu_active_substance_code: '',
                      sponsor_substance_code: '',
                      strength: '',
                    },
                  ]
              ).map((substance, subIndex) => (
                <Box
                  key={`product-${index}-active-substance-${subIndex}`}
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">
                      Active substance #{subIndex + 1}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      disabled={loading || saving}
                      onClick={handleRemoveActiveSubstance(index, subIndex)}
                    >
                      Remove
                    </Button>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Classification (CT05.08.01)"
                        value={substance.classification || ''}
                        onChange={handleActiveSubstanceChange(
                          index,
                          subIndex,
                          'classification',
                        )}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Classification of the active substance"
                        helperText="Classification of the medicinal product according to its active substance"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Authorisation Status (CT05.08.02)"
                        value={substance.authorisation_status || ''}
                        onChange={handleActiveSubstanceChange(
                          index,
                          subIndex,
                          'authorisation_status',
                        )}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Authorisation status at active substance level"
                        helperText="Read-only in CTIS; editable here for data capture"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Name (CT05.08.03)"
                        value={substance.name || ''}
                        onChange={handleActiveSubstanceChange(index, subIndex, 'name')}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Active substance name"
                        helperText="Name of the active substance"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Name synonyms (CT05.08.04)"
                        value={substance.synonyms || ''}
                        onChange={handleActiveSubstanceChange(index, subIndex, 'synonyms')}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Synonyms of the active substance name"
                        helperText="You may enter multiple synonyms separated by commas"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Other descriptive name (CT05.08.05)"
                        value={substance.other_descriptive_name || ''}
                        onChange={handleActiveSubstanceChange(
                          index,
                          subIndex,
                          'other_descriptive_name',
                        )}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Other descriptive name of the active substance"
                        helperText="Limitation of 4000 characters"
                        inputProps={{ maxLength: 4000 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="EU active substance code (CT05.08.06)"
                        value={substance.eu_active_substance_code || ''}
                        onChange={handleActiveSubstanceChange(
                          index,
                          subIndex,
                          'eu_active_substance_code',
                        )}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="EU (EV) code for the active substance"
                        helperText="Read-only in CTIS; editable here for data capture"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Sponsor substance code (CT05.08.07)"
                        value={substance.sponsor_substance_code || ''}
                        onChange={handleActiveSubstanceChange(
                          index,
                          subIndex,
                          'sponsor_substance_code',
                        )}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Sponsor's internal code for the active substance"
                        helperText="Read-only in CTIS; editable here for data capture"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Strength (CT05.08.08)"
                        value={substance.strength || ''}
                        onChange={handleActiveSubstanceChange(index, subIndex, 'strength')}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Strength of the active substance"
                        helperText="Text field (may include units, e.g. 10 mg/mL)"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddActiveSubstance(index)}
                  disabled={loading || saving}
                >
                  Add active substance
                </Button>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* Section: Advanced Therapy Medicinal Product (CT05.09) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Advanced Therapy Medicinal Product (CT05.09)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide information on advanced therapy classification and CAT reference number, if applicable.
      </Typography>
      <Stack spacing={2} sx={{ mb: 4 }}>
        {(formData.products || []).map((product, index) => (
          <Box
            key={`product-atmp-${index}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Advanced Therapy information for Product #{index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Advanced therapy classification (CT05.09.01)"
                  value={product.advanced_therapy_classification || ''}
                  onChange={handleProductChange(index, 'advanced_therapy_classification')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="e.g. GTMP, CTMP, TEP"
                  helperText="Advanced therapy classification (Reference list; free text entry here)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="CAT reference number (CT05.09.02)"
                  value={product.cat_reference_number || ''}
                  onChange={handleProductChange(index, 'cat_reference_number')}
                  fullWidth
                  disabled={loading || saving}
                  placeholder="Provide the CAT reference number, if available"
                  helperText="Limitation of 20 characters"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Subsection: Gene therapy product description (CT05.09.03.01) */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
        Subsection: Gene therapy – Product description (CT05.09.03.01)
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => {
          const geneTherapy = product.gene_therapy || {};
          return (
            <Box
              key={`product-gene-therapy-${index}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Gene therapy details for Product #{index + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Gene of interest (CT05.09.03.01.01)"
                    value={geneTherapy.gene_of_interest || ''}
                    onChange={(e) =>
                      handleActiveSubstanceChange(index, 0, null) ||
                      setFormData((prev) => {
                        const products = [...prev.products];
                        products[index] = {
                          ...products[index],
                          gene_therapy: {
                            ...(products[index].gene_therapy || {}),
                            gene_of_interest: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    helperText="Limitation of 100 characters (optional)."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Gene Therapy type (CT05.09.03.01.02)"
                    value={geneTherapy.gene_therapy_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        products[index] = {
                          ...products[index],
                          gene_therapy: {
                            ...(products[index].gene_therapy || {}),
                            gene_therapy_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    helperText="Lookup list: Gene therapy type (mandatory when applicable)."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Type of gene transfer product (CT05.09.03.01.03)"
                    value={geneTherapy.gene_transfer_product_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        products[index] = {
                          ...products[index],
                          gene_therapy: {
                            ...(products[index].gene_therapy || {}),
                            gene_transfer_product_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    helperText="Lookup list: Gene transfer product type (optional)."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Additional description (CT05.09.03.01.04)"
                    value={geneTherapy.gene_therapy_additional_description || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        products[index] = {
                          ...products[index],
                          gene_therapy: {
                            ...(products[index].gene_therapy || {}),
                            gene_therapy_additional_description: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 4000 }}
                    helperText="Conditionally required; limitation of 4000 characters."
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {/* Subsection: Genetically modified cells (CT05.09.03.02) */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
        Subsection: Genetically modified cells (CT05.09.03.02)
      </Typography>
      <Stack spacing={2} sx={{ mb: 4 }}>
        {(formData.products || []).map((product, index) => {
          const gmc = product.genetically_modified_cells || {};
          return (
            <Box
              key={`product-gmc-${index}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Genetically modified cells for Product #{index + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={loading || saving}>
                    <InputLabel>Genetically modified cells present? (CT05.09.03.02.01)</InputLabel>
                    <Select
                      value={
                        gmc.has_genetically_modified_cells === true
                          ? 'Yes'
                          : gmc.has_genetically_modified_cells === false
                          ? 'No'
                          : ''
                      }
                      label="Genetically modified cells present? (CT05.09.03.02.01)"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[index] || {};
                          const currentGmc = current.genetically_modified_cells || {};
                          products[index] = {
                            ...current,
                            genetically_modified_cells: {
                              ...currentGmc,
                              has_genetically_modified_cells:
                                value === '' ? false : value === 'Yes',
                            },
                          };
                          return { ...prev, products };
                        });
                      }}
                    >
                      <MenuItem value="">
                        <em>Select...</em>
                      </MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Origin of the genetically modified cells (CT05.09.03.02.02)"
                    value={gmc.genetically_modified_cells_origin || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentGmc = current.genetically_modified_cells || {};
                        products[index] = {
                          ...current,
                          genetically_modified_cells: {
                            ...currentGmc,
                            genetically_modified_cells_origin: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    helperText="Lookup list: Cell origin (conditionally required if genetically modified cells are present)."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Species origin for the xenogeneic cells (CT05.09.03.02.03)"
                    value={gmc.xenogeneic_species_origin || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentGmc = current.genetically_modified_cells || {};
                        products[index] = {
                          ...current,
                          genetically_modified_cells: {
                            ...currentGmc,
                            xenogeneic_species_origin: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    helperText="Limitation of 100 characters; conditionally required when xenogeneic cells are present."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Specify type of cells (CT05.09.03.02.04)"
                    value={gmc.genetically_modified_cells_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentGmc = current.genetically_modified_cells || {};
                        products[index] = {
                          ...current,
                          genetically_modified_cells: {
                            ...currentGmc,
                            genetically_modified_cells_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    helperText="Optional; limitation of 100 characters."
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Somatic cell therapy (CT05.09.04) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Somatic cell therapy (CT05.09.04)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide information about somatic cell therapy medicinal product description, if applicable.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => {
          const sct = product.somatic_cell_therapy || {};
          return (
            <Box
              key={`product-somatic-cell-${index}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Somatic cell therapy information for Product #{index + 1}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Somatic cell therapy Medicinal Product Description (CT05.09.04.01)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Somatic cell origin (CT05.09.04.02)"
                    value={sct.somatic_cell_origin || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentSct = current.somatic_cell_therapy || {};
                        products[index] = {
                          ...current,
                          somatic_cell_therapy: {
                            ...currentSct,
                            somatic_cell_origin: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    helperText="Confirmation of the origin of the somatic cells (Reference list 20). Optional."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Species origin for the xenogeneic cell (CT05.09.04.03)"
                    value={sct.somatic_xenogeneic_species_origin || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentSct = current.somatic_cell_therapy || {};
                        products[index] = {
                          ...current,
                          somatic_cell_therapy: {
                            ...currentSct,
                            somatic_xenogeneic_species_origin: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    helperText="Description of the species of origin for the substance with a xenogeneic origin. Conditionally required; limitation of 100 characters."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Somatic cell type (CT05.09.04.04)"
                    value={sct.somatic_cell_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentSct = current.somatic_cell_therapy || {};
                        products[index] = {
                          ...current,
                          somatic_cell_therapy: {
                            ...currentSct,
                            somatic_cell_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    helperText="Somatic cell type (Reference list 21). Optional."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Specify type of differentiated cells (CT05.09.04.05)"
                    value={sct.somatic_differentiated_cell_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentSct = current.somatic_cell_therapy || {};
                        products[index] = {
                          ...current,
                          somatic_cell_therapy: {
                            ...currentSct,
                            somatic_differentiated_cell_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 4000 }}
                    helperText="Conditionally required; limitation of 4000 characters."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Specify other somatic cell type (CT05.09.04.06)"
                    value={sct.somatic_other_cell_type || ''}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const products = [...prev.products];
                        const current = products[index] || {};
                        const currentSct = current.somatic_cell_therapy || {};
                        products[index] = {
                          ...current,
                          somatic_cell_therapy: {
                            ...currentSct,
                            somatic_other_cell_type: e.target.value,
                          },
                        };
                        return { ...prev, products };
                      })
                    }
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    helperText="Conditionally required; limitation of 100 characters."
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Tissue engineered medicinal product (CT05.09.05) - 1 or many */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Tissue engineered medicinal product (CT05.09.05)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide information about tissue engineered medicinal product(s), if applicable. You can add multiple tissue engineered products.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const tepList = Array.isArray(product.tissue_engineered_products)
            ? product.tissue_engineered_products
            : product.tissue_engineered_products
              ? [product.tissue_engineered_products]
              : [
                  {
                    tissue_engineered_tissue_origin: '',
                    tissue_engineered_xenogeneic_species_origin: '',
                    tissue_engineered_cell_type: '',
                    tissue_engineered_cell_specification: '',
                  },
                ];

          return (
            <Box
              key={`product-tissue-engineered-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Tissue engineered medicinal product(s) for Product #{productIndex + 1}
              </Typography>
              <Stack spacing={2}>
                {tepList.map((tep, tepIndex) => (
                  <Box
                    key={`tep-${productIndex}-${tepIndex}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Tissue engineered product #{tepIndex + 1}
                      </Typography>
                      {tepList.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[productIndex] || {};
                              const list = Array.isArray(current.tissue_engineered_products)
                                ? [...current.tissue_engineered_products]
                                : [];
                              list.splice(tepIndex, 1);
                              products[productIndex] = {
                                ...current,
                                tissue_engineered_products: list.length > 0 ? list : [
                                  {
                                    tissue_engineered_tissue_origin: '',
                                    tissue_engineered_xenogeneic_species_origin: '',
                                    tissue_engineered_cell_type: '',
                                    tissue_engineered_cell_specification: '',
                                  },
                                ],
                              };
                              return { ...prev, products };
                            });
                          }}
                          disabled={loading || saving}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Origin of the engineered tissue (CT05.09.05.01)"
                          value={tep.tissue_engineered_tissue_origin || ''}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[productIndex] || {};
                              const list = Array.isArray(current.tissue_engineered_products)
                                ? [...current.tissue_engineered_products]
                                : [];
                              list[tepIndex] = {
                                ...list[tepIndex],
                                tissue_engineered_tissue_origin: e.target.value,
                              };
                              products[productIndex] = {
                                ...current,
                                tissue_engineered_products: list,
                              };
                              return { ...prev, products };
                            });
                          }}
                          fullWidth
                          disabled={loading || saving}
                          helperText="Confirmation of origin of the engineered tissue (Reference list 20). Optional."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Tissue Engineered xenogeneic species of origin (CT05.09.05.02)"
                          value={tep.tissue_engineered_xenogeneic_species_origin || ''}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[productIndex] || {};
                              const list = Array.isArray(current.tissue_engineered_products)
                                ? [...current.tissue_engineered_products]
                                : [];
                              list[tepIndex] = {
                                ...list[tepIndex],
                                tissue_engineered_xenogeneic_species_origin: e.target.value,
                              };
                              products[productIndex] = {
                                ...current,
                                tissue_engineered_products: list,
                              };
                              return { ...prev, products };
                            });
                          }}
                          fullWidth
                          disabled={loading || saving}
                          inputProps={{ maxLength: 100 }}
                          helperText="Description of the species of origin for the substance with a xenogeneic origin. Conditionally required; limitation of 100 characters."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Tissue engineered cell type (CT05.09.05.03)"
                          value={tep.tissue_engineered_cell_type || ''}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[productIndex] || {};
                              const list = Array.isArray(current.tissue_engineered_products)
                                ? [...current.tissue_engineered_products]
                                : [];
                              list[tepIndex] = {
                                ...list[tepIndex],
                                tissue_engineered_cell_type: e.target.value,
                              };
                              products[productIndex] = {
                                ...current,
                                tissue_engineered_products: list,
                              };
                              return { ...prev, products };
                            });
                          }}
                          fullWidth
                          disabled={loading || saving}
                          helperText="Tissue engineered cell type (Reference list 21). Optional."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Cell specification (CT05.09.05.04)"
                          value={tep.tissue_engineered_cell_specification || ''}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[productIndex] || {};
                              const list = Array.isArray(current.tissue_engineered_products)
                                ? [...current.tissue_engineered_products]
                                : [];
                              list[tepIndex] = {
                                ...list[tepIndex],
                                tissue_engineered_cell_specification: e.target.value,
                              };
                              products[productIndex] = {
                                ...current,
                                tissue_engineered_products: list,
                              };
                              return { ...prev, products };
                            });
                          }}
                          fullWidth
                          disabled={loading || saving}
                          inputProps={{ maxLength: 100 }}
                          placeholder="e.g. keratinocytes, fibroblasts, chondrocytes..."
                          helperText="Cells are for example keratinocytes, fibroblasts, chondrocytes... or if the cells are not stem cells or differentiated cells, specify their type. Conditionally required; limitation of 100 characters."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setFormData((prev) => {
                      const products = [...prev.products];
                      const current = products[productIndex] || {};
                      const list = Array.isArray(current.tissue_engineered_products)
                        ? [...current.tissue_engineered_products]
                        : [];
                      list.push({
                        tissue_engineered_tissue_origin: '',
                        tissue_engineered_xenogeneic_species_origin: '',
                        tissue_engineered_cell_type: '',
                        tissue_engineered_cell_specification: '',
                      });
                      products[productIndex] = {
                        ...current,
                        tissue_engineered_products: list,
                      };
                      return { ...prev, products };
                    });
                  }}
                  disabled={loading || saving}
                  sx={{ mt: 1 }}
                >
                  Add Tissue engineered product
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Device associated with medicinal product (CT05.10) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Device associated with medicinal product (CT05.10)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If one of the product is used in combination with a device, complete this section.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, index) => {
          const device = product.device_associated || {};
          return (
            <Box
              key={`product-device-${index}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Device associated with Product #{index + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={device.product_used_with_device || false}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                product_used_with_device: e.target.checked,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        disabled={loading || saving}
                      />
                    }
                    label="Product used in combination with a device (CT05.10.01)"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Mandatory - Reference list 86
                  </Typography>
                </Grid>
                {device.product_used_with_device && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading || saving}>
                        <InputLabel>Product ID(s) (CT05.10.02)</InputLabel>
                        <Select
                          multiple
                          value={device.product_ids || []}
                          label="Product ID(s) (CT05.10.02)"
                          onChange={(e) => {
                            setFormData((prev) => {
                              const products = [...prev.products];
                              const current = products[index] || {};
                              const currentDevice = current.device_associated || {};
                              products[index] = {
                                ...current,
                                device_associated: {
                                  ...currentDevice,
                                  product_ids: e.target.value,
                                },
                              };
                              return { ...prev, products };
                            });
                          }}
                          renderValue={(selected) => (selected || []).join(', ')}
                        >
                          {/* Product IDs would come from a lookup - for now allow text input via TextField */}
                        </Select>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Conditionally required, 1 or many
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Product ID (CT05.10.02)"
                        value={(device.product_ids || []).join(', ') || ''}
                        onChange={(e) => {
                          const ids = e.target.value.split(',').map((id) => id.trim()).filter(Boolean);
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                product_ids: ids,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="Enter product ID(s), separated by commas"
                        helperText="Conditionally required, 1 or many (limitation of 100 characters each)"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Type of device (CT05.10.03)"
                        value={device.device_type || ''}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                device_type: e.target.value,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        fullWidth
                        disabled={loading || saving}
                        placeholder="e.g. pipette, pre-filled syringe, aerosol inhaler, medical device"
                        helperText="Optional - Reference list 85"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={device.device_has_ce_mark || false}
                            onChange={(e) => {
                              setFormData((prev) => {
                                const products = [...prev.products];
                                const current = products[index] || {};
                                const currentDevice = current.device_associated || {};
                                products[index] = {
                                  ...current,
                                  device_associated: {
                                    ...currentDevice,
                                    device_has_ce_mark: e.target.checked,
                                  },
                                };
                                return { ...prev, products };
                              });
                            }}
                            disabled={loading || saving}
                          />
                        }
                        label="Device has CE mark (CT05.10.04)"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Device Trade Name (CT05.10.05)"
                        value={device.device_trade_name || ''}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                device_trade_name: e.target.value,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        fullWidth
                        disabled={loading || saving}
                        inputProps={{ maxLength: 100 }}
                        helperText="Optional; limitation of 100 characters"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Device notified body (CT05.10.06)"
                        value={device.device_notified_body || ''}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                device_notified_body: e.target.value,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        fullWidth
                        disabled={loading || saving}
                        inputProps={{ maxLength: 100 }}
                        helperText="Optional; limitation of 100 characters"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description of the device (CT05.10.07)"
                        value={device.device_description || ''}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[index] || {};
                            const currentDevice = current.device_associated || {};
                            products[index] = {
                              ...current,
                              device_associated: {
                                ...currentDevice,
                                device_description: e.target.value,
                              },
                            };
                            return { ...prev, products };
                          });
                        }}
                        fullWidth
                        disabled={loading || saving}
                        multiline
                        rows={3}
                        inputProps={{ maxLength: 2000 }}
                        helperText="Optional; limitation of 2000 characters"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Investigator Brochure for the Medicinal Product (CT05.11) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Investigator Brochure for the Medicinal Product (CT05.11)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload Investigator Brochure and Summary of Product Characteristics (SmPC) documents.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const ibDocs = product.investigator_brochure_documents || [];
          const smpcDocs = product.smpc_documents || [];
          const uploadKeyIb = `ib-${productIndex}`;
          const uploadKeySmpc = `smpc-${productIndex}`;

          return (
            <Box
              key={`product-docs-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Documents for Product #{productIndex + 1}
              </Typography>

              {/* Investigator Brochure */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Investigator Brochure (CT05.11.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyIb]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyIb]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'investigator_brochure',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            investigator_brochure_documents: [
                              ...(current.investigator_brochure_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyIb]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyIb] ? 'Uploading...' : 'Upload Investigator Brochure (PDF/A)'}
                </Button>
                {ibDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ibDocs.map((doc, idx) => (
                      <Chip
                        key={`ib-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              investigator_brochure_documents: (current.investigator_brochure_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format
                </Typography>
              </Box>

              {/* SmPC */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Summary of Product Characteristics (SmPC) (CT05.11.02)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeySmpc]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySmpc]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'smpc',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            smpc_documents: [...(current.smpc_documents || []), ...uploadedDocs],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySmpc]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeySmpc] ? 'Uploading...' : 'Upload SmPC (PDF/A)'}
                </Button>
                {smpcDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {smpcDocs.map((doc, idx) => (
                      <Chip
                        key={`smpc-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              smpc_documents: (current.smpc_documents || []).filter((_, i) => i !== idx),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Compliance with (GMP) for the Medicinal Product (CT05.12) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Compliance with (GMP) for the Medicinal Product (CT05.12)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload GMP compliance documents and provide authorisation numbers.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const mfgDocs = product.manufacturing_authorisation_documents || [];
          const qpDocs = product.qp_gmp_certification_documents || [];
          const uploadKeyMfg = `mfg-${productIndex}`;
          const uploadKeyQp = `qp-${productIndex}`;

          return (
            <Box
              key={`product-gmp-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                GMP Compliance for Product #{productIndex + 1}
              </Typography>

              {/* Manufacturing Authorisation */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Authorisation of manufacturing and import (CT05.12.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyMfg]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyMfg]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'manufacturing_authorisation',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            manufacturing_authorisation_documents: [
                              ...(current.manufacturing_authorisation_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyMfg]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyMfg] ? 'Uploading...' : 'Upload Manufacturing Authorisation (PDF/A)'}
                </Button>
                {mfgDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {mfgDocs.map((doc, idx) => (
                      <Chip
                        key={`mfg-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              manufacturing_authorisation_documents: (
                                current.manufacturing_authorisation_documents || []
                              ).filter((_, i) => i !== idx),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Optional, 1 or many - PDF/A format
                </Typography>
              </Box>

              {/* Authorisation Number */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Authorisation number of manufacturing and import (CT05.12.02)"
                    value={product.manufacturing_authorisation_number || ''}
                    onChange={handleProductChange(productIndex, 'manufacturing_authorisation_number')}
                    fullWidth
                    disabled={loading || saving}
                    inputProps={{ maxLength: 100 }}
                    placeholder="EudraGMP/MIA number"
                    helperText="Optional - The EudraGMP/MIA number associated with the medicinal product/document uploaded. Limitation of 100 characters."
                  />
                </Grid>
              </Grid>

              {/* QP GMP Certification */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  QP GMP certification (CT05.12.03)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyQp]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyQp]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'qp_gmp_certification',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            qp_gmp_certification_documents: [
                              ...(current.qp_gmp_certification_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyQp]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyQp] ? 'Uploading...' : 'Upload QP GMP Certification (PDF/A)'}
                </Button>
                {qpDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {qpDocs.map((doc, idx) => (
                      <Chip
                        key={`qp-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              qp_gmp_certification_documents: (current.qp_gmp_certification_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Optional, 1 or many - PDF/A format
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: IMPD-Q (CT05.13) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: IMPD-Q (CT05.13)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload IMPD-Q documents or provide justification for not uploading.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const impdQDocs = product.impd_q_documents || [];
          const simplifiedImpdQDocs = product.simplified_impd_q_documents || [];
          const uploadKeyImpdQ = `impd-q-${productIndex}`;
          const uploadKeySimplifiedImpdQ = `simplified-impd-q-${productIndex}`;

          return (
            <Box
              key={`product-impd-q-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                IMPD-Q for Product #{productIndex + 1}
              </Typography>

              {/* CT05.13.01: IMPD-Q Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: IMPD-Q (CT05.13.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyImpdQ]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyImpdQ]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'impd_q',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            impd_q_documents: [
                              ...(current.impd_q_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyImpdQ]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyImpdQ] ? 'Uploading...' : 'Upload IMPD-Q (PDF/A)'}
                </Button>
                {impdQDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {impdQDocs.map((doc, idx) => (
                      <Chip
                        key={`impd-q-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              impd_q_documents: (current.impd_q_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format. Version of the IMPD-Q (not to be published).
                </Typography>
              </Box>

              {/* CT05.13.02: Simplified IMPD-Q Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: Simplified IMPD-Q (CT05.13.02)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeySimplifiedImpdQ]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySimplifiedImpdQ]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'simplified_impd_q',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            simplified_impd_q_documents: [
                              ...(current.simplified_impd_q_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySimplifiedImpdQ]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeySimplifiedImpdQ] ? 'Uploading...' : 'Upload Simplified IMPD-Q (PDF/A)'}
                </Button>
                {simplifiedImpdQDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {simplifiedImpdQDocs.map((doc, idx) => (
                      <Chip
                        key={`simplified-impd-q-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              simplified_impd_q_documents: (current.simplified_impd_q_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format. Simplified version of the IMPD-Q (not to be published).
                </Typography>
              </Box>

              {/* CT05.13.03: Justification for no IMPD upload */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Justification for no IMPD upload (CT05.13.03)"
                  value={product.impd_q_justification || ''}
                  onChange={handleProductChange(productIndex, 'impd_q_justification')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 4000 }}
                  helperText="Conditionally required. Reason for not uploading IMPD documentation. Limitation of 4000 characters."
                />
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: IMPD-S and E (CT05.14) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: IMPD-S and E (CT05.14)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload IMPD-Safety and Efficacy documents or provide justification for not uploading.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const impdSEDocs = product.impd_s_e_documents || [];
          const simplifiedImpdSEDocs = product.simplified_impd_s_e_documents || [];
          const uploadKeyImpdSE = `impd-s-e-${productIndex}`;
          const uploadKeySimplifiedImpdSE = `simplified-impd-s-e-${productIndex}`;

          return (
            <Box
              key={`product-impd-s-e-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                IMPD-S and E for Product #{productIndex + 1}
              </Typography>

              {/* CT05.14.01: IMPD-Safety and Efficacy Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: IMPD-Safety and Efficacy (CT05.14.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyImpdSE]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyImpdSE]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'impd_s_e',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            impd_s_e_documents: [
                              ...(current.impd_s_e_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyImpdSE]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyImpdSE] ? 'Uploading...' : 'Upload IMPD-Safety and Efficacy (PDF/A)'}
                </Button>
                {impdSEDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {impdSEDocs.map((doc, idx) => (
                      <Chip
                        key={`impd-s-e-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              impd_s_e_documents: (current.impd_s_e_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format. Version of the IMPD-S and E to be published (containing IMPD-S and IMPD-E; excluding IMPD-Q).
                </Typography>
              </Box>

              {/* CT05.14.02: Simplified IMPD-Safety and Efficacy Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: Simplified IMPD-Safety and Efficacy (CT05.14.02)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeySimplifiedImpdSE]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySimplifiedImpdSE]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'simplified_impd_s_e',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            simplified_impd_s_e_documents: [
                              ...(current.simplified_impd_s_e_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeySimplifiedImpdSE]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeySimplifiedImpdSE] ? 'Uploading...' : 'Upload Simplified IMPD-Safety and Efficacy (PDF/A)'}
                </Button>
                {simplifiedImpdSEDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {simplifiedImpdSEDocs.map((doc, idx) => (
                      <Chip
                        key={`simplified-impd-s-e-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              simplified_impd_s_e_documents: (current.simplified_impd_s_e_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Conditionally required, 1 or many - PDF/A format. Simplified version of the IMPD-S and E to be published (containing IMPD-S and IMPD-E; excluding IMPD-Q).
                </Typography>
              </Box>

              {/* CT05.14.03: Justification for no IMPD upload */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Justification for no IMPD upload (CT05.14.03)"
                  value={product.impd_s_e_justification || ''}
                  onChange={handleProductChange(productIndex, 'impd_s_e_justification')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 4000 }}
                  helperText="Conditionally required. Reason for not uploading IMPD documentation. Limitation of 4000 characters."
                />
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Auxiliary Medicinal Product Dossier (CT05.15) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Auxiliary Medicinal Product Dossier (CT05.15)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload Auxiliary Medicinal Product Dossier documents or provide reason for not uploading.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const ampdDocs = product.ampd_full_documents || [];
          const uploadKeyAmpd = `ampd-${productIndex}`;

          return (
            <Box
              key={`product-ampd-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                AMPD for Product #{productIndex + 1}
              </Typography>

              {/* CT05.15.01: AMPD - Full Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  AMPD Document: AMPD - Full (CT05.15.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyAmpd]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyAmpd]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'ampd_full',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            ampd_full_documents: [
                              ...(current.ampd_full_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyAmpd]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyAmpd] ? 'Uploading...' : 'Upload AMPD - Full (PDF/A)'}
                </Button>
                {ampdDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {ampdDocs.map((doc, idx) => (
                      <Chip
                        key={`ampd-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              ampd_full_documents: (current.ampd_full_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Optional, 1 or many - PDF/A format. Version of the Auxiliary Medicinal Product Dossier for publication.
                </Typography>
              </Box>

              {/* CT05.15.03: Reason for no AMPD Upload */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Reason for no AMPD Upload (CT05.15.03)"
                  value={product.ampd_justification || ''}
                  onChange={handleProductChange(productIndex, 'ampd_justification')}
                  fullWidth
                  disabled={loading || saving}
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 4000 }}
                  helperText="Conditionally required. Reason for not uploading AMPD documentation. Limitation of 4000 characters."
                />
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Placebo (CT05.16) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Placebo (CT05.16)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload IMPD-Q Placebo documents.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const placeboDocs = product.impd_q_placebo_documents || [];
          const uploadKeyPlacebo = `placebo-${productIndex}`;

          return (
            <Box
              key={`product-placebo-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Placebo Documents for Product #{productIndex + 1}
              </Typography>

              {/* CT05.16.01: IMPD-Q Placebo Document */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: IMPD-Q Placebo (CT05.16.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyPlacebo]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyPlacebo]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'impd_q_placebo',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            impd_q_placebo_documents: [
                              ...(current.impd_q_placebo_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyPlacebo]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyPlacebo] ? 'Uploading...' : 'Upload IMPD-Q Placebo (PDF/A)'}
                </Button>
                {placeboDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {placeboDocs.map((doc, idx) => (
                      <Chip
                        key={`placebo-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              impd_q_placebo_documents: (current.impd_q_placebo_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Optional, 1 or many - PDF/A format.
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Content labelling (CT05.17) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: Content labelling (CT05.17)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload content labelling documents and link to related products.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => {
          const contentLabellingDocs = product.content_labelling_documents || [];
          const uploadKeyContentLabelling = `content-labelling-${productIndex}`;

          return (
            <Box
              key={`product-content-labelling-${productIndex}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Content labelling for Product #{productIndex + 1}
              </Typography>

              {/* CT05.17.01: Content labelling of the IMPs Document */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Document: Content labelling of the IMPs (CT05.17.01)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKeyContentLabelling]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.PDF"
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyContentLabelling]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: 'content_labelling',
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => {
                          const products = [...prev.products];
                          const current = products[productIndex] || {};
                          products[productIndex] = {
                            ...current,
                            content_labelling_documents: [
                              ...(current.content_labelling_documents || []),
                              ...uploadedDocs,
                            ],
                          };
                          return { ...prev, products };
                        });
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKeyContentLabelling]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKeyContentLabelling] ? 'Uploading...' : 'Upload Content labelling of the IMPs (PDF/A)'}
                </Button>
                {contentLabellingDocs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {contentLabellingDocs.map((doc, idx) => (
                      <Chip
                        key={`content-labelling-${productIndex}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => {
                            const products = [...prev.products];
                            const current = products[productIndex] || {};
                            products[productIndex] = {
                              ...current,
                              content_labelling_documents: (current.content_labelling_documents || []).filter(
                                (_, i) => i !== idx,
                              ),
                            };
                            return { ...prev, products };
                          })
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Mandatory, 1 or many - PDF/A format. Description for the content labelling of the IMPs.
                </Typography>
              </Box>

              {/* CT05.17.02: Content labelling Linked products */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Content labelling Linked products (CT05.17.02)</InputLabel>
                  <Select
                    multiple
                    value={product.content_labelling_linked_products || []}
                    onChange={(e) => {
                      setFormData((prev) => {
                        const products = [...prev.products];
                        products[productIndex] = {
                          ...products[productIndex] || {},
                          content_labelling_linked_products: e.target.value,
                        };
                        return { ...prev, products };
                      });
                    }}
                    disabled={loading || saving}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {(formData.products || []).map((p, idx) => {
                      if (idx === productIndex) return null;
                      const productLabel = p.name || p.selected_product_id || `Product #${idx + 1}`;
                      return (
                        <MenuItem key={idx} value={productLabel}>
                          {productLabel}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Optional, 1 or many. Link to test/comparator/placebo/auxiliary products.
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Section: Section changes description (CT05.18) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Field: Section changes description (CT05.18)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe the changes made in this section.
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {(formData.products || []).map((product, productIndex) => (
          <Box
            key={`product-changes-${productIndex}`}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Section changes for Product #{productIndex + 1}
            </Typography>
            <TextField
              label="Section changes description (CT05.18)"
              value={product.product_section_changes_description || ''}
              onChange={handleProductChange(productIndex, 'product_section_changes_description')}
              fullWidth
              disabled={loading || saving}
              multiline
              rows={4}
              inputProps={{ maxLength: 4000 }}
              helperText="Mandatory. The description of the changes made in this section. Limitation of 4000 characters."
            />
          </Box>
        ))}
      </Stack>

      {/* Section: All documents (CT05.19) */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
        Section: All documents (CT05.19)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Section that lists all documents uploaded in Part I. This section also gives the possibility to upload documents related to product section of Part I from here.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This section displays a summary of all documents uploaded in the Products section. Documents can also be uploaded from this section.
      </Alert>

      {/* Part 2: Trial Sites (CT06.01) - Each field is 1 or many */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
          Part 2: Trial Sites (CT06.01)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Each field below can have 1 or many values. Use Add/Remove buttons to manage multiple entries for each field.
        </Typography>

        {/* Helper function to render repeatable field */}
        {(() => {
          const renderRepeatableField = (fieldName, label, helperText, type = 'text', options = null) => {
            const values = formData.trial_sites?.[fieldName] || [''];
            return (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {label}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        trial_sites: {
                          ...prev.trial_sites,
                          [fieldName]: [...(prev.trial_sites?.[fieldName] || ['']), ''],
                        },
                      }));
                    }}
                    disabled={loading || saving}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                <Stack spacing={1}>
                  {values.map((value, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                      {options ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>{label}</InputLabel>
                          <Select
                            value={value || ''}
                            onChange={(e) => {
                              setFormData((prev) => {
                                const newValues = [...(prev.trial_sites?.[fieldName] || [])];
                                newValues[index] = e.target.value;
                                return {
                                  ...prev,
                                  trial_sites: {
                                    ...prev.trial_sites,
                                    [fieldName]: newValues,
                                  },
                                };
                              });
                            }}
                            disabled={loading || saving}
                            label={label}
                            size="small"
                          >
                            <MenuItem value="">Select...</MenuItem>
                            {options.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={value || ''}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const newValues = [...(prev.trial_sites?.[fieldName] || [])];
                              newValues[index] = e.target.value;
                              return {
                                ...prev,
                                trial_sites: {
                                  ...prev.trial_sites,
                                  [fieldName]: newValues,
                                },
                              };
                            });
                          }}
                          disabled={loading || saving}
                          type={type}
                          inputProps={type === 'number' ? {} : type === 'tel' ? { maxLength: 15 } : type === 'email' ? { maxLength: 100 } : { maxLength: 100 }}
                        />
                      )}
                      <IconButton
                        onClick={() => {
                          setFormData((prev) => {
                            const newValues = [...(prev.trial_sites?.[fieldName] || [])];
                            newValues.splice(index, 1);
                            if (newValues.length === 0) newValues.push('');
                            return {
                              ...prev,
                              trial_sites: {
                                ...prev.trial_sites,
                                [fieldName]: newValues,
                              },
                            };
                          });
                        }}
                        disabled={loading || saving || values.length === 1}
                        color="error"
                        size="small"
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
                {helperText && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {helperText}
                  </Typography>
                )}
              </Box>
            );
          };

          const renderInvestigatorField = (fieldName, label, helperText, type = 'text', options = null) => {
            const value = formData.trial_sites?.investigator?.[fieldName] || '';
            return (
              <Box sx={{ mb: 2 }}>
                {options ? (
                  <FormControl fullWidth>
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={value || ''}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          trial_sites: {
                            ...prev.trial_sites,
                            investigator: {
                              ...prev.trial_sites?.investigator,
                              [fieldName]: e.target.value,
                            },
                          },
                        }));
                      }}
                      disabled={loading || saving}
                      label={label}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={label}
                    value={value || ''}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        trial_sites: {
                          ...prev.trial_sites,
                          investigator: {
                            ...prev.trial_sites?.investigator,
                            [fieldName]: e.target.value,
                          },
                        },
                      }));
                    }}
                    disabled={loading || saving}
                    type={type}
                    inputProps={type === 'number' ? {} : type === 'tel' ? { maxLength: 15 } : type === 'email' ? { maxLength: 100 } : { maxLength: 100 }}
                    helperText={helperText}
                  />
                )}
                {options && helperText && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {helperText}
                  </Typography>
                )}
              </Box>
            );
          };

          return (
            <>
              {/* CT06.01.01 - CT06.01.07: Site Information (Read-only fields) */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Section: Trial sites (CT06.01)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('organisation_id', 'Organisation ID (CT06.01.01)', 'Numeric, read-only, mandatory, 1 or many', 'number')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('organisation_name', 'Organisation Name (CT06.01.02)', 'Read-only, mandatory, 1 or many')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('site_location', 'Site location (CT06.01.03)', 'Read-only, mandatory, 1 or many')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('site_street_address', 'Site street address (CT06.01.04)', 'Read-only, mandatory, 1 or many')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('site_city', 'Site City (CT06.01.05)', 'Read-only, mandatory, 1 or many')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('site_post_code', 'Site Post code (CT06.01.06)', 'Read-only, mandatory, 1 or many')}
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  {renderRepeatableField('site_country', 'Site Country (CT06.01.07)', 'Read-only, mandatory, 1 or many')}
                </Grid>
              </Grid>

              {/* CT06.01.08: Investigator information */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Investigator information (CT06.01.08)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('title', 'Title (CT06.01.08.01)', 'Optional - Lookup: Person Title (Reference list 109)', 'text', ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'])}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('first_name', 'First Name (CT06.01.08.02)', 'Mandatory, limitation of 100 characters')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('last_name', 'Last name (CT06.01.08.03)', 'Mandatory, limitation of 100 characters')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('department', 'Department (CT06.01.08.04)', 'Mandatory, limitation of 100 characters')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('phone', 'Phone (CT06.01.08.05)', 'Conditionally required, limitation of 15 characters', 'tel')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInvestigatorField('email', 'Email (CT06.01.08.06)', 'Conditionally required, limitation of 100 characters', 'email')}
                </Grid>
              </Grid>
            </>
          );
        })()}
      </Box>

      {/* Part 2: Documents (CT06.02) */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
          Section: Documents (CT06.02)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload required documents for Part 2. Each document type supports multiple file uploads.
        </Typography>

        {/* Helper function to render document upload section */}
        {(() => {
          const renderDocumentUpload = (fieldName, label, description, helperText, acceptTypes = '.pdf,.PDF,.doc,.docx', isMandatory = true) => {
            const docs = formData.documents?.[fieldName] || [];
            const uploadKey = `doc-${fieldName}`;

            return (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {label}
                </Typography>
                {description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {description}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={uploadingProductDocs[uploadKey] ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  disabled={loading || saving || uploadingProductDocs[uploadKey]}
                  sx={{ mb: 2 }}
                >
                  <input
                    type="file"
                    hidden
                    accept={acceptTypes}
                    multiple
                    onChange={async (event) => {
                      const files = event.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingProductDocs((prev) => ({ ...prev, [uploadKey]: true }));
                      try {
                        const uploadedDocs = [];
                        for (const file of Array.from(files)) {
                          const result = await protocolRegistrationService.uploadCtisProtocolDocument(file);
                          uploadedDocs.push({
                            document_type: fieldName,
                            title: result.originalName || file.name,
                            file_url: result.url,
                          });
                        }
                        setFormData((prev) => ({
                          ...prev,
                          documents: {
                            ...prev.documents,
                            [fieldName]: [
                              ...(prev.documents?.[fieldName] || []),
                              ...uploadedDocs,
                            ],
                          },
                        }));
                        setSuccess(true);
                        setTimeout(() => setSuccess(false), 3000);
                      } catch (err) {
                        setError(err?.message || 'Failed to upload document(s)');
                      } finally {
                        setUploadingProductDocs((prev) => ({ ...prev, [uploadKey]: false }));
                        event.target.value = '';
                      }
                    }}
                  />
                  {uploadingProductDocs[uploadKey] ? 'Uploading...' : `Upload ${label}`}
                </Button>
                {docs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {docs.map((doc, idx) => (
                      <Chip
                        key={`${fieldName}-${idx}`}
                        icon={<CheckCircleIcon color="success" fontSize="small" />}
                        label={doc.title || doc.file_url}
                        disabled={loading || saving}
                        clickable
                        onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                        onDelete={() =>
                          setFormData((prev) => ({
                            ...prev,
                            documents: {
                              ...prev.documents,
                              [fieldName]: (prev.documents?.[fieldName] || []).filter(
                                (_, i) => i !== idx,
                              ),
                            },
                          }))
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                )}
                {helperText && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {isMandatory ? 'Mandatory' : 'Optional'}, 1 or many - {helperText}
                  </Typography>
                )}
              </Box>
            );
          };

          return (
            <>
              {/* CT06.02.01: Recruitment arrangements */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Subsection: Recruitment arrangements (CT06.02.01)
              </Typography>
              {renderDocumentUpload(
                'recruitment_arrangements_documents',
                'Document: Recruitment arrangements (CT06.02.01.01)',
                null,
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                true
              )}

              {/* CT06.02.02: Subject information and Informed Consent form */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Subject information and Informed Consent form (CT06.02.02)
              </Typography>
              {renderDocumentUpload(
                'subject_information_consent_documents',
                'Document: Subject information and informed consent form (CT06.02.02.01)',
                null,
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                true
              )}

              {/* CT06.02.03: Suitability of the investigator */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Suitability of the investigator (CT06.02.03)
              </Typography>
              {renderDocumentUpload(
                'investigator_cv_documents',
                'Document: Investigator CV (CT06.02.03.01)',
                'CV of the investigator',
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                true
              )}
              {renderDocumentUpload(
                'investigator_suitability_documents',
                'Document: Suitability of the investigator (CT06.02.03.02)',
                null,
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                false
              )}

              {/* CT06.02.04: Suitability of the facilities */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Suitability of the facilities (CT06.02.04)
              </Typography>
              {renderDocumentUpload(
                'facilities_suitability_documents',
                'Document: Suitability of the facilities (CT06.02.04.01)',
                'Written statement on the suitability of the clinical trial sites adapted to the nature and use of the IMP.',
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                true
              )}

              {/* CT06.02.05: Proof of insurance cover or indemnification */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Proof of insurance cover or indemnification (CT06.02.05)
              </Typography>
              {renderDocumentUpload(
                'proof_of_insurance_documents',
                'Document: Proof of insurance (CT06.02.05.01)',
                null,
                'Doc/Docx, JPEG format',
                '.doc,.docx,.jpg,.jpeg,.JPEG',
                true
              )}

              {/* CT06.02.06: Financial and other arrangements */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Financial and other arrangements (CT06.02.06)
              </Typography>
              {renderDocumentUpload(
                'financial_arrangements_documents',
                'Document: Financial arrangements (CT06.02.06.01)',
                null,
                'Doc/Docx, JPEG, PNG format',
                '.doc,.docx,.jpg,.jpeg,.png,.JPEG,.PNG',
                true
              )}

              {/* CT06.02.07: Compliance with national requirements on Data Protection */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Compliance with national requirements on Data Protection (CT06.02.07)
              </Typography>
              {renderDocumentUpload(
                'data_protection_compliance_documents',
                'Document: Compliance with national requirements on Data Protection (CT06.02.07.01)',
                null,
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                false
              )}

              {/* CT06.02.08: Compliance with use of Biological samples */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Subsection: Compliance with use of Biological samples (CT06.02.08)
              </Typography>
              {renderDocumentUpload(
                'biological_samples_compliance_documents',
                'Document: Compliance with use of Biological samples (CT06.02.08.01)',
                null,
                'PDF/A, Doc/Docx format',
                '.pdf,.PDF,.doc,.docx',
                false
              )}

              {/* CT06.02.09: Section: All documents */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                Section: All documents (CT06.02.09)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Section that lists all documents uploaded in Part II. This section also gives the possibility to upload documents related to Part II from here.
              </Alert>
            </>
          );
        })()}
      </Box>

      {/* Snackbar for auto-population messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
  </Box>
);
};

export default EuropeanUnionRegistry;






